import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { getSalesInvoices, saveSalesInvoice, getSalesInvoiceForReturn, getSalesReturnById, getNextSalesInvoiceNumber, getInvoiceForPrint, getCustomerAdvanceBalance, getCustomerOutstandingCredit, getCustomerBranchLoyaltyBalance, getActiveLoyaltyConfig } from "../../components/Api/GmsBilling";
import { checkDayClosing } from "../../components/Api/GmsDayClosing";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getProducts, searchProducts } from "../../components/Api/GmsProduct";
import { getSpaServices, searchSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getTaxes } from "../../components/Api/GmsTax";
import { getCustomerMemberships, saveCustomerMembership } from "../../components/Api/GmsCustomerMembership";
import { getCustomerUnbilledServices } from "../../components/Api/GmsBooking";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getSources } from "../../components/Api/GmsSource";
import { getGiftCards, getGiftCardBalance, generateUniqueGiftCardCode } from "../../components/Api/GmsGiftCard";
import { Eye, SquarePen, Receipt, Package, RotateCcw, Printer, User, Search, FileText } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";
import QuickAddCustomer from "../../components/QuickAddCustomer";
import CustomerProfileModal from "../../components/CustomerProfileModal";
import CustomerAdvancedSearchModal from "../../components/CustomerAdvancedSearchModal";
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import StockSelectionModal from "../../components/StockSelectionModal";
import StockDetailsModal from "../../components/StockDetailsModal";
import { checkStockSelectionLocks, deleteStockSelectionLock, insertStockSelectionLock, getStockSelectionLocks } from "../../components/Api/GmsStockSelectionLock";
import { getStockByProduct } from "../../components/Api/GmsStock";
import html2pdf from "html2pdf.js";
import BASE_URL from "../../config";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  Invoice_Id: null,
  Invoice_No: "",
  Invoice_Date: new Date(),
  Invoice_CustomerId: null,
  Invoice_BusinessId: null,
  Invoice_BranchId: null,
  selectedProducts: [],
  selectedServices: [],
  selectedPackages: [],
  selectedPackageSeries: [],
  productQuantities: {},
  serviceQuantities: {},
  packageQuantities: {},
  packageSeriesQuantities: {},
  productPrices: {},
  servicePrices: {},
  packagePrices: {},
  packageSeriesPrices: {},
  productDiscounts: {},
  serviceDiscounts: {},
  packageDiscounts: {},
  packageSeriesDiscounts: {},
  productTaxes: {},
  serviceTaxes: {},
  packageTaxes: {},
  packageSeriesTaxes: {},
  Invoice_TotalQty: 0,
  Invoice_TotalAmount: 0,
  Invoice_DisAmount: 0,
  Invoice_TaxAmount: 0,
  Invoice_NetAmount: 0,
  Invoice_Type: "ca", // Cash by default
  PaidAmount: 0,
  AmountPaidByCustomer: null, // NEW: Amount paid by customer (can be >= net amount)
  TipAmount: 0, // NEW: Calculated tip amount (amount paid - net amount)
  CashAmount: null,
  CashlessAmount: null,
  PaymentMethod: "ca", // Cash by default
  AuthCode: "",
  CostAmount: 0,
  TotalCost: 0,
  discountPercentage: 0,
  overallDiscountPercentage: null,
  overallDiscountAmount: 0,
  GiftCardAmount: 0,
  RedeemedGiftCardCode: "",
  // Fields for entity selection
  entityItems: [], // Array to store sequential entity items
  // Modal fields
  showEntityModal: false,
  selectedModalItems: {}, // {entityId: quantity}
  modalEntityType: '',
  modalSearchTerm: '', // Search within modal
  dropdownOpen: {},
  // Stock selection modal
  showStockModal: false,
  currentStockProduct: null,
  selectedStockData: {}, // {productId: stockData} // Track dropdown states for each item
  addButtonDropdownOpen: false, // Track add button dropdown state
  // Stock details modal fields
  showStockDetailsModal: false,
  currentStockDetailsProduct: null,
  currentStockDetails: [],
  // Customer membership modal fields
  showMembershipModal: false,
  selectedMembershipInstallments: {}, // {installmentId: {amount: value, checked: boolean}}
  selectedCompletePayments: {}, // {membershipId: {amount: value, checked: boolean}} for packages without installments
  customerMemberships: [], // Store customer memberships
  installmentNames: {}, // Store installment names
  entityNames: {}, // Store entity names {entityId: entityName}
  // New package series installments (for purchase)
  newPackageSeriesInstallments: {}, // {seriesId: {installmentId: {amount: value, checked: boolean}}}
  currentPackageSeriesForInstallments: [], // Array of {seriesId, seriesName, installments}
  // Unbilled services from bookings
  unbilledServices: [], // Store unbilled services from bookings
  selectedUnbilledServices: {}, // {bookingResourceDetailId: {checked: boolean, amount: value}}
  // Return mode fields
  preloadedStockItems: null, // Pre-loaded stock items for return mode
  // Product stock status in modal
  productStockStatus: {}, // {productId: hasStock} - tracks which products have stock available
  // Document upload fields
  Invoice_Document: null, // File object for document upload
  Invoice_DocumentPath: null, // Document path string from database
  PointsRedeemed: 0,
  PointsAmount: 0,
  LoyaltyId: null,
  ExpiryDate: null,
  PointsAdded: 0,
  LoyaltyAmount: 0,
  Invoice_SourceId: null,
};

const emptyFilter = {
  Invoice_Id: null,
  Customer_Id: null,
  Employee_Id: null,
  FromDate: null,
  ToDate: null,
  Invoice_Type: null,
  PaymentMethod: null,
};

export default function BillingPage() {
  // Helper function to normalize entity type (handle case variations)
  const normalizeEntityType = (entityType) => {
    if (!entityType) return null;
    const normalized = String(entityType).toUpperCase();
    if (normalized === 'PAC') return 'PAC';
    if (normalized === 'SEP' || normalized === 'SER') return 'SEP';
    return normalized;
  };

  const currencyCode = localStorage.getItem("currencyCode") || "AED";

  // Helper function to check if entity type is Package
  const isPackageType = (entityType) => {
    return normalizeEntityType(entityType) === 'PAC';
  };

  // Helper function to check if entity type is Package Series
  const isPackageSeriesType = (entityType) => {
    return normalizeEntityType(entityType) === 'SEP';
  };

  // Helper function to format numeric values - returns empty string if 0, otherwise returns the value
  const formatNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numValue = parseFloat(value) || 0;
    if (numValue === 0) return '';
    // Round to 2 decimal places to avoid floating point precision issues
    return Math.round(numValue * 100) / 100;
  };

  // Helper function to parse DD/MM/YYYY format date string for FIFO sorting
  const parseDDMMYYYY = (dateStr) => {
    if (!dateStr) return null;
    const ddMMyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateStr.match(ddMMyyyyPattern);
    if (match) {
      const [, day, month, year] = match;
      return new Date(`${year}-${month}-${day}`);
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Add CSS for stock details modal z-index
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Force stock details modal to appear above everything */
      .stock-details-modal__wrapper {
        z-index: 2001 !important;
        position: fixed !important;
      }
      .stock-details-modal__dialog {
        z-index: 2001 !important;
      }
      .stock-details-modal__backdrop {
        z-index: 2000 !important;
      }
      /* Ensure SweetAlert stays below stock modal */
      .swal2-container {
        z-index: 1060 !important;
      }
      .swal2-backdrop-show {
        z-index: 1059 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [selectedEmployeeFilterOption, setSelectedEmployeeFilterOption] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  // Removed activeTab state - no longer using tabs since Invoice List is separate page
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);
  // Track if page is opened from booking page
  const [isFromBooking, setIsFromBooking] = useState(false);
  // Ref to persist fromBooking flag even after state resets
  const isFromBookingRef = useRef(false);
  // Loading state for booking data processing
  const [loadingBookingData, setLoadingBookingData] = useState(false);
  // Return mode states
  const [isReturnMode, setIsReturnMode] = useState(false);
  const [returnSalesInvoiceId, setReturnSalesInvoiceId] = useState(null);
  const [originalInvoiceNo, setOriginalInvoiceNo] = useState(null);
  const [originalInvoicePaymentMethod, setOriginalInvoicePaymentMethod] = useState(null);
  const [originalInvoiceType, setOriginalInvoiceType] = useState(null);
  // Invoice list type: 'normal' or 'return'
  const [invoiceListType, setInvoiceListType] = useState('normal');
  // Stock selection lock - unique form ID
  const [temporaryFormUniqueId, setTemporaryFormUniqueId] = useState(null);
  // Invoice print modal state
  const [showInvoicePrintModal, setShowInvoicePrintModal] = useState(false);
  const [invoicePrintData, setInvoicePrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  // Customer advance and outstanding credit state
  const [customerAdvanceBalance, setCustomerAdvanceBalance] = useState(null);
  const [customerOutstandingCredit, setCustomerOutstandingCredit] = useState(null);
  const [loadingCustomerInfo, setLoadingCustomerInfo] = useState(false);
  // Modal pagination state for products and services
  const [modalItems, setModalItems] = useState([]); // Array of items loaded in modal
  const [modalPage, setModalPage] = useState(1);
  const [modalHasMore, setModalHasMore] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalTotalCount, setModalTotalCount] = useState(0);
  // Loyalty states
  const [customerBranchLoyaltyBalance, setCustomerBranchLoyaltyBalance] = useState(0);
  const [activeLoyaltyConfig, setActiveLoyaltyConfig] = useState(null);
  const [showLoyaltyRedeemModal, setShowLoyaltyRedeemModal] = useState(false);
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);

  // Track previous Invoice_NetAmount to detect changes (e.g., when overall discount is applied)
  const prevInvoiceNetAmountRef = useRef(null);
  // Track if we've processed customer profile navigation to avoid duplicate processing
  const processedCustomerProfileRef = useRef(false);
  // Track if we've processed booking navigation to avoid duplicate processing
  const processedBookingRef = useRef(false);
  // Track which bookingId we've processed to detect new bookings
  const processedBookingIdRef = useRef(null);
  // Ref to store current entityItems for access in async functions (like openMembershipModal)
  const entityItemsRef = useRef([]);

  // Auto-fill CashAmount with NetAmount and update PointsAdded
  useEffect(() => {
    setForm(prev => {
      const netAmount = parseFloat(prev.Invoice_NetAmount) || 0;
      const tipAmount = parseFloat(prev.TipAmount) || 0;
      const roundedNet = Math.round(netAmount * 100) / 100;
      const totalToPay = Math.round((roundedNet + tipAmount) * 100) / 100;

      const newForm = {
        ...prev,
        // Return invoices never add points - only normal sales earn points
        PointsAdded: isReturnMode ? 0 : roundedNet
      };

      // If CashlessAmount and LoyaltyAmount are not set (Full Cash scenario),
      // we keep the CashAmount in sync with the NetAmount + Tip.
      // This ensures that when adding/modifying items, the payment fields follow the total.

      if (isReturnMode) {
        const isCashlessMethod = prev.PaymentMethod !== 'ca' && prev.PaymentMethod !== null;

        if (isCashlessMethod) {
          // Cashless Return: If CashAmount is empty/zero, fill CashlessAmount
          const cashEmpty = prev.CashAmount === null || prev.CashAmount === 0;
          if (cashEmpty) {
            newForm.CashlessAmount = totalToPay;
            newForm.CashAmount = 0;
          }
        } else {
          // Cash Return: If CashlessAmount is empty/zero, fill CashAmount
          const cashlessEmpty = prev.CashlessAmount === null || prev.CashlessAmount === 0;
          if (cashlessEmpty) {
            newForm.CashAmount = totalToPay;
            newForm.CashlessAmount = 0;
          }
        }
      } else {
        // Normal Mode
        const loyalty = prev.LoyaltyAmount || 0;
        const giftCard = prev.GiftCardAmount || 0;
        const balanceToPay = Math.max(0, Math.round((totalToPay - loyalty - giftCard) * 100) / 100);

        const isCashMethod = prev.PaymentMethod === 'ca' || !prev.PaymentMethod;
        const isCashlessMethod = prev.PaymentMethod === 'cc' || prev.PaymentMethod === 'ba' || prev.PaymentMethod === 'on';

        // Auto-fill logic: only if the "other" field is empty/zero OR it was previously in sync, keep primary in sync with remainder
        if (isCashMethod && (prev.CashlessAmount === null || prev.CashlessAmount === 0)) {
          newForm.CashAmount = balanceToPay;
          newForm.AmountPaidByCustomer = totalToPay;
        } else if (isCashlessMethod && (prev.CashAmount === null || prev.CashAmount === 0)) {
          newForm.CashlessAmount = balanceToPay;
          newForm.AmountPaidByCustomer = totalToPay;
        } else if (prev.PaymentMethod === 'ta') {
          // Tabby always covers the full remaining balance
          newForm.CashlessAmount = balanceToPay;
          newForm.CashAmount = 0;
          newForm.AmountPaidByCustomer = totalToPay;
        }
      }

      return newForm;
    });
  }, [form.Invoice_NetAmount, form.TipAmount, isReturnMode, form.PaymentMethod]);

  // Fetch Loyalty Config and Branch Balance
  useEffect(() => {
    const fetchLoyaltyData = async () => {
      if (form.Invoice_BusinessId && form.Invoice_BranchId && form.Invoice_Date) {
        try {
          console.log("🔍 Fetching Loyalty Data for:", {
            businessId: form.Invoice_BusinessId,
            branchId: form.Invoice_BranchId,
            customerId: form.Invoice_CustomerId,
            date: formatDateForAPI(form.Invoice_Date)
          });

          const config = await getActiveLoyaltyConfig(
            form.Invoice_BusinessId,
            form.Invoice_BranchId,
            formatDateForAPI(form.Invoice_Date)
          );
          console.log("✅ Active Loyalty Config:", config);
          setActiveLoyaltyConfig(config);

          if (form.Invoice_CustomerId) {
            const balance = await getCustomerBranchLoyaltyBalance(
              form.Invoice_CustomerId,
              form.Invoice_BranchId,
              form.Invoice_BusinessId
            );
            console.log("💰 Customer Loyalty Balance:", balance);
            setCustomerBranchLoyaltyBalance(balance);
          }
        } catch (err) {
          console.error("❌ Error fetching loyalty data:", err);
        }
      }
    };
    fetchLoyaltyData();
  }, [form.Invoice_BusinessId, form.Invoice_BranchId, form.Invoice_CustomerId, form.Invoice_Date]);

  // Sync CashlessAmount with Invoice_NetAmount when Tabee is selected in return mode
  useEffect(() => {
    if (isReturnMode && form.PaymentMethod === "ta" && form.Invoice_NetAmount) {
      const roundedNetAmount = Math.round((form.Invoice_NetAmount || 0) * 100) / 100;
      if (form.CashlessAmount !== roundedNetAmount) {
        setForm(prev => ({
          ...prev,
          CashlessAmount: roundedNetAmount > 0 ? roundedNetAmount : null
        }));
      }
    }
  }, [form.Invoice_NetAmount, form.PaymentMethod, isReturnMode, form.CashlessAmount]);

  // Filter options
  const invoiceTypeOptions = [
    { value: "ca", label: "Cash" },
    { value: "cr", label: "Credit" }
  ];

  const paymentMethodOptions = [
    { value: "ca", label: "Cash" },
    { value: "cc", label: "Credit Card" },
    { value: "on", label: "Online" },
    { value: "ta", label: "Tabee" },
    { value: "ba", label: "Bank" }
  ];

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsBilling');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsBilling');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsBilling');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsBilling');
  const isAdmin = isUserAdmin();

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
      companyId: authUser.companyId || user.user_Company_Id || null,
      branchId: authUser.branchId || user.user_Branch_Id || null,
    };
  };

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  // Generate unique form ID for stock selection lock
  const generateTemporaryFormUniqueId = () => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, ''); // HHMMSS
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const prefix = isReturnMode ? 'SR' : 'SI'; // SR for Sales Return, SI for Sales Invoice
    const formId = `${prefix}-${dateStr}-${timeStr}-${sequence}`;

    // Store form ID in sessionStorage so we can clean it up on refresh
    const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
    storedFormIds.push(formId);
    sessionStorage.setItem('stockLockFormIds', JSON.stringify(storedFormIds));

    return formId;
  };

  useEffect(() => {
    if (hasPermission) {
      // Reset customer profile processing ref on mount
      processedCustomerProfileRef.current = false;

      // Clear form and entity items on page load/refresh
      // IMPORTANT: This runs on every mount (including refresh)

      // Cleanup stock locks on page load/refresh
      // Since the form is cleared, we should also clear any locks from previous session
      const cleanupLocksOnRefresh = async () => {
        try {
          const { cleanupStockSelectionLocks, deleteStockSelectionLock } = await import('../components/Api/GmsStockSelectionLock');

          // 1. Clean up any form IDs stored in sessionStorage (from previous session before refresh)
          const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
          for (const formId of storedFormIds) {
            try {
              await deleteStockSelectionLock(formId);
            } catch (err) {
              // Silently handle cleanup errors
            }
          }
          // Clear the stored form IDs
          sessionStorage.removeItem('stockLockFormIds');

          // 2. Also clean up locks older than 5 minutes (very short time to catch any missed ones)
          // This ensures any locks from the previous session are cleared
          await cleanupStockSelectionLocks(0.083).catch(() => { }); // 0.083 hours = 5 minutes
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
      cleanupLocksOnRefresh();

      setForm({
        ...defaultForm,
        Invoice_Date: new Date(),
        entityItems: [] // Explicitly clear entity items
      });
      setIsEditing(false);
      setIsReturnMode(false);
      setReturnSalesInvoiceId(null);
      setOriginalInvoiceNo(null);
      setTemporaryFormUniqueId(null);

      loadInitialData();
      // Removed loadInvoices() - invoice list is on separate page (/GmsBillingList)
      // Don't call loadNextInvoiceNumber here - it will be called when BusinessId is available via useEffect

      // Check day closing for current date (initial validation)
      // This will be triggered again when date is selected via useEffect
      if (form.Invoice_Date) {
        checkSelectedDateDayClosing(form.Invoice_Date);
      }

      // View-only users can still access the form (read-only mode)
    } else {
      window.location.href = '/Dashboard';
    }
  }, []); // Empty dependency array - run only on mount/refresh

  // Check if current date is closed/holiday and previous day closing
  // Check day closing for selected date (triggered on date selection)
  const checkSelectedDateDayClosing = async (selectedDate) => {
    try {
      if (!selectedDate) return;

      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userBusinessId = authUser.businessId || user.user_Business_Id || null;
      const userBranchId = authUser.branchId || user.user_Branch_Id || null;

      // Only check if we have business info (from session or form)
      const businessId = userBusinessId || form.Invoice_BusinessId;
      const branchId = userBranchId || form.Invoice_BranchId || null;

      if (!businessId) {
        // Business not selected yet, skip validation
        return;
      }

      // Format selected date
      const selectedDateOnly = new Date(selectedDate);
      selectedDateOnly.setHours(0, 0, 0, 0);

      // Check selected date with previous days check enabled (stricter validation)
      const checkRequest = {
        transactionDate: formatDateForAPI(selectedDateOnly, false),
        businessId: businessId,
        branchId: branchId,
        checkPreviousDays: true  // Enable stricter validation
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);

      // Check if selected date itself is closed
      if (dayClosingCheck.isClosed) {
        const message = dayClosingCheck.isHoliday
          ? `Holiday has been marked for ${formatDateForDisplay(selectedDateOnly, 'date')}. Please select a different date.`
          : `Day closing has been marked for ${formatDateForDisplay(selectedDateOnly, 'date')}. Please select a different date.`;

        Swal.fire({
          icon: 'warning',
          title: 'Day Closed',
          text: message,
          confirmButtonText: 'OK'
        });

        // Disable the form
        setIsReadOnly(true);
        return;
      }

      // Check if any previous days are not closed (stricter validation)
      if (dayClosingCheck.firstUnclosedDate) {
        const unclosedDateStr = formatDateForDisplay(new Date(dayClosingCheck.firstUnclosedDate), 'date');
        const selectedDateStr = formatDateForDisplay(selectedDateOnly, 'date');

        Swal.fire({
          icon: 'warning',
          title: 'Previous Day Not Closed',
          text: `Day closing is not done for ${unclosedDateStr}. Please close that day first before creating transactions for ${selectedDateStr}.`,
          confirmButtonText: 'OK'
        });

        // Disable the form
        setIsReadOnly(true);
        return;
      }

      // All checks passed - enable the form
      setIsReadOnly(false);

    } catch (error) {
      console.error("Error checking day closing for selected date:", error);
      // Don't block on error - allow user to continue
    }
  };

  // Auto-populate company if user has companyId
  useEffect(() => {
    const userCompanyId = session.companyId || localStorage.getItem("userCompanyId");
    if (userCompanyId && businesses.length > 0) {
      const companyId = typeof userCompanyId === 'string' ? parseInt(userCompanyId) : userCompanyId;
      const companyExists = businesses.find(b => b.business_Id === companyId);
      if (companyExists) {
        setForm(prev => ({
          ...prev,
          Invoice_BusinessId: companyId
        }));
      }
    }
  }, [businesses, session.companyId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    const userBranchId = session.branchId || localStorage.getItem("userBranchId");
    if (userBranchId && branches.length > 0 && form.Invoice_BusinessId) {
      const branchId = typeof userBranchId === 'string' ? parseInt(userBranchId) : userBranchId;
      const branchExists = branches.find(b => b.branch_Id === branchId && b.branch_Buisness_Id === form.Invoice_BusinessId);
      if (branchExists) {
        setForm(prev => ({
          ...prev,
          Invoice_BranchId: branchId
        }));
      }
    }
  }, [branches, form.Invoice_BusinessId, session.branchId]);

  // Check day closing when date is selected (NEW: validation triggers on date selection)
  useEffect(() => {
    if (form.Invoice_Date && form.Invoice_BusinessId) {
      // Small delay to ensure state is updated
      const timer = setTimeout(() => {
        checkSelectedDateDayClosing(form.Invoice_Date);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [form.Invoice_Date, form.Invoice_BusinessId, form.Invoice_BranchId]);

  // Load customer advance balance and outstanding credit when customer is selected
  useEffect(() => {
    const loadCustomerInfo = async () => {
      if (form.Invoice_CustomerId) {
        try {
          setLoadingCustomerInfo(true);
          const [advanceBalance, outstandingCredit] = await Promise.all([
            getCustomerAdvanceBalance(form.Invoice_CustomerId),
            getCustomerOutstandingCredit(form.Invoice_CustomerId)
          ]);

          // Only set if values exist and are greater than 0
          const advanceValue = advanceBalance?.availableAdvance || advanceBalance?.AvailableAdvance || 0;
          const outstandingValue = outstandingCredit?.totalOutstanding || outstandingCredit?.TotalOutstanding || 0;

          setCustomerAdvanceBalance(advanceValue > 0 ? advanceBalance : null);
          setCustomerOutstandingCredit(outstandingValue > 0 ? outstandingCredit : null);
        } catch (error) {
          console.error("Error loading customer info:", error);
          setCustomerAdvanceBalance(null);
          setCustomerOutstandingCredit(null);
        } finally {
          setLoadingCustomerInfo(false);
        }
      } else {
        // Reset when customer is cleared
        setCustomerAdvanceBalance(null);
        setCustomerOutstandingCredit(null);
      }
    };

    loadCustomerInfo();
  }, [form.Invoice_CustomerId]);

  // Process sales return from navigation (data already fetched in InvoiceListPage)
  const processSalesReturnFromNavigation = async (invoiceDataToUse) => {
    try {
      setLoading(true);

      if (!invoiceDataToUse || (!invoiceDataToUse.invoice_Id && !invoiceDataToUse.Invoice_Id)) {
        Swal.fire("Error", "Invalid invoice data for return", "error");
        setLoading(false);
        return;
      }

      // Load details for return:
      // - Products require available stock details
      // - Services/packages/package-series do not require stock details
      const details = invoiceDataToUse.details || invoiceDataToUse.Details || [];

      // Ensure products and services are loaded for name resolution if needed
      let currentProducts = products;
      let currentServices = services;
      const needsProducts = details.some(d => (d.entityId || d.EntityId) && (d.entityType || d.EntityType) === 'pro');
      const needsServices = details.some(d => (d.entityId || d.EntityId) && (d.entityType || d.EntityType)?.toLowerCase() === 'ser');

      if ((needsProducts && currentProducts.length === 0) || (needsServices && currentServices.length === 0)) {
        console.log('🔄 Loading missing dependencies for return name resolution...', { needsProducts, needsServices });
        const [pData, sData] = await Promise.all([
          (needsProducts && currentProducts.length === 0) ? getProducts({ status: true }) : Promise.resolve(currentProducts),
          (needsServices && currentServices.length === 0) ? getSpaServices({ status: true }) : Promise.resolve(currentServices)
        ]);

        if (needsProducts && currentProducts.length === 0) {
          currentProducts = pData || [];
          setProducts(currentProducts);
        }
        if (needsServices && currentServices.length === 0) {
          currentServices = (sData || []).filter(service => service.status === true);
          setServices(currentServices);
        }
      }

      const entityItems = [];

      if (details && Array.isArray(details) && details.length > 0) {
        details.forEach((detail, index) => {
          const entityType = detail.entityType || detail.EntityType;
          const stockDetails = detail.stockDetails || detail.StockDetails || [];

          // Products: require stock details
          if (entityType === 'pro' && stockDetails && stockDetails.length > 0) {
            const mappedStockDetails = stockDetails.map(stock => ({
              purchaseStock_Id: stock.stockTransaction_StockId || stock.purchaseStock_Id,
              purchaseStock_Product_Id: detail.entityId || detail.EntityId,
              purchaseStock_Unit_Id: stock.purchaseStock_Unit_Id || null,
              purchaseStock_BatchNo: stock.purchaseStock_BatchNo || stock.BatchNo,
              purchaseStock_ExpDate: stock.purchaseStock_ExpDate || stock.ExpDate,
              purchaseStock_IsAvailable: stock.purchaseStock_IsAvailable !== undefined ? stock.purchaseStock_IsAvailable : false,
              purchaseStock_SalesPrice: stock.purchaseStock_SalesPrice || stock.SalesPrice || detail.price || detail.Price,
              purchaseStock_CostPrice: stock.purchaseStock_CostPrice || stock.CostPrice || detail.cost || detail.Cost,
              purchaseStock_IsBonus: stock.purchaseStock_IsBonus !== undefined ? stock.purchaseStock_IsBonus : false,
              purchaseStock_Branch_Id: stock.purchaseStock_Branch_Id || invoiceDataToUse.invoice_BranchId || invoiceDataToUse.Invoice_BranchId
            }));

            const productId = detail.entityId || detail.EntityId;
            const productIdNum = parseInt(productId, 10);
            const product = currentProducts.find(p => {
              const pId = parseInt(p.product_Id || p.productId || p.id || 0, 10);
              return pId === productIdNum && pId > 0;
            });
            const productName =
              product
                ? (product.product_NameEn || product.productName_En || product.name || `Product ${productId}`)
                : `Product ${productId}`;

            // Extract salesmanIds from API response
            const extractedSalesmanIds = detail.salesmanIds && Array.isArray(detail.salesmanIds)
              ? detail.salesmanIds.map(id => parseInt(id)).filter(id => id > 0)
              : [];

            const entityItem = {
              id: index + 1,
              entityId: productId,
              entityType: 'pro',
              type: 'product',
              entityName: productName,
              originalQuantity: detail.quantity || detail.Quantity || 0, // Original billed qty
              quantity: 0, // Return qty (set via stock selection)
              unitPrice: detail.price || detail.Price || 0,
              price: detail.price || detail.Price || 0,
              discount: detail.disPer || detail.DisPer || 0,
              discountAmount: 0,
              tax: parseFloat((detail.taxPercent || detail.TaxPercent || "0")?.toString().split(',')[0]) || 0,
              taxAmount: 0,
              netAmount: 0,
              cost: detail.cost || detail.Cost || 0,
              salesmanId: detail.salesman_ID || detail.Salesman_ID || 0,
              salesmanIds: extractedSalesmanIds, // Extract salesmanIds array from API response
              stockDetails: mappedStockDetails,
              stockData: null
            };
            entityItems.push(entityItem);
          }

          // Services: returnable without stock details
          if (entityType && (entityType.toLowerCase() === 'ser' || entityType.toUpperCase() === 'SER')) {
            // Look up service name from services array
            const serviceId = detail.entityId || detail.EntityId;
            const service = currentServices.find(s => {
              const sId = parseInt(s.spaService_Id || s.spaServiceId || s.id || 0, 10);
              const entityIdNum = parseInt(serviceId, 10);
              return sId === entityIdNum && sId > 0;
            });
            const serviceName = service
              ? (service.spaServiceName_En || service.spaServiceNameEn || service.name || `Service ${serviceId}`)
              : (detail.service_NameEn || detail.Service_NameEn || detail.spaServiceName_En || detail.SpaServiceName_En || `Service ${serviceId}`);

            // Extract salesmanIds from API response
            const extractedSalesmanIds = detail.salesmanIds && Array.isArray(detail.salesmanIds)
              ? detail.salesmanIds.map(id => parseInt(id)).filter(id => id > 0)
              : [];

            const entityItem = {
              id: `ser_${index + 1}`,
              entityId: serviceId,
              entityType: 'ser',
              type: 'service',
              entityName: serviceName,
              originalQuantity: detail.quantity || detail.Quantity || 0, // Original billed qty
              quantity: 0, // Return qty (user-entered)
              unitPrice: detail.price || detail.Price || 0,
              price: detail.price || detail.Price || 0,
              discount: detail.disPer || detail.DisPer || 0,
              discountAmount: 0,
              tax: parseFloat((detail.taxPercent || detail.TaxPercent || "0")?.toString().split(',')[0]) || 0,
              taxAmount: 0,
              netAmount: 0,
              cost: detail.cost || detail.Cost || 0,
              salesmanId: detail.salesman_ID || detail.Salesman_ID || 0,
              salesmanIds: extractedSalesmanIds, // Extract salesmanIds array from API response
              stockDetails: [], // Not applicable for services
              stockData: null // Not applicable for services
            };
            entityItems.push(entityItem);
          }
        });
      }

      // Check if there are any returnable items before proceeding
      if (entityItems.length === 0) {
        Swal.fire("Info", "No returnable items found in this invoice.", "info");
        setLoading(false);
        return;
      }

      setIsReturnMode(true);
      setReturnSalesInvoiceId(invoiceDataToUse.invoice_Id || invoiceDataToUse.Invoice_Id);
      setOriginalInvoiceNo(invoiceDataToUse.invoice_No || invoiceDataToUse.Invoice_No);
      setOriginalInvoicePaymentMethod(invoiceDataToUse.paymentMethod || invoiceDataToUse.PaymentMethod || "ca");
      setOriginalInvoiceType(invoiceDataToUse.invoice_Type || invoiceDataToUse.Invoice_Type || "ca");
      setIsEditing(false);
      setIsReadOnly(false);

      const customerId = invoiceDataToUse.invoice_CustomerId || invoiceDataToUse.Invoice_CustomerId;
      const customer = customers.find(c => c.customer_Id === customerId);
      if (customer) {
        setSelectedCustomerOption({
          value: customer.customer_Id,
          label: customer.customer_NameEn
        });
      }

      const originalPaymentMethod = invoiceDataToUse.paymentMethod || invoiceDataToUse.PaymentMethod || "ca";
      const originalInvoiceType = invoiceDataToUse.invoice_Type || invoiceDataToUse.Invoice_Type || "ca";

      setForm(prev => ({
        ...prev,
        Invoice_Id: null,
        Invoice_No: "",
        Invoice_Date: new Date(),
        Invoice_CustomerId: customerId,
        Invoice_BusinessId: invoiceDataToUse.invoice_BusinessId || invoiceDataToUse.Invoice_BusinessId,
        Invoice_BranchId: invoiceDataToUse.invoice_BranchId || invoiceDataToUse.Invoice_BranchId,
        Invoice_Type: originalInvoiceType,
        PaymentMethod: originalPaymentMethod,
        Invoice_SourceId: invoiceDataToUse.invoice_SourceId || invoiceDataToUse.Invoice_SourceId || null,
        CashAmount: null,
        CashlessAmount: null,
        entityItems: entityItems,
        selectedStockData: {},
        preloadedStockItems: null
      }));

      // Generate return invoice number (sequential: 1, 2, 3... per business)
      // No longer uses 'R' suffix - uses sequential numbering per business
      if (invoiceDataToUse.invoice_BusinessId || invoiceDataToUse.Invoice_BusinessId) {
        const businessId = invoiceDataToUse.invoice_BusinessId || invoiceDataToUse.Invoice_BusinessId;
        try {
          const result = await getNextSalesInvoiceNumber(
            businessId,
            true, // isReturn = true
            null  // Not used for sequential returns - procedure generates sequential numbers per business
          );
          if (result && result.nextInvoiceNumber) {
            setForm(prev => ({
              ...prev,
              Invoice_No: String(result.nextInvoiceNumber) // Ensure it's a string
            }));
          } else {
            // Fallback: clear invoice number if generation fails
            setForm(prev => ({
              ...prev,
              Invoice_No: "" // Will be auto-generated by procedure if not provided
            }));
          }
        } catch (error) {
          console.error("Error generating return invoice number:", error);
          // Fallback: clear invoice number if generation fails
          setForm(prev => ({
            ...prev,
            Invoice_No: "" // Will be auto-generated by procedure if not provided
          }));
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error processing sales return from navigation:", error);
      Swal.fire("Error", `Failed to process sales return: ${error.message || "Unknown error"}`, "error");
      setLoading(false);
    }
  };

  // Handle navigation state for sales return from InvoiceListPage
  useEffect(() => {
    if (location.state?.salesReturnInvoice && location.state?.salesReturnInvoiceData) {
      const invoiceDataToUse = location.state.salesReturnInvoiceData;

      // Process sales return using the already-fetched data
      processSalesReturnFromNavigation(invoiceDataToUse);

      // Clear the navigation state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, customers, services]);

  // Handle navigation state for read-only invoice view
  useEffect(() => {
    if (location.state?.viewInvoice && location.state?.isReadOnly) {
      const invoice = location.state.viewInvoice;
      setIsReadOnly(true);

      // Map invoice details to entity items
      const entityItems = [];
      const productQuantities = {};
      const serviceQuantities = {};
      const packageQuantities = {};
      const packageSeriesQuantities = {};
      const productPrices = {};
      const servicePrices = {};
      const packagePrices = {};
      const packageSeriesPrices = {};
      const productDiscounts = {};
      const serviceDiscounts = {};
      const packageDiscounts = {};
      const packageSeriesDiscounts = {};
      const productTaxes = {};
      const serviceTaxes = {};
      const packageTaxes = {};
      const packageSeriesTaxes = {};

      if (invoice.details && Array.isArray(invoice.details) && invoice.details.length > 0) {
        invoice.details.forEach((detail, index) => {
          // Add safety checks for detail object
          if (!detail || typeof detail !== 'object') return;

          const entityType = detail.entityType || '';
          const entityId = detail.entityId || 0;

          // Find entity name from loaded data
          let entityName = 'Unknown Item';
          if (entityType === 'pro' && products.length > 0) {
            const product = products.find(p => p.product_Id === entityId);
            entityName = product?.product_NameEn || 'Unknown Product';
          } else if (entityType === 'ser' && services.length > 0) {
            const service = services.find(s => s.spaService_Id === entityId);
            entityName = service?.spaServiceName_En || 'Unknown Service';
          } else if ((entityType === 'pkg' || entityType === 'pac') && packages.length > 0) {
            const pkg = packages.find(p => p.packageId === entityId);
            entityName = pkg?.packageName_En || 'Unknown Package';
          } else if (entityType === 'sep' && packageSeries.length > 0) {
            const series = packageSeries.find(s => s.seriesId === entityId);
            entityName = series?.seriesName_En || 'Unknown Package Series';
          }

          // Calculate total tax percentage for multiple taxes
          let totalTaxPercent = 0;
          if (detail.taxPercent) {
            const taxPercentages = detail.taxPercent.split(',').map(tax => parseFloat(tax) || 0);
            totalTaxPercent = taxPercentages.reduce((sum, tax) => sum + tax, 0);
          }

          // Create entity item object with safety checks
          const entityItem = {
            id: index + 1,
            entityId: entityId,
            entityType: entityType,
            type: entityType === 'pro' ? 'product' : entityType === 'ser' ? 'service' : entityType === 'sep' ? 'packageSeries' : 'package',
            entityName: entityName,
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            price: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: totalTaxPercent,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            cost: detail.cost || 0,
            m_InstallmentId: detail.m_InstallmentId || null,
            salesmanId: detail.salesman_ID || detail.Salesman_ID || 0
          };

          entityItems.push(entityItem);

          // Set quantities, prices, discounts, and taxes with safety checks
          if (entityType === 'pro' && entityId) {
            productQuantities[entityId] = detail.quantity || 0;
            productPrices[entityId] = detail.price || 0;
            productDiscounts[entityId] = detail.disAmount || 0;
            productTaxes[entityId] = detail.taxAmt || 0;
          } else if (entityType === 'ser' && entityId) {
            serviceQuantities[entityId] = detail.quantity || 0;
            servicePrices[entityId] = detail.price || 0;
            serviceDiscounts[entityId] = detail.disAmount || 0;
            serviceTaxes[entityId] = detail.taxAmt || 0;
          } else if ((entityType === 'pkg' || entityType === 'pac') && entityId) {
            packageQuantities[entityId] = detail.quantity || 0;
            packagePrices[entityId] = detail.price || 0;
            packageDiscounts[entityId] = detail.disAmount || 0;
            packageTaxes[entityId] = detail.taxAmt || 0;
          } else if (entityType === 'sep' && entityId) {
            packageSeriesQuantities[entityId] = detail.quantity || 0;
            packageSeriesPrices[entityId] = detail.price || 0;
            packageSeriesDiscounts[entityId] = detail.disAmount || 0;
            packageSeriesTaxes[entityId] = detail.taxAmt || 0;
          }
        });
      }

      // Populate form with invoice data
      const populatedForm = {
        ...defaultForm,
        Invoice_Id: invoice.invoice_Id,
        Invoice_No: invoice.invoice_No ? String(invoice.invoice_No) : "",
        Invoice_Date: new Date(invoice.invoice_Date),
        Invoice_CustomerId: invoice.invoice_CustomerId,
        Invoice_BusinessId: invoice.invoice_BusinessId,
        Invoice_BranchId: invoice.invoice_BranchId,
        Invoice_TotalQty: invoice.invoice_TotalQty,
        Invoice_TotalAmount: invoice.invoice_TotalAmount,
        Invoice_DisAmount: invoice.invoice_DisAmount,
        Invoice_TaxAmount: invoice.invoice_TaxAmount,
        Invoice_NetAmount: invoice.invoice_NetAmount,
        Invoice_Type: invoice.invoice_Type,
        PaidAmount: invoice.paidAmount,
        AmountPaidByCustomer: invoice.paidAmount ?? invoice.invoice_NetAmount,
        TipAmount: invoice.tipAmount ?? 0,
        CashAmount: invoice.cashAmount,
        CashlessAmount: invoice.cashlessAmount,
        PaymentMethod: invoice.paymentMethod,
        AuthCode: invoice.authCode,
        CostAmount: invoice.costAmount,
        TotalCost: invoice.totalCost,
        Invoice_DocumentPath: invoice.invoice_DocumentPath || invoice.Invoice_DocumentPath || null,
        Invoice_SourceId: invoice.invoice_SourceId || invoice.Invoice_SourceId || null,
        // Add entity items and related data
        entityItems: entityItems,
        productQuantities: productQuantities,
        serviceQuantities: serviceQuantities,
        packageQuantities: packageQuantities,
        packageSeriesQuantities: packageSeriesQuantities,
        productPrices: productPrices,
        servicePrices: servicePrices,
        packagePrices: packagePrices,
        packageSeriesPrices: packageSeriesPrices,
        productDiscounts: productDiscounts,
        serviceDiscounts: serviceDiscounts,
        packageDiscounts: packageDiscounts,
        packageSeriesDiscounts: packageSeriesDiscounts,
        productTaxes: productTaxes,
        serviceTaxes: serviceTaxes,
        packageTaxes: packageTaxes,
        packageSeriesTaxes: packageSeriesTaxes,
      };

      setForm(populatedForm);

      // Set selected customer option for display in read-only mode
      const cust = customers.find(c => c.customer_Id === invoice.invoice_CustomerId);
      if (cust) {
        setSelectedCustomerOption({
          value: cust.customer_Id,
          label: `${cust.customer_NameEn || 'N/A'} — ${cust.customer_ContactNo || 'N/A'}`,
          customer: cust
        });
      } else {
        setSelectedCustomerOption(null);
      }

      // Clear the navigation state to prevent re-population on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, products, services, packages, packageSeries]);

  // Handle navigation from booking page
  useEffect(() => {
    const processBookingData = async () => {
      // Check if we have booking data from navigation
      const hasBookingData = location.state?.customerId && location.state?.bookingId;

      if (!hasBookingData) {
        // No booking data - reset the refs and ensure loading is false
        console.log('🔄 [processBookingData] No booking data - resetting isFromBookingRef and processedBookingRef');
        isFromBookingRef.current = false;
        processedBookingRef.current = false;
        processedBookingIdRef.current = null;
        setIsFromBooking(false);
        setLoadingBookingData(false);
        return;
      }

      const currentBookingId = location.state.bookingId;

      // Check if this is a different booking than what we've processed
      if (processedBookingRef.current && processedBookingIdRef.current === currentBookingId) {
        console.log('⏭️ [processBookingData] Already processed this booking data - skipping');
        return;
      }

      // If it's a different booking, reset the processed flag
      if (processedBookingIdRef.current !== null && processedBookingIdRef.current !== currentBookingId) {
        console.log('🔄 [processBookingData] Different booking detected - resetting processed flag');
        processedBookingRef.current = false;
      }

      // If we have booking data, set the ref to true
      console.log('✅ [processBookingData] Found booking data - setting isFromBookingRef to true');
      isFromBookingRef.current = true;

      // Auto-select customer when navigated from booking
      const customerId = location.state.customerId;
      const bookingDetails = location.state.bookingDetails || [];

      // Determine which entity types are actually in the booking
      const hasServiceEntity = bookingDetails.some(detail => detail.entity_Type === 'ser');
      const hasPackageEntity = bookingDetails.some(detail => detail.entity_Type === 'pac');
      const hasPackageSeriesEntity = bookingDetails.some(detail => detail.entity_Type === 'sep');
      const hasMembershipEntity = bookingDetails.some(detail => detail.entity_Type === 'mem');

      // Check if required dependencies are loaded based on what's actually in the booking
      const needsCustomers = true; // Always needed
      const needsServices = hasServiceEntity;
      const needsPackages = hasPackageEntity;
      const needsPackageSeries = hasPackageSeriesEntity;
      const needsTaxes = hasServiceEntity || hasPackageEntity || hasPackageSeriesEntity; // Taxes needed for any entity type

      // Check if all required dependencies are ready
      const customersReady = !needsCustomers || customers.length > 0;
      const servicesReady = !needsServices || services.length > 0;
      const packagesReady = !needsPackages || packages.length > 0;
      const packageSeriesReady = !needsPackageSeries || packageSeries.length > 0;
      const taxesReady = !needsTaxes || taxes.length > 0;

      const allDependenciesReady = customersReady && servicesReady && packagesReady && packageSeriesReady && taxesReady;

      // Set loading state if we have booking data but dependencies aren't ready yet
      if (!allDependenciesReady) {
        // Load services if needed and not already loading/loaded
        if (needsServices && services.length === 0) {
          loadServices().catch(err => {
            console.error('Error loading services:', err);
          });
        }

        // Load taxes if needed and not already loading/loaded
        if (needsTaxes && taxes.length === 0) {
          loadTaxes().catch(err => {
            console.error('Error loading taxes:', err);
          });
        }

        setLoadingBookingData(true);
        return; // Wait for dependencies to load
      }

      // All dependencies are ready, process the booking data
      if (allDependenciesReady) {
        try {
          // Set loading state while processing
          setLoadingBookingData(true);

          // Mark that we're coming from booking page
          console.log('✅ [processBookingData] Setting isFromBooking to true');
          setIsFromBooking(true);
          isFromBookingRef.current = true;

          // Get business_Id and branch_Id from navigation state (from booking)
          const bookingBusinessId = location.state?.business_Id || location.state?.Business_Id;
          const bookingBranchId = location.state?.branch_Id || location.state?.Branch_Id;

          const customer = customers.find(c => c.customer_Id == customerId);

          if (customer) {
            // Set the selected customer option for display
            setSelectedCustomerOption({
              value: customer.customer_Id,
              label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
              customer: customer
            });

            // Set business and branch from booking if available
            if (bookingBusinessId && businesses.length > 0) {
              const businessId = typeof bookingBusinessId === 'string' ? parseInt(bookingBusinessId) : bookingBusinessId;
              const businessExists = businesses.find(b => b.business_Id === businessId);
              if (businessExists) {
                setForm(prev => ({
                  ...prev,
                  Invoice_BusinessId: businessId
                }));
                // Load branches for this business
                if (bookingBranchId) {
                  // Load branches first, then set branch
                  loadBranches(businessId).then(() => {
                    const branchId = typeof bookingBranchId === 'string' ? parseInt(bookingBranchId) : bookingBranchId;
                    setForm(prev => ({
                      ...prev,
                      Invoice_BranchId: branchId
                    }));
                  });
                }
              }
            }

            // Check if booking has membership entity type
            const hasMembershipEntity = bookingDetails.some(detail => detail.entity_Type === 'mem');

            // Get membership-related entity IDs if membership exists
            let membershipEntityIds = new Set();
            if (hasMembershipEntity) {
              try {
                const memberships = await getCustomerMemberships({ customerId: customerId });
                if (memberships && memberships.length > 0) {
                  memberships.forEach(membership => {
                    // Add the main entity (package/series)
                    membershipEntityIds.add(membership.entity_Id);

                    // Add all services and packages from membership details
                    if (membership.details) {
                      membership.details.forEach(detail => {
                        if (detail.serviceId) membershipEntityIds.add(detail.serviceId);
                        if (detail.packageId) membershipEntityIds.add(detail.packageId);
                      });
                    }
                  });
                }
              } catch (error) {
                console.error('Error fetching membership data:', error);
              }
            }

            // Process booking details to populate entities
            const entityItems = [];
            const selectedServices = [];
            const selectedPackages = [];
            const selectedPackageSeries = [];

            // Filter booking details based on isBilled, CompletionStatus, and Membership_Id
            // Only show services that are:
            // 1. CompletionStatus = 'Complete'
            // 2. Membership_Id IS NULL (individual services/packages)
            // 3. isBilled = 0/false/null (not yet billed) - handle all possible values
            const billableDetails = bookingDetails.filter(detail => {
              // Filter out membership entity type
              if (detail.entity_Type === 'mem') {
                return false;
              }

              // Check if this detail has resourceDetails
              if (detail.resourceDetails && detail.resourceDetails.length > 0) {
                // Check if any resource meets the billing criteria
                const hasBillableResource = detail.resourceDetails.some(resource => {
                  // Check completion status
                  if (resource.completionStatus !== 'Complete') return false;

                  // Check membership (exclude membership services)
                  if (resource.membership_Id !== null && resource.membership_Id !== undefined) return false;

                  // Check isBilled - handle all possible values (number, boolean, string, null, undefined)
                  // Also check InvoiceDetail_Id as an indicator of being billed
                  const isBilled = resource.isBilled || resource.IsBilled || resource.is_Billed;
                  const invoiceDetailId = resource.invoiceDetail_Id || resource.InvoiceDetail_Id || resource.Invoice_Detail_Id;
                  const isBilledValue = isBilled === true || isBilled === 1 || isBilled === '1' || isBilled === 'true';
                  const hasInvoiceDetailId = invoiceDetailId !== null && invoiceDetailId !== undefined && invoiceDetailId !== '';

                  if (isBilledValue || hasInvoiceDetailId) return false;  // Exclude if already billed

                  return true;  // Include this resource
                });
                return hasBillableResource;
              }

              // If no resourceDetails, check at detail level (fallback)
              // This shouldn't happen in normal flow, but handle it gracefully
              return true;
            });

            // Filter out membership-related entities from regular billing
            const standaloneDetails = billableDetails;

            standaloneDetails.forEach(detail => {
              // Filter resourceDetails to only include billable ones
              // Handle all possible isBilled values (number, boolean, string, null, undefined)
              const billableResources = (detail.resourceDetails || []).filter(resource => {
                // Check completion status
                if (resource.completionStatus !== 'Complete') return false;

                // Check membership (exclude membership services)
                if (resource.membership_Id !== null && resource.membership_Id !== undefined) return false;

                // Check isBilled - handle all possible values (number, boolean, string, null, undefined)
                // Also check InvoiceDetail_Id as an indicator of being billed
                const isBilled = resource.isBilled || resource.IsBilled || resource.is_Billed;
                const invoiceDetailId = resource.invoiceDetail_Id || resource.InvoiceDetail_Id || resource.Invoice_Detail_Id;
                const isBilledValue = isBilled === true || isBilled === 1 || isBilled === '1' || isBilled === 'true';
                const hasInvoiceDetailId = invoiceDetailId !== null && invoiceDetailId !== undefined && invoiceDetailId !== '';

                if (isBilledValue || hasInvoiceDetailId) return false;  // Exclude if already billed

                return true;  // Include this resource
              });

              // Skip this detail if no billable resources
              if (billableResources.length === 0) {
                return;
              }

              if (detail.entity_Type === 'ser') {
                // Service
                const service = services.find(s => s.spaService_Id === detail.entity_Id && s.status === true);
                if (service) {
                  selectedServices.push(service);
                  // Get bookingResourceDetail_Id from the first billable resource
                  // This is needed to link the invoice detail back to the booking resource for isBilled update
                  const bookingResourceDetailId = billableResources.length > 0
                    ? (billableResources[0].bookingResourceDetail_Id || billableResources[0].BookingResourceDetail_Id || null)
                    : null;

                  // Calculate tax percentage from taxIds (sum of all taxes)
                  let taxPercentage = 0;
                  const serviceTaxIds = service.taxIds || [];
                  const taxBreakdown = [];

                  if (serviceTaxIds.length > 0) {
                    serviceTaxIds.forEach(taxId => {
                      const tax = taxes.find(t => t.tax_Id === taxId);
                      if (tax && tax.tax_Perc) {
                        taxPercentage += tax.tax_Perc;
                        taxBreakdown.push({
                          id: tax.tax_Id,
                          name: tax.tax_NameEn,
                          percentage: tax.tax_Perc
                        });
                      }
                    });
                  }

                  const unitPrice = service.amount || 0;
                  const discount = service.discountPercentage || 0;
                  const totalPrice = 1 * unitPrice;
                  const discountAmount = (unitPrice * discount / 100) * 1;
                  const taxAmount = ((totalPrice - discountAmount) * taxPercentage / 100);
                  const netAmount = totalPrice - discountAmount + taxAmount;

                  entityItems.push({
                    id: `ser_${detail.entity_Id}`,
                    entityId: detail.entity_Id,
                    entityType: 'ser',
                    type: 'service',
                    entityName: service.spaServiceName_En,
                    quantity: 1,
                    qtyLocked: true,
                    unitPrice: unitPrice,
                    discount: discount,
                    discountAmount: discountAmount,
                    tax: taxPercentage,
                    taxAmount: taxAmount,
                    netAmount: netAmount,
                    costPrice: service.cost || 0,
                    taxIds: serviceTaxIds,
                    taxBreakdown: taxBreakdown,
                    bookingResourceDetailId: bookingResourceDetailId // Add booking resource detail ID for isBilled update
                  });
                }
              } else if (detail.entity_Type === 'pac') {
                // Package
                const pkg = packages.find(p => p.packageId === detail.entity_Id && p.status === true);
                if (pkg) {
                  selectedPackages.push(pkg);
                  // Get bookingResourceDetail_Id from the first billable resource
                  // This is needed to link the invoice detail back to the booking resource for isBilled update
                  // For packages, the stored procedure will mark ALL services in the package as billed
                  const bookingResourceDetailId = billableResources.length > 0
                    ? (billableResources[0].bookingResourceDetail_Id || billableResources[0].BookingResourceDetail_Id || null)
                    : null;

                  // Calculate tax percentage from taxIds (sum of all taxes)
                  let taxPercentage = 0;
                  const packageTaxIds = pkg.taxIds || [];
                  const taxBreakdown = [];

                  if (packageTaxIds.length > 0) {
                    packageTaxIds.forEach(taxId => {
                      const tax = taxes.find(t => t.tax_Id === taxId);
                      if (tax && tax.tax_Perc) {
                        taxPercentage += tax.tax_Perc;
                        taxBreakdown.push({
                          id: tax.tax_Id,
                          name: tax.tax_NameEn,
                          percentage: tax.tax_Perc
                        });
                      }
                    });
                  }

                  const unitPrice = pkg.amount || 0;
                  const discount = pkg.discountPercentage || 0;
                  const totalPrice = 1 * unitPrice;
                  const discountAmount = (unitPrice * discount / 100) * 1;
                  const taxAmount = ((totalPrice - discountAmount) * taxPercentage / 100);
                  const netAmount = totalPrice - discountAmount + taxAmount;

                  // CRITICAL FIX: Use bookingDetail_Id in the ID to distinguish multiple instances of the same package
                  // If bookingDetail_Id exists, use it; otherwise fall back to entity_Id
                  const detailId = detail.bookingDetail_Id || detail.BookingDetail_Id || detail.entity_Id;
                  entityItems.push({
                    id: `pac_${detail.entity_Id}_${detailId}`, // Include bookingDetail_Id to distinguish instances
                    entityId: detail.entity_Id,
                    entityType: 'pac',
                    type: 'package',
                    entityName: pkg.packageName_En,
                    quantity: 1,
                    qtyLocked: true,
                    unitPrice: unitPrice,
                    discount: discount,
                    discountAmount: discountAmount,
                    tax: taxPercentage,
                    taxAmount: taxAmount,
                    netAmount: netAmount,
                    costPrice: pkg.cost || 0,
                    taxIds: packageTaxIds,
                    taxBreakdown: taxBreakdown,
                    bookingResourceDetailId: bookingResourceDetailId, // Add booking resource detail ID for isBilled update
                    bookingDetail_Id: detailId // Preserve bookingDetail_Id for reference
                  });
                }
              } else if (detail.entity_Type === 'sep') {
                // Package Series
                const series = packageSeries.find(s => s.seriesId === detail.entity_Id && s.status === true);
                if (series) {
                  selectedPackageSeries.push(series);
                  // Calculate tax percentage from taxIds (sum of all taxes)
                  let taxPercentage = 0;
                  const seriesTaxIds = series.taxIds || [];
                  const taxBreakdown = [];

                  if (seriesTaxIds.length > 0) {
                    seriesTaxIds.forEach(taxId => {
                      const tax = taxes.find(t => t.tax_Id === taxId);
                      if (tax && tax.tax_Perc) {
                        taxPercentage += tax.tax_Perc;
                        taxBreakdown.push({
                          id: tax.tax_Id,
                          name: tax.tax_NameEn,
                          percentage: tax.tax_Perc
                        });
                      }
                    });
                  }

                  const unitPrice = series.amount || 0;
                  const discount = series.discountPercentage || 0;
                  const totalPrice = 1 * unitPrice;
                  const discountAmount = (unitPrice * discount / 100) * 1;
                  const taxAmount = ((totalPrice - discountAmount) * taxPercentage / 100);
                  const netAmount = totalPrice - discountAmount + taxAmount;

                  entityItems.push({
                    id: `sep_${detail.entity_Id}`,
                    entityId: detail.entity_Id,
                    entityType: 'sep',
                    type: 'packageSeries',
                    entityName: series.seriesName_En,
                    quantity: 1,
                    qtyLocked: true,
                    unitPrice: unitPrice,
                    discount: discount,
                    discountAmount: discountAmount,
                    tax: taxPercentage,
                    taxAmount: taxAmount,
                    netAmount: netAmount,
                    costPrice: series.cost || 0,
                    taxIds: seriesTaxIds,
                    taxBreakdown: taxBreakdown
                  });
                }
              } else if (detail.entity_Type === 'mem') {
                // Membership - will be handled by membership installment logic
              }
            });

            // Calculate totals from entityItems (same logic as calculateTotals)
            let totalQty = 0;
            let totalAmount = 0;
            let totalDiscount = 0;
            let totalTax = 0;
            let totalCost = 0;

            entityItems.forEach(item => {
              const lineTotal = (item.unitPrice ?? 0) * item.quantity;
              const discountAmount = item.discountAmount || 0;
              const taxAmount = item.taxAmount || 0;
              const costAmount = (item.costPrice || 0) * item.quantity;

              totalQty += item.quantity;
              totalAmount += lineTotal;
              totalDiscount += discountAmount;
              totalTax += taxAmount;
              totalCost += costAmount;
            });

            // Calculate totals and set form with functional update to access current form state
            setForm(prev => {
              // Apply overall discount percentage (using current form values from prev)
              const currentDiscountPercentage = prev.discountPercentage || 0;
              const overallDiscountAmount = (totalAmount * currentDiscountPercentage) / 100;
              const finalTotalDiscount = totalDiscount + overallDiscountAmount;

              // Calculate initial net amount: amount - discount + tax
              const initialNetAmount = totalAmount - finalTotalDiscount + totalTax;

              // Apply additional overall discount percentage to initial net amount
              const currentOverallDiscountPercentage = prev.overallDiscountPercentage || 0;
              const additionalOverallDiscountAmount = (initialNetAmount * currentOverallDiscountPercentage) / 100;
              const finalNetAmount = initialNetAmount - additionalOverallDiscountAmount;
              const roundedNetAmount = Math.round(finalNetAmount * 100) / 100;

              return {
                ...prev,
                Invoice_CustomerId: customerId,
                selectedServices: selectedServices,
                selectedPackages: selectedPackages,
                selectedPackageSeries: selectedPackageSeries,
                entityItems: entityItems,
                Invoice_TotalQty: totalQty,
                Invoice_TotalAmount: totalAmount,
                Invoice_DisAmount: finalTotalDiscount,
                Invoice_TaxAmount: totalTax,
                Invoice_NetAmount: roundedNetAmount,
                GiftCardAmount: prev.GiftCardAmount || 0,
                RedeemedGiftCardCode: prev.RedeemedGiftCardCode || "",
                CostAmount: totalCost,
                TotalCost: totalCost,
                overallDiscountAmount: additionalOverallDiscountAmount,
                // Initialize AmountPaidByCustomer to net amount (same as when opening directly)
                AmountPaidByCustomer: prev.AmountPaidByCustomer === null || prev.AmountPaidByCustomer === '' ? roundedNetAmount : prev.AmountPaidByCustomer,
                TipAmount: prev.AmountPaidByCustomer === null || prev.AmountPaidByCustomer === '' ? 0 : prev.TipAmount
              };
            });

            // Check if coming from booking page
            const comingFromBooking = location.state?.customerId && location.state?.bookingId;

            // IMPORTANT: Always check for billable items (memberships/unbilled services) when coming from booking
            // This ensures the "Select Unbilled Services to Bill" button is shown if customer has billable items
            // We call openMembershipModal but don't auto-open the modal (forceOpen = false)
            // This will set customerHasMembership and customerHasBillableItems flags for button visibility
            if (comingFromBooking) {
              // Coming from booking - check for billable items but don't auto-open modal
              setTimeout(() => {
                openMembershipModal(customerId, false); // Check for billable items but don't auto-open modal
              }, 100);
            } else if (hasMembershipEntity) {
              // Not coming from booking and has membership entities - auto-open modal
              setTimeout(() => {
                openMembershipModal(customerId);
              }, 100);
            } else {
              // Not coming from booking and no membership entities - still check for billable items
              // This handles cases where customer might have unbilled services or memberships
              setTimeout(() => {
                openMembershipModal(customerId, false); // Check for billable items but don't auto-open modal
              }, 100);
            }

            // Clear the navigation state
            window.history.replaceState({}, document.title);

            // Mark as processed to prevent re-processing
            processedBookingRef.current = true;
            processedBookingIdRef.current = currentBookingId;

            // Set loading to false after processing is complete
            setLoadingBookingData(false);
          } else {
            // Customer not found in list, but still set the customer ID and mark as from booking
            setIsFromBooking(true);
            isFromBookingRef.current = true;

            // Set business and branch from booking if available
            if (bookingBusinessId && businesses.length > 0) {
              const businessId = typeof bookingBusinessId === 'string' ? parseInt(bookingBusinessId) : bookingBusinessId;
              const businessExists = businesses.find(b => b.business_Id === businessId);
              if (businessExists) {
                setForm(prev => ({
                  ...prev,
                  Invoice_CustomerId: customerId,
                  Invoice_BusinessId: businessId
                }));
                // Load branches for this business
                if (bookingBranchId) {
                  loadBranches(businessId).then(() => {
                    const branchId = typeof bookingBranchId === 'string' ? parseInt(bookingBranchId) : bookingBranchId;
                    setForm(prev => ({
                      ...prev,
                      Invoice_BranchId: branchId
                    }));
                  });
                }
              } else {
                setForm(prev => ({
                  ...prev,
                  Invoice_CustomerId: customerId
                }));
              }
            } else {
              setForm(prev => ({
                ...prev,
                Invoice_CustomerId: customerId
              }));
            }

            // Set a basic customer option with just the ID
            setSelectedCustomerOption({
              value: customerId,
              label: `Customer ID: ${customerId}`
            });
            // Clear the navigation state
            window.history.replaceState({}, document.title);

            // Mark as processed to prevent re-processing
            processedBookingRef.current = true;
            processedBookingIdRef.current = currentBookingId;

            // Set loading to false after processing is complete
            setLoadingBookingData(false);
          }
        } catch (error) {
          console.error('Error processing booking data:', error);
          // Reset processed ref on error so it can retry
          processedBookingRef.current = false;
          processedBookingIdRef.current = null;
          // Ensure loading is cleared even if there's an error
          setLoadingBookingData(false);
          // Show error message to user
          Swal.fire({
            icon: 'error',
            title: 'Error Processing Booking',
            text: error.message || 'Failed to process booking data. Please try again.',
            confirmButtonText: 'OK'
          });
        }
      }
    };

    processBookingData();
  }, [location.state, customers, services, packages, packageSeries, taxes]);

  // Handle navigation from Customer Profile Modal
  useEffect(() => {
    const processCustomerProfileData = async () => {
      // Check if we have data from customer profile navigation
      const hasCustomerProfileData = location.state?.fromCustomerProfile && location.state?.customerId;

      // Reset the ref if there's no customer profile data
      if (!hasCustomerProfileData) {
        processedCustomerProfileRef.current = false;
        return;
      }

      // Skip if we've already processed this navigation
      if (processedCustomerProfileRef.current) {
        return;
      }

      const customerId = location.state.customerId;
      const selectedInstallments = location.state.selectedInstallments || [];
      const selectedUnbilledServices = location.state.selectedUnbilledServices || [];

      // Process customer profile navigation data

      // Mark as processed to avoid duplicate processing
      processedCustomerProfileRef.current = true;

      // Show loader while processing customer profile data
      setLoadingBookingData(true);

      // Check if required dependencies are loaded
      // For services, we don't actually need the services list loaded - we'll match by ID from API
      // For installments, we need packages/packageSeries, but we can proceed without them and load later
      const needsCustomers = true;
      const needsServices = false; // Don't require services list - we'll match by ID from API
      const needsPackages = false; // Can load packages later if needed
      const needsPackageSeries = false; // Can load package series later if needed
      const needsTaxes = false; // Taxes will be loaded when needed

      const customersReady = !needsCustomers || customers.length > 0;
      const servicesReady = !needsServices || services.length > 0;
      const packagesReady = !needsPackages || packages.length > 0;
      const packageSeriesReady = !needsPackageSeries || packageSeries.length > 0;
      const taxesReady = !needsTaxes || taxes.length > 0;

      const allDependenciesReady = customersReady && servicesReady && packagesReady && packageSeriesReady && taxesReady;

      if (!allDependenciesReady) {
        // Reset the ref so we can try again when dependencies are ready
        processedCustomerProfileRef.current = false;
        setLoadingBookingData(false); // Hide loader if dependencies not ready
        return; // Wait for dependencies to load
      }

      try {
        // Set the customer
        const customer = customers.find(c => c.customer_Id == customerId);

        if (customer) {
          setSelectedCustomerOption({
            value: customer.customer_Id,
            label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
            customer: customer
          });

          setForm(prev => ({
            ...prev,
            Invoice_CustomerId: customerId
          }));

          // Auto-open membership modal with pre-selected items
          await openMembershipModalWithPreSelection(customerId, selectedInstallments, selectedUnbilledServices);
        } else {
          // Customer not found, but still set the customer ID
          setForm(prev => ({
            ...prev,
            Invoice_CustomerId: customerId
          }));
          setSelectedCustomerOption({
            value: customerId,
            label: `Customer ID: ${customerId}`
          });

          // Auto-open membership modal with pre-selected items
          await openMembershipModalWithPreSelection(customerId, selectedInstallments, selectedUnbilledServices);
        }

        // Clear the navigation state after a delay to ensure processing is complete
        setTimeout(() => {
          window.history.replaceState({}, document.title);
        }, 1000);
      } catch (error) {
        console.error('Error processing customer profile data:', error);
        processedCustomerProfileRef.current = false; // Reset on error
        setLoadingBookingData(false); // Hide loader on error
        Swal.fire({
          icon: 'error',
          title: 'Error Processing Data',
          text: error.message || 'Failed to process data from customer profile. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    };

    processCustomerProfileData();
  }, [location.state, customers, services, packages, packageSeries, taxes]);

  // Function to open membership modal with pre-selected items
  const openMembershipModalWithPreSelection = async (customerId, selectedInstallments, selectedUnbilledServices) => {
    if (!customerId) {
      setLoadingBookingData(false);
      return;
    }

    try {
      // Fetch both memberships and unbilled services
      const [memberships, unbilledServicesData] = await Promise.all([
        getCustomerMemberships({ customerId: customerId }),
        getCustomerUnbilledServices({
          CustomerId: customerId,
          BranchId: null
        }).catch(err => {
          console.error('Error fetching unbilled services:', err);
          return [];
        })
      ]);

      console.log('🔍 [openMembershipModalWithPreSelection] Raw API response:', {
        memberships,
        firstMembership: memberships && memberships.length > 0 ? memberships[0] : null,
        firstMembershipKeys: memberships && memberships.length > 0 ? Object.keys(memberships[0]) : []
      });

      // Set membership status
      setCustomerHasMembership(memberships && memberships.length > 0);

      // Check if there are unpaid installments
      const hasUnpaidInstallments = memberships && memberships.some(membership => {
        if (!membership.installments || membership.installments.length === 0) {
          return false;
        }
        return membership.installments.some(installment => {
          const billedAmount = installment.billedAmount || installment.paidAmount || 0;
          const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
          return remainingAmount > 0;
        });
      });

      // Check if there are unbilled services
      const hasUnbilledServices = unbilledServicesData && unbilledServicesData.length > 0;

      // Set billable items status only if there are actually billable items
      setCustomerHasBillableItems(hasUnpaidInstallments || hasUnbilledServices);

      // Process membership data
      let membershipsWithPackageData = [];
      let installmentNames = {};
      let entityNames = {};

      const transformedMemberships = memberships.map(membership => {
        console.log('🔍 [openMembershipModalWithPreSelection] Transforming membership:', {
          membershipId: membership.membershipId,
          rawMembership: membership,
          rawSalesmanIds: membership.salesmanIds,
          rawSalesmanId: membership.salesmanId,
          allKeys: Object.keys(membership)
        });

        const allInstallments = (membership.installments || []).map(installment => ({
          m_InstallmentId: installment.m_InstallmentId,
          membershipId: installment.membershipId,
          seriesId: installment.seriesId,
          installmentId: installment.installmentId,
          amount: installment.amount,
          paidAmount: installment.billedAmount || installment.paidAmount || 0
        }));

        const minInstallmentId = allInstallments.length > 0
          ? Math.min(...allInstallments.map(inst => inst.installmentId))
          : null;

        const extractedSalesmanIds = membership.salesmanIds && Array.isArray(membership.salesmanIds) && membership.salesmanIds.length > 0
          ? membership.salesmanIds.map(id => parseInt(id)).filter(id => id > 0)
          : (() => {
            const singleSalesmanId = membership.salesmanId || membership.SalesmanId || membership.salesman_ID || membership.Salesman_ID || null;
            return singleSalesmanId && singleSalesmanId > 0 ? [parseInt(singleSalesmanId)] : [];
          })();

        console.log('🔍 [openMembershipModalWithPreSelection] Extracted salesmanIds:', {
          membershipId: membership.membershipId,
          extractedSalesmanIds,
          rawSalesmanIds: membership.salesmanIds
        });

        const transformed = {
          membershipId: membership.membershipId,
          customerId: membership.customerId,
          branchId: membership.branchId,
          entity_Id: membership.entity_Id,
          entity_Type: membership.entity_Type,
          purchaseDate: membership.purchaseDate,
          salesmanId: membership.salesmanId || membership.SalesmanId || membership.salesman_ID || membership.Salesman_ID || null,
          salesmanIds: extractedSalesmanIds,
          installments: allInstallments,
          minInstallmentId: minInstallmentId
        };

        console.log('🔍 [openMembershipModalWithPreSelection] Transformed membership:', {
          membershipId: transformed.membershipId,
          transformedSalesmanIds: transformed.salesmanIds
        });

        return transformed;
      });

      // Fetch entity names
      const packageDataMap = {};

      for (const membership of transformedMemberships) {
        try {
          if (isPackageType(membership.entity_Type)) {
            const packageData = await getSpaPackages({ packageId: membership.entity_Id });
            if (packageData && packageData.length > 0) {
              entityNames[membership.entity_Id] = packageData[0].packageName_En || `Package ${membership.entity_Id}`;
              packageDataMap[membership.entity_Id] = packageData[0];
            }
          } else if (isPackageSeriesType(membership.entity_Type)) {
            const seriesData = await getSpaPackageSeries({ seriesId: membership.entity_Id });
            if (seriesData && seriesData.length > 0) {
              entityNames[membership.entity_Id] = seriesData[0].seriesName_En || `Series ${membership.entity_Id}`;
            }
          }
        } catch (error) {
          console.error('Error fetching entity name:', error);
          entityNames[membership.entity_Id] = `${isPackageType(membership.entity_Type) ? 'Package' : 'Series'} ${membership.entity_Id}`;
        }
      }

      membershipsWithPackageData = transformedMemberships.map(membership => ({
        ...membership,
        packageData: isPackageType(membership.entity_Type) ? packageDataMap[membership.entity_Id] : null
      }));

      console.log('🔍 [openMembershipModalWithPreSelection] Final membershipsWithPackageData:', {
        count: membershipsWithPackageData.length,
        firstMembership: membershipsWithPackageData.length > 0 ? membershipsWithPackageData[0] : null,
        firstMembershipSalesmanIds: membershipsWithPackageData.length > 0 ? membershipsWithPackageData[0].salesmanIds : null
      });

      // Fetch installment names for package series
      for (const membership of membershipsWithPackageData) {
        if (isPackageSeriesType(membership.entity_Type) && membership.installments.length > 0) {
          try {
            const seriesData = await getSpaPackageSeries({ seriesId: membership.entity_Id });
            if (seriesData && seriesData.length > 0 && seriesData[0].installments) {
              seriesData[0].installments.forEach(installment => {
                installmentNames[installment.installmentId] = installment.installmentName;
              });
            }
          } catch (error) {
            console.error('Error fetching installment names:', error);
          }
        }
      }

      // Pre-select installments
      const preSelectedInstallments = {};
      selectedInstallments.forEach(selected => {
        if (selected && selected.installmentId) {
          preSelectedInstallments[selected.installmentId] = {
            checked: true,
            amount: selected.amount || 0,
            validationError: ''
          };
        }
      });

      // Pre-select unbilled services
      // Match services from navigation state with services fetched from API
      const preSelectedServices = {};

      selectedUnbilledServices.forEach((service) => {
        const serviceBookingResourceDetailId = service.bookingResourceDetail_Id || service.BookingResourceDetail_Id;
        const serviceBookingId = service.booking_Id || service.Booking_Id;

        if (!serviceBookingResourceDetailId) {
          console.error('Service missing bookingResourceDetailId:', service);
          return;
        }

        // Always use the original service ID from navigation state for the key
        // This ensures each service gets a unique key even if they match to the same fetched service
        const key = `unbilled_${serviceBookingResourceDetailId}`;

        // PRIORITY: Use the amount from the navigation state (original service object)
        // This preserves the exact amount that was selected in the customer profile
        const originalCalculatedAmount = service.calculatedAmount || service.CalculatedAmount || 0;
        const originalServiceNetAmount = service.serviceNetAmount || service.ServiceNetAmount || 0;
        const originalPackageAmount = service.packageAmount || service.PackageAmount || 0; // Use PackageAmount (before tax) instead of NetAmount
        const originalPackageNetAmount = service.packageNetAmount || service.PackageNetAmount || 0;
        const isPackage = service.package_Id || service.Package_Id;
        // For packages, prioritize: selectedAmount > packageAmount > calculatedAmount (which should be PackageAmount)
        // For services, use: selectedAmount > calculatedAmount > serviceNetAmount
        const originalAmount = service.selectedAmount ||
          (isPackage ? (originalPackageAmount || originalCalculatedAmount) : (originalCalculatedAmount || originalServiceNetAmount)) || 0;

        // Try to find matching service in fetched data (for validation, but don't override the amount)
        const matchingService = (unbilledServicesData || []).find(s => {
          const fetchedId = s.bookingResourceDetail_Id || s.BookingResourceDetail_Id;
          const fetchedBookingId = s.booking_Id || s.Booking_Id;

          // Match by bookingResourceDetailId first (most reliable) - use loose comparison for type safety
          const idMatch = fetchedId == serviceBookingResourceDetailId || String(fetchedId) === String(serviceBookingResourceDetailId);

          // Fallback: match by bookingId if IDs don't match
          const bookingIdMatch = fetchedBookingId && serviceBookingId && (fetchedBookingId == serviceBookingId || String(fetchedBookingId) === String(serviceBookingId));

          return idMatch || bookingIdMatch;
        });

        // Use the original amount from navigation state (preserves the exact amount selected)
        // Only fallback to fetched amount if original amount is 0 or missing
        let finalAmount = originalAmount;
        if (finalAmount === 0 && matchingService) {
          // Fallback: use fetched service's amount only if original amount is 0
          const fetchedCalculatedAmount = matchingService.calculatedAmount || matchingService.CalculatedAmount || 0;
          const fetchedServiceNetAmount = matchingService.serviceNetAmount || matchingService.ServiceNetAmount || 0;
          const fetchedPackageAmount = matchingService.packageAmount || matchingService.PackageAmount || 0; // Use PackageAmount (before tax) instead of NetAmount
          const fetchedIsPackage = matchingService.package_Id || matchingService.Package_Id;
          // For packages, prioritize: packageAmount > calculatedAmount (which should be PackageAmount)
          // For services, use: calculatedAmount > serviceNetAmount
          finalAmount = fetchedIsPackage
            ? (fetchedPackageAmount || fetchedCalculatedAmount)
            : (fetchedCalculatedAmount || fetchedServiceNetAmount) || 0;
        }

        preSelectedServices[key] = {
          checked: true,
          amount: finalAmount,
          validationError: ''
        };
      });

      // Open modal with pre-selected items
      setForm(prev => ({
        ...prev,
        showMembershipModal: true,
        selectedMembershipInstallments: preSelectedInstallments,
        selectedCompletePayments: {},
        customerMemberships: membershipsWithPackageData,
        installmentNames: installmentNames,
        entityNames: entityNames,
        unbilledServices: unbilledServicesData || [],
        selectedUnbilledServices: preSelectedServices
      }));

      // Hide loader once modal is opened and items are pre-selected
      setLoadingBookingData(false);
    } catch (error) {
      console.error('Error opening membership modal with pre-selection:', error);
      setLoadingBookingData(false); // Hide loader on error
      Swal.fire("Error", "Failed to load customer data", "error");
    }
  };

  useEffect(() => {
    if (hasAppliedFilter) {
      // Removed loadInvoices() - invoice list is on separate page (/GmsBillingList)
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  // Keep entityItemsRef in sync with form.entityItems
  useEffect(() => {
    entityItemsRef.current = form.entityItems || [];
  }, [form.entityItems]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCustomers(),
        loadBusinesses(),
        loadBranches(),
        // Removed loadProducts() and loadServices() - now loaded only when modal opens
        loadPackages(),
        loadPackageSeries(),
        loadTaxes(),
        loadEmployees(),
        loadSources(),
        loadGiftCards(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({ status: true });
      setCustomers(data || []);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  // Manual search function for customer search
  const searchCustomersManually = async (inputValue) => {
    // Trim spaces from the search text to handle cases like "052 422 5719"
    const cleanedInput = inputValue ? inputValue.replace(/\s+/g, '') : '';
    const originalInput = inputValue ? inputValue.trim() : '';

    if (!originalInput || originalInput.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      // Try searching with the cleaned input (no spaces)
      const response = await searchCustomers(cleanedInput, 1, 20);

      let formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      // If no results with cleaned input, try with original input (optional, but safer)
      if (formattedOptions.length === 0 && cleanedInput !== originalInput) {
        const responseOriginal = await searchCustomers(originalInput, 1, 20);
        formattedOptions = responseOriginal.items.map(customer => ({
          value: customer.customer_Id,
          label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
          customer: customer
        }));
      }

      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Manual search function for customer filter search
  const searchCustomersFilterManually = async (inputValue) => {
    // Trim spaces from the search text to handle cases like "052 422 5719"
    const cleanedInput = inputValue ? inputValue.replace(/\s+/g, '') : '';
    const originalInput = inputValue ? inputValue.trim() : '';

    if (!originalInput || originalInput.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      // Try searching with the cleaned input (no spaces)
      const response = await searchCustomers(cleanedInput, 1, 20);

      let formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      // If no results with cleaned input, try with original input (optional, but safer)
      if (formattedOptions.length === 0 && cleanedInput !== originalInput) {
        const responseOriginal = await searchCustomers(originalInput, 1, 20);
        formattedOptions = responseOriginal.items.map(customer => ({
          value: customer.customer_Id,
          label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
          customer: customer
        }));
      }

      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Manual search function for employee filter search
  const searchEmployeesFilterManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeSearchOptions([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      // Pass the selected company and branch from form
      const businessId = form.Invoice_BusinessId || null;
      const branchId = form.Invoice_BranchId || null;
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        employee: employee
      }));

      setEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeSearchOptions([]);
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  const handleCustomerAdded = async (newCustomer) => {

    // Get the customer ID from the response
    const customerId = newCustomer.customer_Id || newCustomer.id;

    try {
      // Try to fetch the specific customer by ID
      const customerData = await getCustomers({ customer_Id: customerId });

      if (customerData && customerData.length > 0) {
        const addedCustomer = customerData[0];

        // Set the newly added customer as selected
        setForm(prev => ({
          ...prev,
          Invoice_CustomerId: customerId
        }));
        // Set the selected customer option for display
        setSelectedCustomerOption({
          value: customerId,
          label: `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`
        });
        // Also reload the full customer list to keep it updated
        loadCustomers();
      } else {
        // Fallback: just set the ID and let the user search for the customer
        setForm(prev => ({
          ...prev,
          Invoice_CustomerId: customerId
        }));
        setSelectedCustomerOption({
          value: customerId,
          label: `Customer ID: ${customerId}`
        });

        // Still reload the full customer list
        loadCustomers();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      // Fallback: just set the ID
      setForm(prev => ({
        ...prev,
        Invoice_CustomerId: customerId
      }));
      setSelectedCustomerOption({
        value: customerId,
        label: `Customer ID: ${customerId}`
      });
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async (businessId = null) => {
    try {
      const filter = { branch_Active: true };
      if (businessId) {
        filter.Business_Id = businessId;
      }
      const data = await getBranches(filter);
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadEmployees = async () => {
    try {
      // Get companyId from login response
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const companyId = authUser.companyId;

      // Filter employees by company if companyId exists
      const filter = companyId ? { business_Id: companyId } : {};
      const data = await getEmployees(filter);
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadSources = async () => {
    try {
      setSourcesLoading(true);
      const response = await getSources({ Page: 1, PageSize: 1000 });
      const data = response?.sources || response?.Sources || (Array.isArray(response) ? response : []);

      if (Array.isArray(data)) {
        const options = data.map(source => ({
          value: source.source_Id || source.Source_Id,
          label: source.sourceName || source.SourceName || 'N/A'
        }));
        setSourceOptions(options);
      }
    } catch (error) {
      console.error("Error loading sources:", error);
    } finally {
      setSourcesLoading(false);
    }
  };

  const loadNextInvoiceNumber = async () => {
    try {
      // Only generate invoice number if BusinessId is available
      // BusinessId is required for per-business invoice numbering
      const businessId = form.Invoice_BusinessId || null;

      if (!businessId || businessId === 0) {
        // Clear invoice number if no business selected
        setForm(prev => {
          return { ...prev, Invoice_No: "" };
        });
        return;
      }

      // Use POST method with business ID for regular invoice number generation
      const payload = {
        Business_Id: businessId,
        IsReturn: false,
        Return_Original_InvoiceNo: null
      };

      const response = await fetch(`${BASE_URL}/api/SalesInvoice/get-next-invoice-number`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle response - check both camelCase and possible variations
      const invoiceNumber = data.nextInvoiceNumber || data.NextInvoiceNumber || data.nextInvoiceNo || "";

      if (invoiceNumber) {
        setForm(prev => {
          const newInvoiceNo = String(invoiceNumber);
          return { ...prev, Invoice_No: newInvoiceNo };
        });
      } else {
        console.warn('No invoice number in response:', data); // Debug log
        setForm(prev => {
          return { ...prev, Invoice_No: "" };
        });
      }
    } catch (error) {
      console.error('Error loading next invoice number:', error);
      // Clear invoice number on error
      setForm(prev => {
        return { ...prev, Invoice_No: "" };
      });
    }
  };

  // Load next invoice number when BusinessId is available
  // NOTE: This useEffect must be defined AFTER loadNextInvoiceNumber function
  useEffect(() => {
    // Only load invoice number if BusinessId is set and not in return mode
    if (form.Invoice_BusinessId && !isReturnMode) {
      // Small delay to ensure form state is stable
      const timer = setTimeout(() => {
        loadNextInvoiceNumber();
      }, 100);
      return () => clearTimeout(timer);
    } else if (!form.Invoice_BusinessId) {
      // Clear invoice number if business is cleared
      setForm(prev => ({ ...prev, Invoice_No: "" }));
    }
  }, [form.Invoice_BusinessId, isReturnMode]); // loadNextInvoiceNumber is intentionally excluded - it's recreated on each render

  const loadProducts = async () => {
    try {
      const data = await getProducts({ status: true });
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };

  const loadServices = async () => {
    try {
      const data = await getSpaServices({ status: true });
      // Additional client-side filtering to ensure only active services are stored
      const activeServices = (data || []).filter(service => service.status === true);
      setServices(activeServices);
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  const loadPackages = async () => {
    try {
      const data = await getSpaPackages({ status: true });
      // Additional client-side filtering to ensure only active packages are stored
      const activePackages = (data || []).filter(pkg => pkg.status === true);
      setPackages(activePackages);
    } catch (error) {
      console.error("Error loading packages:", error);
    }
  };

  const loadPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ status: true });
      // Additional client-side filtering to ensure only active package series are stored
      const activePackageSeries = (data || []).filter(series => series.status === true);
      setPackageSeries(activePackageSeries);
    } catch (error) {
      console.error("Error loading package series:", error);
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ status: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
    }
  };

  const loadGiftCards = async () => {
    try {
      const response = await getGiftCards({ Page: 1, PageSize: 1000 });
      const data = response?.items || response?.Items || (Array.isArray(response) ? response : []);
      setGiftCards(data || []);
    } catch (error) {
      console.error("Error loading gift cards:", error);
    }
  };

  const generateGiftCardCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let randomPart = '';
    for (let i = 0; i < 6; i++) {
      randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const timePart = Date.now().toString(36).toUpperCase().slice(-4);
    return (randomPart + timePart).substring(0, 10);
  };

  // Function to fetch individual entity cost
  const fetchEntityCost = async (entityType, entityId) => {
    try {
      let cost = 0;

      if (entityType === 'product') {
        const product = await getProducts({ product_Id: entityId });
        if (product && product.length > 0) {
          cost = product[0].product_CostPrice || 0;
        }
      } else if (entityType === 'service') {
        const service = await getSpaServices({ SpaService_Id: entityId });
        if (service && service.length > 0) {
          cost = service[0].cost || 0;
        }
      } else if (entityType === 'package') {
        const packageData = await getSpaPackages({ PackageId: entityId });
        if (packageData && packageData.length > 0) {
          cost = packageData[0].cost || 0;
        }
      } else if (entityType === 'packageSeries') {
        const packageSeries = await getSpaPackageSeries({ SeriesId: entityId });
        if (packageSeries && packageSeries.length > 0) {
          cost = packageSeries[0].cost || 0;
        }
      }

      return cost;
    } catch (error) {
      console.error(`Error fetching ${entityType} cost:`, error);
      return 0;
    }
  };

  // Function to populate entity data when selected
  const populateEntityData = (entityType, selectedItems) => {
    selectedItems.forEach(item => {
      let entityData = null;

      if (entityType === 'product') {
        entityData = products.find(p => p.product_Id === item.value);
        if (entityData) {
          handlePriceChange('product', item.value, entityData.product_CostPrice || 0);
          handleDiscountChange('product', item.value, entityData.product_DiscountPercentage || 0);
          // Calculate tax percentage for products using combined tax calculation
          const taxPercentage = calculateCombinedTaxPercentage(entityData.taxIds || []);
          handleTaxChange('product', item.value, taxPercentage);
        }
      } else if (entityType === 'service') {
        entityData = services.find(s => s.spaService_Id === item.value && s.status === true);
        if (entityData && entityData.status === true) {
          // For services, we need to calculate price based on duration or use a default
          const servicePrice = entityData.amount || 100; // Default price if not available
          handlePriceChange('service', item.value, servicePrice);
          handleDiscountChange('service', item.value, entityData.discountPercentage || 0);
          // Calculate tax percentage for services using combined tax calculation
          const taxPercentage = calculateCombinedTaxPercentage(entityData.taxIds || []);
          handleTaxChange('service', item.value, taxPercentage);
        }
      } else if (entityType === 'package') {
        entityData = packages.find(p => p.packageId === item.value && p.status === true);
        if (entityData && entityData.status === true) {
          handlePriceChange('package', item.value, entityData.amount || 0);
          handleDiscountChange('package', item.value, entityData.discountPercentage || 0);
          // Calculate tax percentage for packages using combined tax calculation
          const taxPercentage = calculateCombinedTaxPercentage(entityData.taxIds || []);
          handleTaxChange('package', item.value, taxPercentage);
        }
      } else if (entityType === 'packageSeries') {
        entityData = packageSeries.find(s => s.seriesId === item.value && s.status === true);
        if (entityData && entityData.status === true) {
          handlePriceChange('packageSeries', item.value, entityData.amount || 0);
          handleDiscountChange('packageSeries', item.value, entityData.discountPercentage || 0);
          // Calculate tax percentage for package series using combined tax calculation
          const taxPercentage = calculateCombinedTaxPercentage(entityData.taxIds || []);
          handleTaxChange('packageSeries', item.value, taxPercentage);
        }
      }
    });
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getSalesInvoices(filter);

      // Sort invoices by invoice ID in descending order (newest first)
      const sortedData = (data || []).sort((a, b) => {
        const idA = a.invoice_Id || a.Invoice_Id || 0;
        const idB = b.invoice_Id || b.Invoice_Id || 0;
        return idB - idA;
      });

      setInvoices(sortedData);
      setTotalCount(sortedData.length);
    } catch (error) {
      console.error("Error loading invoices:", error);
      Swal.fire("Error", "Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals when form changes
  useEffect(() => {
    calculateTotals();
  }, [form.selectedProducts, form.selectedServices, form.selectedPackages, form.selectedPackageSeries, form.productQuantities, form.serviceQuantities, form.packageQuantities, form.packageSeriesQuantities, form.discountPercentage, form.overallDiscountPercentage, form.entityItems]);

  const calculateTotals = () => {
    let totalQty = 0;
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let totalCost = 0;

    // Calculate totals from entityItems array
    form.entityItems.forEach(item => {
      const lineTotal = (item.unitPrice ?? 0) * item.quantity;
      const discountAmount = item.discountAmount || 0;
      const taxAmount = item.taxAmount || 0;
      const costAmount = (item.costPrice || 0) * item.quantity; // Cost * quantity

      totalQty += item.quantity;
      totalAmount += lineTotal;
      totalDiscount += discountAmount;
      totalTax += taxAmount;
      totalCost += costAmount;
    });

    // Apply overall discount percentage
    const overallDiscountAmount = (totalAmount * (form.discountPercentage || 0)) / 100;
    const finalTotalDiscount = totalDiscount + overallDiscountAmount;

    // Calculate initial net amount: amount - discount + tax
    const initialNetAmount = totalAmount - finalTotalDiscount + totalTax;

    // Apply additional overall discount percentage to initial net amount
    const additionalOverallDiscountAmount = (initialNetAmount * (form.overallDiscountPercentage || 0)) / 100;
    const finalNetAmount = initialNetAmount - additionalOverallDiscountAmount;

    setForm(prev => ({
      ...prev,
      Invoice_TotalQty: totalQty,
      Invoice_TotalAmount: totalAmount,
      Invoice_DisAmount: finalTotalDiscount,
      Invoice_TaxAmount: totalTax,
      Invoice_NetAmount: finalNetAmount,
      CostAmount: totalCost,
      TotalCost: totalCost,
      overallDiscountAmount: additionalOverallDiscountAmount,
    }));
  };

  // Function to show all customer memberships with detailed view
  const showAllCustomerMemberships = async (customerId) => {
    if (!customerId) {
      Swal.fire("Warning", "Please select a customer first", "warning");
      return;
    }

    try {
      const memberships = await getCustomerMemberships({ customerId: customerId });

      if (!memberships || memberships.length === 0) {
        Swal.fire("Info", "This customer has no memberships.", "info");
        return;
      }

      // Fetch entity names and installment names
      const entityNames = {};
      const installmentNamesMap = {};

      for (const membership of memberships) {
        try {
          if (isPackageType(membership.entity_Type)) {
            const packageData = await getSpaPackages({ packageId: membership.entity_Id });
            if (packageData && packageData.length > 0) {
              entityNames[membership.entity_Id] = packageData[0].packageName_En || `Package ${membership.entity_Id}`;
            }
          } else if (isPackageSeriesType(membership.entity_Type)) {
            const seriesData = await getSpaPackageSeries({ seriesId: membership.entity_Id });
            if (seriesData && seriesData.length > 0) {
              entityNames[membership.entity_Id] = seriesData[0].seriesName_En || `Series ${membership.entity_Id}`;
              // Get installment names
              if (seriesData[0].installments) {
                seriesData[0].installments.forEach(inst => {
                  installmentNamesMap[inst.installmentId] = inst.installmentName;
                });
              }
            }
          }
        } catch (error) {
          console.error('Error fetching entity data:', error);
        }
      }

      // Generate membership list HTML
      const membershipListHtml = memberships.map((membership, index) => {
        const entityName = entityNames[membership.entity_Id] || `${isPackageType(membership.entity_Type) ? 'Package' : 'Series'} ${membership.entity_Id}`;
        const totalAmount = membership.installments ? membership.installments.reduce((sum, inst) => sum + (inst.amount || 0), 0) : 0;
        const totalBilled = membership.installments ? membership.installments.reduce((sum, inst) => sum + (inst.billedAmount || inst.paidAmount || 0), 0) : 0;
        const totalBalance = totalAmount - totalBilled;

        // Generate details summary
        const detailsCount = membership.details ? membership.details.length : 0;
        const completedCount = membership.details ? membership.details.filter(d => d.completionStatus === 'Completed' || d.completionStatus === 'Complete').length : 0;
        const pendingCount = membership.details ? membership.details.filter(d => d.completionStatus === 'Pending').length : 0;

        return `
          <div class="border rounded p-3 mb-3" style="background: #f8f9fa;">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h6 class="text-primary mb-1">${entityName}</h6>
                <div class="text-muted small">
                  Type: ${isPackageType(membership.entity_Type) ? 'Package' : 'Package Series'} | 
                  Purchase Date: ${new Date(membership.purchaseDate).toLocaleDateString('en-GB')}
                </div>
              </div>
              <span class="badge ${totalBalance > 0 ? 'bg-warning' : 'bg-success'}">
                ${totalBalance > 0 ? 'Pending' : 'Paid'}
              </span>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="small">
                  <strong>Details:</strong> ${detailsCount} items | 
                  Completed: ${completedCount} | 
                  Pending: ${pendingCount}
                </div>
              </div>
              <div class="col-md-6 text-end">
                <div class="small">
                  <strong>Total:</strong> ${totalAmount.toFixed(2)} | 
                  <strong>Billed:</strong> ${totalBilled.toFixed(2)} | 
                  <strong>Balance:</strong> ${totalBalance.toFixed(2)}
                </div>
              </div>
            </div>
            ${membership.installments && membership.installments.length > 0 ? `
              <div class="mt-2">
                <strong class="small">${isPackageType(membership.entity_Type) ? 'Package Payment' : 'Installments'}:</strong>
                <div class="small mt-1">
                  ${membership.installments.map(inst => {
          // For packages (SeriesId is NULL/0), show "Package Payment" instead of "Installment 1"
          const isPackage = isPackageType(membership.entity_Type) && (!inst.seriesId || inst.seriesId === 0);
          const instName = isPackage
            ? 'Package Payment'
            : (installmentNamesMap[inst.installmentId] || `Installment ${inst.installmentId}`);
          const billed = inst.billedAmount || inst.paidAmount || 0;
          const remaining = inst.amount - billed;
          return `${instName}: ${inst.amount.toFixed(2)} (Billed: ${billed.toFixed(2)}, Remaining: ${remaining.toFixed(2)})`;
        }).join(' | ')}
                </div>
              </div>
            ` : ''}
            <button 
              class="btn btn-sm btn-info mt-2" 
              onclick="window.__viewMembershipDetails_${index}()"
              style="font-size: 0.75rem;"
            >
              View Details
            </button>
          </div>
        `;
      }).join('');

      // Store membership list data for back button functionality
      const showMembershipList = () => {
        Swal.fire({
          title: 'Customer Memberships',
          html: `
            <div style="max-height: 500px; overflow-y: auto;">
              ${membershipListHtml}
            </div>
          `,
          width: '800px',
          showConfirmButton: true,
          confirmButtonText: 'Close'
        });
      };

      // Create detailed view functions for each membership
      memberships.forEach((membership, index) => {
        window[`__viewMembershipDetails_${index}`] = async () => {
          // Close current popup
          Swal.close();

          // Show detailed view (similar to MembershipPage handleView)
          const customer = customers.find(c => c.customer_Id === membership.customerId);
          const branch = branches.find(b => b.branch_Id === membership.branchId);
          const salesman = employees.find(e => e.employee_Id === membership.salesmanId);
          const salesmanName = salesman ? `${salesman.employee_Code || ''} - ${salesman.employee_NameEn || 'N/A'}`.trim() : 'N/A';
          const entityName = entityNames[membership.entity_Id] || `${isPackageType(membership.entity_Type) ? 'Package' : 'Series'} ${membership.entity_Id}`;

          // Generate installments HTML
          const installmentsHtml = membership.installments && membership.installments.length > 0
            ? membership.installments.map(inst => {
              // For packages (SeriesId is NULL/0), show "Package Payment" instead of "Installment 1"
              const isPackage = isPackageType(membership.entity_Type) && (!inst.seriesId || inst.seriesId === 0);
              const instName = isPackage
                ? 'Package Payment'
                : (installmentNamesMap[inst.installmentId] || `Installment ${inst.installmentId}`);
              const billed = inst.billedAmount || inst.paidAmount || 0;
              return `
                  <div style="background: #fff; padding: 10px; border-radius: 6px; border: 1px solid #e9ecef; margin-bottom: 8px;">
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                      <div>
                        <span style="color: #495057; font-size: 14px; font-weight: ${isPackage ? '600' : '400'};">${instName}:</span>
                        <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${inst.amount.toFixed(2)}</span>
                      </div>
                      <div>
                        <span style="color: #6c757d; font-size: 14px;">Billed: ${billed.toFixed(2)}</span>
                        <span class="badge ${billed >= inst.amount ? 'bg-success' : 'bg-warning'} ms-2" style="font-size: 11px;">
                          ${billed >= inst.amount ? 'Billed' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                `;
            }).join('')
            : '<div style="text-align: center; color: #6c757d; padding: 20px;">No installment details available</div>';

          const totalAmount = membership.installments ? membership.installments.reduce((sum, inst) => sum + (inst.amount || 0), 0) : 0;
          const totalBilled = membership.installments ? membership.installments.reduce((sum, inst) => sum + (inst.billedAmount || inst.paidAmount || 0), 0) : 0;
          const totalBalance = totalAmount - totalBilled;

          // Create back button handler function
          const membershipId = membership.membershipId || membership.MembershipId || index;
          const backButtonId = `membershipDetailsBackBtn_${membershipId}`;
          window[`__handleBackClick_${membershipId}`] = () => {
            Swal.close();
            // Small delay to ensure smooth transition
            setTimeout(() => {
              showMembershipList();
            }, 100);
          };

          let isClosingFromBackButton = false;

          Swal.fire({
            title: `<div style="text-align: center; color: #556ee6; font-size: 18px; font-weight: bold; margin-bottom: 10px;">
                      <i class="bx bx-id-card" style="margin-right: 10px;"></i>
                      Membership Details
                    </div>`,
            html: `
              <div style="max-width: 900px; margin: 0 auto;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
                  <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #556ee6;">
                    <h6 style="color: #556ee6; margin-bottom: 8px; font-weight: bold; font-size: 16px;">
                      <i class="bx bx-info-circle" style="margin-right: 5px;"></i>
                      Basic Information
                    </h6>
                    <div style="margin-bottom: 6px;">
                      <strong style="color: #495057; font-size: 14px;">Customer:</strong>
                      <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${customer?.customer_NameEn || 'N/A'}</span>
                    </div>
                    <div style="margin-bottom: 6px;">
                      <strong style="color: #495057; font-size: 14px;">Branch:</strong>
                      <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${branch?.branch_NameEn || 'N/A'}</span>
                    </div>
                    <div style="margin-bottom: 6px;">
                      <strong style="color: #495057; font-size: 14px;">Entity:</strong>
                      <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${entityName}</span>
                    </div>
                    <div style="margin-bottom: 6px;">
                      <strong style="color: #495057; font-size: 14px;">Purchase Date:</strong>
                      <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${new Date(membership.purchaseDate).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div style="margin-bottom: 0;">
                      <strong style="color: #495057; font-size: 14px;">Salesman:</strong>
                      <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${salesmanName}</span>
                    </div>
                  </div>
                  
                  <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #28a745;">
                    <h6 style="color: #28a745; margin-bottom: 8px; font-weight: bold; font-size: 16px;">
                      <i class="bx bx-credit-card" style="margin-right: 5px;"></i>
                      ${isPackageType(membership.entity_Type) ? 'Package Payment' : 'Installments'}
                    </h6>
                    <div style="margin-bottom: 6px;">
                      <strong style="color: #495057; font-size: 14px;">Total Amount:</strong>
                      <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${totalAmount.toFixed(2)}</span>
                    </div>
                    <div style="margin-bottom: 0;">
                      <strong style="color: #495057; font-size: 14px;">Remaining Balance:</strong>
                      <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${totalBalance.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #17a2b8;">
                  <h6 style="color: #17a2b8; margin-bottom: 8px; font-weight: bold; font-size: 16px;">
                    <i class="bx bx-credit-card" style="margin-right: 5px;"></i>
                    Installment Details
                  </h6>
                  <div style="max-height: 300px; overflow-y: auto;">
                    ${installmentsHtml}
                  </div>
                </div>
                
                <div style="text-align: center; margin-top: 16px;">
                  <button
                    id="${backButtonId}"
                    type="button"
                    style="
                      background: #556ee6;
                      color: white;
                      border: none;
                      padding: 10px 22px;
                      border-radius: 6px;
                      cursor: pointer;
                      font-weight: 500;
                      font-size: 14px;
                      pointer-events: auto !important;
                      z-index: 20001;
                      position: relative;
                    "
                    onmouseover="this.style.background='#4858d4'"
                    onmouseout="this.style.background='#556ee6'"
                  >
                    <i class="bx bx-arrow-back" style="margin-right: 5px;"></i>Back
                  </button>
                </div>
              </div>
            `,
            showConfirmButton: false,
            width: '900px',
            allowOutsideClick: false,
            allowEscapeKey: true,
            didOpen: () => {
              // Attach click handler to the back button
              setTimeout(() => {
                const backBtn = document.getElementById(backButtonId);
                if (backBtn) {
                  backBtn.disabled = false;
                  backBtn.style.pointerEvents = 'auto';
                  backBtn.style.opacity = '1';
                  backBtn.style.cursor = 'pointer';
                  backBtn.style.zIndex = '20001';
                  backBtn.style.position = 'relative';

                  // Create handler using the stored function
                  const backHandler = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    isClosingFromBackButton = true;
                    if (window[`__handleBackClick_${membershipId}`]) {
                      window[`__handleBackClick_${membershipId}`]();
                    }
                  };

                  // Remove any existing listener first
                  if (backBtn._backHandler) {
                    backBtn.removeEventListener('click', backBtn._backHandler);
                  }

                  // Store handler reference for cleanup
                  backBtn._backHandler = backHandler;

                  // Add event listener with capture to ensure it fires
                  backBtn.addEventListener('click', backHandler, { capture: true });

                  // Also try mousedown as fallback
                  backBtn.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    isClosingFromBackButton = true;
                    if (window[`__handleBackClick_${membershipId}`]) {
                      window[`__handleBackClick_${membershipId}`]();
                    }
                  });
                }
              }, 100);
            },
            didClose: () => {
              // Cleanup: remove the handler function if not closing from back button
              if (!isClosingFromBackButton && window[`__handleBackClick_${membershipId}`]) {
                delete window[`__handleBackClick_${membershipId}`];
              }
            }
          });
        };
      });

      Swal.fire({
        title: 'Customer Memberships',
        html: `
          <div style="max-height: 500px; overflow-y: auto;">
            ${membershipListHtml}
          </div>
        `,
        width: '800px',
        showConfirmButton: true,
        confirmButtonText: 'Close'
      });
    } catch (error) {
      console.error('Error loading customer memberships:', error);
      Swal.fire("Error", "Failed to load customer memberships", "error");
    }
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    let netAmount = parseFloat(form.Invoice_NetAmount) || 0;
    let tipAmount = parseFloat(form.TipAmount) || 0;
    let loyaltyAmount = parseFloat(form.LoyaltyAmount) || 0;
    let cashAmount = parseFloat(form.CashAmount) || 0;
    let cashlessAmount = parseFloat(form.CashlessAmount) || 0;
    let giftCardAmount = parseFloat(form.GiftCardAmount) || 0;

    if (saveLoading) return;
    try {
      // Check day closing before saving
      if (form.Invoice_BusinessId && form.Invoice_Date) {
        try {
          const invoiceDate = new Date(form.Invoice_Date);
          const invoiceDateOnly = new Date(invoiceDate);
          invoiceDateOnly.setHours(0, 0, 0, 0);

          // Use stricter validation with previous days check
          const checkRequest = {
            transactionDate: formatDateForAPI(invoiceDateOnly, false),
            businessId: form.Invoice_BusinessId,
            branchId: form.Invoice_BranchId || null,
            checkPreviousDays: true  // Enable stricter validation
          };

          const dayClosingCheck = await checkDayClosing(checkRequest);

          // Check if selected date itself is closed
          if (dayClosingCheck.isClosed) {
            const message = dayClosingCheck.isHoliday
              ? `Holiday has been marked for ${formatDateForDisplay(invoiceDateOnly, 'date')}. Please select a different date.`
              : `Day closing has been marked for ${formatDateForDisplay(invoiceDateOnly, 'date')}. Please select a different date.`;
            Swal.fire("Day Closed", message, "warning");
            return;
          }

          // Check if any previous days are not closed (stricter validation)
          if (dayClosingCheck.firstUnclosedDate) {
            const unclosedDateStr = formatDateForDisplay(new Date(dayClosingCheck.firstUnclosedDate), 'date');
            Swal.fire("Previous Day Not Closed", `Day closing is not done for ${unclosedDateStr}. Please close that day first before creating transactions for ${formatDateForDisplay(invoiceDateOnly, 'date')}.`, "warning");
            return;
          }
        } catch (error) {
          console.error("Error checking day closing:", error);
          // Continue with save if check fails (don't block due to API error)
        }
      }

      // Validate branch selection
      if (!form.Invoice_BranchId) {
        Swal.fire("Validation Error", "Please select a branch before saving.", "error");
        return;
      }

      // Validate source selection
      if (!form.Invoice_SourceId) {
        Swal.fire("Validation Error", "Please select a source before saving.", "error");
        return;
      }

      // Check for validation errors in entity items
      const itemsWithErrors = form.entityItems.filter(item => item.validationError);

      if (itemsWithErrors.length > 0) {
        const errorMessages = itemsWithErrors.map(item => `${item.entityName}: ${item.validationError}`).join('\n');
        Swal.fire("Validation Error", `Please fix the validation errors in the selected items:\n\n${errorMessages}`, "error");
        return;
      }

      // Check for missing salesman for products, packages, and package series (skip in return mode as salesman comes from original)
      // Also skip validation for existing installments (m_InstallmentId) as salesman should come from membership
      if (!isReturnMode) {
        const itemsWithoutSalesman = form.entityItems.filter(item => {
          // Skip validation if this is an existing installment (salesman should be from membership)
          if (item.m_InstallmentId) {
            // If salesmanId is 0 or null, try to get it from membership
            if (!item.salesmanId || item.salesmanId === 0) {
              const membership = form.customerMemberships.find(m =>
                m.installments && m.installments.some(inst => inst.m_InstallmentId === item.m_InstallmentId)
              );
              if (membership) {
                const salesmanId = membership.salesmanId || membership.SalesmanId || membership.salesman_ID || membership.Salesman_ID;
                if (salesmanId) {
                  // Update the item's salesmanId
                  updateEntityItemSalesman(item.id, parseInt(salesmanId));
                  console.log('Auto-set salesman from membership for installment:', { itemId: item.id, m_InstallmentId: item.m_InstallmentId, salesmanId });
                  return false; // Don't include in validation error
                }
              }
            } else {
              return false; // Has salesmanId, skip validation
            }
          }
          return (item.type === 'product' || item.type === 'package' || item.type === 'packageSeries') && (!item.salesmanId || item.salesmanId === 0);
        });

        if (itemsWithoutSalesman.length > 0) {
          const itemNames = itemsWithoutSalesman.map(item => item.entityName).join(', ');
          const itemType = itemsWithoutSalesman[0].type === 'product' ? 'products' :
            itemsWithoutSalesman[0].type === 'package' ? 'packages' : 'package series';
          Swal.fire("Validation Error", `Please select salesman for the following ${itemType}:\n\n${itemNames}`, "error");
          return;
        }
      }

      // Validate that all services, packages, and package series being billed are active
      const inactiveEntities = [];
      for (const item of form.entityItems) {
        if (item.type === 'service') {
          let service = services.find(s => s.spaService_Id === item.entityId);
          // If service not found in array (e.g., from booking), fetch it from API
          if (!service) {
            try {
              // Fetch without status filter first, then check status in response
              const fetchedServices = await getSpaServices({ SpaService_Id: item.entityId });
              if (fetchedServices && fetchedServices.length > 0 && fetchedServices[0].status === true) {
                service = fetchedServices[0];
              }
            } catch (error) {
              console.error(`Error fetching service ${item.entityId} for validation:`, error);
            }
          }
          // Check if service exists and is active
          if (!service || service.status !== true) {
            inactiveEntities.push({ name: item.entityName, type: 'Service' });
          }
        } else if (item.type === 'package') {
          let pkg = packages.find(p => p.packageId === item.entityId);
          // If package not found in array, fetch it from API
          if (!pkg) {
            try {
              // Fetch without status filter first, then check status in response
              const fetchedPackages = await getSpaPackages({ PackageId: item.entityId });
              if (fetchedPackages && fetchedPackages.length > 0 && fetchedPackages[0].status === true) {
                pkg = fetchedPackages[0];
              }
            } catch (error) {
              console.error(`Error fetching package ${item.entityId} for validation:`, error);
            }
          }
          // Check if package exists and is active
          if (!pkg || pkg.status !== true) {
            inactiveEntities.push({ name: item.entityName, type: 'Package' });
          }
        } else if (item.type === 'packageSeries') {
          let series = packageSeries.find(s => s.seriesId === item.entityId);
          // If package series not found in array, fetch it from API
          if (!series) {
            try {
              // Fetch without status filter first, then check status in response
              const fetchedSeries = await getSpaPackageSeries({ SeriesId: item.entityId });
              if (fetchedSeries && fetchedSeries.length > 0 && fetchedSeries[0].status === true) {
                series = fetchedSeries[0];
              }
            } catch (error) {
              console.error(`Error fetching package series ${item.entityId} for validation:`, error);
            }
          }
          // Check if package series exists and is active
          if (!series || series.status !== true) {
            inactiveEntities.push({ name: item.entityName, type: 'Package Series' });
          }
        }
      }

      if (inactiveEntities.length > 0) {
        const entityList = inactiveEntities.map(e => `${e.type}: ${e.name}`).join('\n');
        Swal.fire("Validation Error", `Cannot bill inactive entities. Please remove the following inactive items:\n\n${entityList}`, "error");
        return;
      }

      // Check for package series that were selected but no installments were added
      // This happens if user closed the installment modal without selecting any installments
      // We check by looking at currentPackageSeriesForInstallments - if it has series but no installments were added
      if (form.currentPackageSeriesForInstallments && form.currentPackageSeriesForInstallments.length > 0) {
        const seriesWithoutInstallments = [];

        for (const seriesData of form.currentPackageSeriesForInstallments) {
          // Check if any installments from this series were added to entityItems
          const hasInstallmentsAdded = form.entityItems.some(item =>
            item.type === 'packageSeries' &&
            item.entityId === seriesData.seriesId &&
            item.isNewPackageSeriesInstallment
          );

          if (!hasInstallmentsAdded) {
            seriesWithoutInstallments.push(seriesData.seriesName);
          }
        }

        if (seriesWithoutInstallments.length > 0) {
          const seriesNames = seriesWithoutInstallments.join(', ');
          const confirmResult = await Swal.fire({
            icon: 'warning',
            title: 'Installment Selection Required',
            html: `Please select at least one installment to pay for the following package series:<br/><br/><strong>${seriesNames}</strong><br/><br/>Would you like to go back and select installments?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, Go Back',
            cancelButtonText: 'No, Proceed Without Installments',
            confirmButtonColor: '#556ee6',
            cancelButtonColor: '#6c757d'
          });

          if (confirmResult.isConfirmed) {
            setSaveLoading(false);
            // Reopen the membership modal
            setForm(prev => ({
              ...prev,
              showMembershipModal: true
            }));
            return;
          }
        }
      }

      // Check stock locks before save (if form has products with stock selection)
      if (temporaryFormUniqueId) {
        // Collect all stock IDs from entityItems (for return mode) and selectedStockData (for regular sales)
        const allStockIds = [];

        // Get stock IDs from entityItems (return mode)
        form.entityItems.forEach(item => {
          if (item.type === 'product' && item.stockData) {
            const stockIds = item.stockData.split(',').filter(id => id.trim() !== '');
            allStockIds.push(...stockIds);
          }
        });

        // Get stock IDs from selectedStockData (regular sales mode)
        Object.values(form.selectedStockData).forEach(stockDataArray => {
          if (Array.isArray(stockDataArray)) {
            stockDataArray.forEach(group => {
              if (group.stocks && Array.isArray(group.stocks)) {
                group.stocks.forEach(stock => {
                  const stockId = stock.purchaseStock_Id || stock.stockTransaction_StockId;
                  if (stockId) {
                    allStockIds.push(stockId.toString());
                  }
                });
              }
            });
          }
        });

        if (allStockIds.length > 0) {
          try {
            const checkResult = await checkStockSelectionLocks(
              allStockIds.join(','),
              temporaryFormUniqueId
            );

            if (!checkResult.isSuccess || checkResult.hasLocks) {
              // Stocks are locked, prevent save and refresh stock modal
              Swal.fire({
                title: "Stock Locked",
                text: checkResult.message || "Some selected stocks are no longer available. Please refresh and select different stocks.",
                icon: "error",
                confirmButtonText: "OK"
              }).then(() => {
                // Refresh stock modal for the first product with stock
                const firstProductWithStock = form.entityItems.find(item =>
                  item.type === 'product' && (item.stockData || form.selectedStockData[item.entityId])
                );
                if (firstProductWithStock) {
                  handleOpenStockModal(
                    firstProductWithStock.entityId,
                    firstProductWithStock.entityName,
                    firstProductWithStock.quantity,
                    form.entityItems.findIndex(i => i.id === firstProductWithStock.id)
                  );
                }
              });
              setSaveLoading(false);
              return;
            }
          } catch (error) {
            console.error('Error checking stock locks:', error);
            // Continue with save if check fails (don't block user)
          }
        }
      }

      setSaveLoading(true);
      const session = getUserSession();

      // Prepare invoice details from entityItems array
      const details = [];

      // Add details from entityItems array (new approach)
      form.entityItems.forEach((item, index) => {
        const entityType = item.type === 'product' ? 'pro' :
          item.type === 'service' ? 'ser' :
            item.type === 'packageSeries' ? 'sep' :
              item.type === 'giftcard' ? 'gif' : 'pac';
        const tempDetailId = index + 1;
        const detailRow = {
          Detail_Id: (item.Detail_Id && item.Detail_Id > 0) ? parseInt(item.Detail_Id) : null, // Always include Detail_Id (null for new invoices)
          TempDetailId: tempDetailId,
          EntityId: item.entityId ? parseInt(item.entityId) : 0,
          EntityType: entityType,
          Quantity: item.quantity,
          Price: item.unitPrice ?? 0, // Unit price (editable) - convert null to 0 for API
          DisPer: item.discount ?? 0, // Discount - convert null to 0 for API
          DisAmount: item.discountAmount || 0, // Use calculated discount amount
          TaxAmt: item.taxAmount || 0,
          NetAmount: item.netAmount || 0,
          Cost: item.costPrice || 0, // Cost price from default entity data
          M_InstallmentId: item.m_InstallmentId ? parseInt(item.m_InstallmentId) : null, // Add membership installment ID
          BookingResourceDetail_Id: item.bookingResourceDetailId ? parseInt(item.bookingResourceDetailId) : null, // Add booking resource detail ID for unbilled services
          Salesman_ID: item.salesmanIds && item.salesmanIds.length > 0 ? parseInt(item.salesmanIds[0]) : (item.salesmanId ? parseInt(item.salesmanId) : 0), // Add first salesman ID for backward compatibility
          SalesmanIds: item.salesmanIds && Array.isArray(item.salesmanIds) ? item.salesmanIds.map(id => parseInt(id)).filter(id => id > 0) : (item.salesmanId && item.salesmanId > 0 ? [parseInt(item.salesmanId)] : []), // Add array of salesman IDs
          StockData: item.stockData ? String(item.stockData) : null, // Add stock data for products
          CreatedBy: parseInt(session.userId),
          CreatedOn: new Date(),
          ModifiedBy: null,
          ModifiedOn: null,
        };
        details.push(detailRow);
      });

      // Prepare tax details from entityItems array
      const invoiceTaxes = [];

      // Add taxes from entityItems (new approach)
      form.entityItems.forEach((item, itemIndex) => {
        // Add tax records for items with valid tax > 0
        if (item.tax > 0) {
          // Prefer taxBreakdown if available, otherwise build from taxIds
          let taxBreakdownToUse = item.taxBreakdown;

          // If taxBreakdown doesn't exist but taxIds do, create it from the taxes state
          if ((!taxBreakdownToUse || taxBreakdownToUse.length === 0) && item.taxIds && item.taxIds.length > 0) {
            taxBreakdownToUse = item.taxIds.map(taxId => {
              const taxDef = taxes.find(t => t.tax_Id === taxId);
              if (taxDef) {
                return {
                  id: taxDef.tax_Id,
                  name: taxDef.tax_NameEn,
                  percentage: taxDef.tax_Perc || 0
                };
              }
              return null;
            }).filter(t => t !== null);
          }

          // Add each tax from breakdown
          if (taxBreakdownToUse && taxBreakdownToUse.length > 0) {
            taxBreakdownToUse.forEach(taxBreakdown => {
              const taxObj = {
                Tax_Id: taxBreakdown.id,
                TaxPer: taxBreakdown.percentage,
                TempDetailId: itemIndex + 1,  // Added: Link tax to detail using TempDetailId (matches detail's TempDetailId)
                CreatedBy: parseInt(session.userId),
                CreatedOn: new Date(),
                ModifiedBy: null,
                ModifiedOn: null,
              };
              // Only include Det_Id if it exists and is > 0 (update operation)
              if (taxBreakdown.Det_Id && taxBreakdown.Det_Id > 0) {
                taxObj.Det_Id = taxBreakdown.Det_Id;
              }
              invoiceTaxes.push(taxObj);
            });
          } else if (item.tax > 0 && (!item.taxIds || item.taxIds.length === 0)) {
            console.warn(`Skipping tax for ${item.type} ${item.entityName}: No valid Tax IDs found (tax% ${item.tax})`);
          }
        }
      });

      // For return mode, validate stock selection
      if (isReturnMode) {
        const productsWithoutStock = form.entityItems.filter(
          item => item.type === 'product' && (!item.stockData || item.stockData === '')
        );
        if (productsWithoutStock.length > 0) {
          Swal.fire("Error", "Please select stock items for all products being returned.", "error").then(() => {
            // Automatically open stock selection modal for the first product without stock
            const firstProductWithoutStock = productsWithoutStock[0];
            if (firstProductWithoutStock) {
              const itemIndex = form.entityItems.findIndex(i => i.id === firstProductWithoutStock.id);
              if (itemIndex !== -1) {
                handleOpenStockModal(
                  firstProductWithoutStock.entityId,
                  firstProductWithoutStock.entityName,
                  firstProductWithoutStock.quantity,
                  itemIndex
                );
              }
            }
          });
          setSaveLoading(false);
          return;
        }

        // Validate Cash or Cashless amount for return mode
        const cashAmount = form.CashAmount ?? 0;
        let cashlessAmount = form.CashlessAmount ?? 0;

        // If Tabee is selected in return mode, use Invoice_NetAmount as CashlessAmount
        if (form.PaymentMethod === "ta" && isReturnMode) {
          cashlessAmount = form.Invoice_NetAmount ?? 0;
        }

        // Convert to numbers and check if they are valid positive numbers
        const cashAmountNum = typeof cashAmount === 'string' ? parseFloat(cashAmount) : (cashAmount || 0);
        const cashlessAmountNum = typeof cashlessAmount === 'string' ? parseFloat(cashlessAmount) : (cashlessAmount || 0);
        const hasCashAmount = !isNaN(cashAmountNum) && cashAmountNum > 0;
        const hasCashlessAmount = !isNaN(cashlessAmountNum) && cashlessAmountNum > 0;

        if (!hasCashAmount && !hasCashlessAmount) {
          Swal.fire("Error", "Please enter either Cash Amount or Cashless Amount for the sales return.", "error");
          setSaveLoading(false);
          return;
        }
      }

      // Validate: If customer has advance, invoice type must be Credit
      if (customerAdvanceBalance && form.Invoice_Type === "ca") {
        const advanceAmount = customerAdvanceBalance.availableAdvance || customerAdvanceBalance.AvailableAdvance || 0;
        if (advanceAmount > 0) {
          Swal.fire({
            icon: "warning",
            title: "Advance Amount Available",
            html: `Customer has an advance amount of ${currencyCode} ${advanceAmount.toFixed(2)}.<br/><br/>
                   Please select <strong>Credit</strong> as invoice type to utilize the advance amount.`,
            confirmButtonText: "OK"
          });
          setSaveLoading(false);
          return;
        }
      }

      // Validate cash/cashless amounts
      if (form.Invoice_Type === "ca" && form.Invoice_NetAmount > 0) {
        // Use the variables defined at the top of handleSave

        // If Tabee is selected in return mode, use Invoice_NetAmount as CashlessAmount
        if (form.PaymentMethod === "ta" && isReturnMode) {
          cashlessAmount = form.Invoice_NetAmount ?? 0;
        }

        const totalPayment = cashAmount + cashlessAmount;
        // netAmount is already defined at top

        if (isReturnMode) {
          // Return mode: Cash + Cashless must equal Net Amount
          const roundedTotalPayment = Math.round(totalPayment * 100) / 100;
          const roundedNetAmount = Math.round(netAmount * 100) / 100;

          if (roundedTotalPayment !== roundedNetAmount) {
            Swal.fire({
              icon: "error",
              title: "Error",
              html: `Cash Amount + Cashless Amount must equal Final Net Amount.<br/><br/>
                     <strong>Final Net Amount:</strong> ${netAmount.toFixed(2)}<br/>
                     <strong>Cash Amount:</strong> ${cashAmount.toFixed(2)}<br/>
                     <strong>Cashless Amount:</strong> ${cashlessAmount.toFixed(2)}<br/>
                     <strong>Total Payment:</strong> ${totalPayment.toFixed(2)}`,
              confirmButtonText: "OK"
            });
            setSaveLoading(false);
            return;
          }

          // Show confirmation dialog for return
          const confirmHtml = `Please confirm the sales return details:<br/><br/>
             <strong>Final Net Amount:</strong> ${netAmount.toFixed(2)}<br/>
             <strong>Cash Amount:</strong> ${cashAmount.toFixed(2)}<br/>
             <strong>Cashless Amount:</strong> ${cashlessAmount.toFixed(2)}<br/>
             <strong>Total Payment:</strong> ${totalPayment.toFixed(2)}`;

          const confirmResult = await Swal.fire({
            icon: "question",
            title: "Confirm Sales Return Save",
            html: confirmHtml,
            showCancelButton: true,
            confirmButtonText: "Confirm & Save",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#556ee6",
            cancelButtonColor: "#6c757d"
          });

          if (!confirmResult.isConfirmed) {
            setSaveLoading(false);
            return;
          }
        } else {
          // Normal mode: Cash + Cashless + Loyalty + GiftCard must equal Net Amount + Tip
          const totalPaid = cashAmount + cashlessAmount + loyaltyAmount + giftCardAmount;
          const expectedTotal = netAmount + tipAmount;

          if (Math.abs(totalPaid - expectedTotal) > 0.01) {
            Swal.fire({
              icon: "error",
              title: "Payment Mismatch",
              html: `Total payment must equal Net Amount + Tip.<br/><br/>
                     <strong>Net Amount:</strong> ${netAmount.toFixed(2)}<br/>
                     <strong>Tip Amount:</strong> ${tipAmount.toFixed(2)}<br/>
                     <strong>Required:</strong> ${expectedTotal.toFixed(2)}<br/>
                     <hr/>
                     <strong>Cash:</strong> ${cashAmount.toFixed(2)}<br/>
                     <strong>Cashless:</strong> ${cashlessAmount.toFixed(2)}<br/>
                     ${loyaltyAmount > 0 ? `<strong>Loyalty:</strong> ${loyaltyAmount.toFixed(2)}<br/>` : ''}
                     ${giftCardAmount > 0 ? `<strong>Gift Card:</strong> ${giftCardAmount.toFixed(2)}<br/>` : ''}
                     <strong>Total Paid:</strong> ${totalPaid.toFixed(2)}`,
              confirmButtonText: "OK"
            });
            setSaveLoading(false);
            return;
          }

          // Show confirmation dialog with amounts
          const displayCashlessAmount = cashlessAmount;
          const displayTotalPayment = totalPaid;
          const confirmHtml = (tipAmount > 0 || loyaltyAmount > 0 || giftCardAmount > 0)
            ? `Please confirm the invoice details:<br/><br/>
               <strong>Final Net Amount:</strong> ${netAmount.toFixed(2)}<br/>
               ${tipAmount > 0 ? `<strong>Tip Amount:</strong> ${tipAmount.toFixed(2)}<br/>` : ''}
               ${loyaltyAmount > 0 ? `<strong>Loyalty Redeemed:</strong> ${loyaltyAmount.toFixed(2)}<br/>` : ''}
               ${giftCardAmount > 0 ? `<strong>Gift Card Redeemed:</strong> ${giftCardAmount.toFixed(2)}<br/>` : ''}
               <hr/>
               <strong>Cash Amount:</strong> ${cashAmount.toFixed(2)}<br/>
               <strong>Cashless Amount:</strong> ${displayCashlessAmount.toFixed(2)}<br/>
               <strong>Total Payment:</strong> ${displayTotalPayment.toFixed(2)}`
            : `Please confirm the invoice details:<br/><br/>
               <strong>Final Net Amount:</strong> ${netAmount.toFixed(2)}<br/>
               <strong>Cash Amount:</strong> ${cashAmount.toFixed(2)}<br/>
               <strong>Cashless Amount:</strong> ${displayCashlessAmount.toFixed(2)}<br/>
               <strong>Total Payment:</strong> ${displayTotalPayment.toFixed(2)}`;

          const confirmResult = await Swal.fire({
            icon: "question",
            title: "Confirm Invoice Save",
            html: confirmHtml,
            showCancelButton: true,
            confirmButtonText: "Confirm & Save",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#556ee6",
            cancelButtonColor: "#6c757d"
          });

          if (!confirmResult.isConfirmed) {
            setSaveLoading(false);
            return;
          }
        }
      }

      // Combine selected date with current time (client's local timezone)
      // This ensures the time reflects when the user is creating the invoice in their timezone
      const selectedDate = form.Invoice_Date instanceof Date ? form.Invoice_Date : new Date(form.Invoice_Date);
      const now = new Date();
      const invoiceDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );

      // Prepare save data - remove ID field for inserts
      const saveData = {
        Invoice_No: form.Invoice_No ? String(form.Invoice_No) : "", // Convert to string (empty string for new invoices, will be generated by backend)
        Invoice_Date: formatDateForAPI(invoiceDateTime, true), // Selected date + current time (client timezone)
        Invoice_DueDate: null,
        Invoice_CustomerId: form.Invoice_CustomerId,
        Invoice_BusinessId: form.Invoice_BusinessId,
        Invoice_BranchId: form.Invoice_BranchId,
        Invoice_TotalQty: form.Invoice_TotalQty,
        Invoice_TotalAmount: form.Invoice_TotalAmount,
        Invoice_DisAmount: form.Invoice_DisAmount,
        Invoice_TaxAmount: form.Invoice_TaxAmount,
        Invoice_NetAmount: form.Invoice_NetAmount,
        Invoice_Type: form.Invoice_Type,
        PaidAmount: isReturnMode ? netAmount : (form.Invoice_Type === "ca" ? (netAmount + tipAmount) : 0),
        CashAmount: form.CashAmount == null || form.CashAmount === '' ? 0 : cashAmount,
        CashlessAmount: (form.PaymentMethod === "ta" && isReturnMode && (form.CashlessAmount == null || form.CashlessAmount === ''))
          ? (netAmount || 0)
          : (form.PaymentMethod === "ta" && !isReturnMode && (form.CashlessAmount == null || form.CashlessAmount === ''))
            ? (netAmount + tipAmount - cashAmount - loyaltyAmount)
            : (form.CashlessAmount == null || form.CashlessAmount === '' ? 0 : cashlessAmount),
        PaymentMethod: form.PaymentMethod,
        AuthCode: form.AuthCode,
        TipAmount: form.TipAmount || 0,
        CostAmount: form.CostAmount,
        TotalCost: form.TotalCost,
        OverallDiscountPercentage: form.overallDiscountPercentage ?? 0,
        OverallDiscountAmount: form.overallDiscountAmount,
        CreatedBy: parseInt(session.userId),
        ModifiedBy: null,
        CreatedDate: formatDateForAPI(new Date(), true), // Use same format as Invoice_Date (local time, no timezone conversion)
        ModifiedDate: null,
        Details: details,
        Taxes: invoiceTaxes,
        // Return fields
        IsReturn: isReturnMode ? true : false,
        Return_Sales_Invoice_Id: isReturnMode ? returnSalesInvoiceId : null,
        // Include Booking_Id ONLY when actually coming from Booking page (isFromBooking flag)
        // This prevents sending stale bookingId after form is cleared and new invoice is created
        Booking_Id: (isFromBooking && location.state?.bookingId) ? location.state.bookingId : null,
        // Loyalty fields
        PointsRedeemed: form.PointsRedeemed || 0,
        PointsAmount: form.PointsAmount || 0,
        LoyaltyId: activeLoyaltyConfig?.loyaltyPoints_Id || null,
        ExpiryDate: activeLoyaltyConfig?.to_Date || null,
        PointsAdded: isReturnMode ? 0 : (form.PointsAdded || 0), // Return invoices never add points
        Invoice_SourceId: form.Invoice_SourceId || null,
        GiftCardAmount: form.GiftCardAmount || 0,
        RedeemedGiftCardCode: form.RedeemedGiftCardCode || null,
      };

      // Only include Invoice_Id if it exists and is > 0 (update operation)
      if (form.Invoice_Id && form.Invoice_Id > 0) {
        saveData.Invoice_Id = form.Invoice_Id;
      }

      // Group all packages (regular and series) - create membership AFTER invoice save
      // This avoids orphaned memberships if invoice fails
      // Note: M_InstallmentId is not needed before invoice save - if invoice fails, nothing is saved anyway
      // The stored procedure can find installments by matching CustomerId + BranchId + EntityId + InstallmentId
      // UPDATED: Track quantity for each unique package to create multiple memberships (Option A)
      const uniquePackages = {}; // All packages - create membership AFTER invoice save

      form.entityItems.forEach(item => {
        if (item.type === 'package' && !item.m_InstallmentId && !item.installmentId) {
          // Regular package
          const key = `${item.type}_${item.entityId}`;
          if (!uniquePackages[key]) {
            // Store item with quantity for creating multiple memberships
            uniquePackages[key] = {
              ...item,
              quantity: parseInt(item.quantity) || 1  // Ensure quantity is a number, default to 1
            };
          } else {
            // If same package appears multiple times, aggregate quantity
            uniquePackages[key].quantity = (uniquePackages[key].quantity || 1) + (parseInt(item.quantity) || 1);
          }
        } else if (item.type === 'packageSeries' && item.isNewPackageSeriesInstallment === true) {
          // Package series installment
          const seriesEntityData = item.seriesEntityData || packageSeries.find(s => s.seriesId === item.entityId);
          if (seriesEntityData) {
            const key = `packageSeries_${item.entityId}`;
            if (!uniquePackages[key]) {
              const itemWithSeriesData = {
                ...item,
                seriesEntityData: seriesEntityData,
                quantity: parseInt(item.quantity) || 1  // Ensure quantity is a number, default to 1
              };
              uniquePackages[key] = itemWithSeriesData;
            } else {
              // If same package series appears multiple times, aggregate quantity
              uniquePackages[key].quantity = (uniquePackages[key].quantity || 1) + (parseInt(item.quantity) || 1);
            }
          }
        }
      });

      // STEP 1: Save invoice FIRST (before creating any memberships)
      // This ensures no orphaned memberships if invoice fails

      // Always use FormData (similar to AddReceiptPage) to ensure consistent handling
      const formData = new FormData();

      // Append all invoice fields
      Object.keys(saveData).forEach(key => {
        if (key === 'Details' || key === 'Taxes') {
          // Serialize Details and Taxes as JSON strings
          formData.append(key === 'Details' ? 'invoiceDetailsJson' : 'taxesJson', JSON.stringify(saveData[key]));
        } else if (saveData[key] !== null && saveData[key] !== undefined) {
          // Convert dates to ISO string for FormData
          if (saveData[key] instanceof Date) {
            formData.append(key, saveData[key].toISOString());
          } else {
            formData.append(key, saveData[key]);
          }
        }
      });

      // Only append documentFile if a document is selected
      if (form.Invoice_Document) {
        formData.append('documentFile', form.Invoice_Document);
      }

      const invoiceDataToSend = formData;

      const result = await saveSalesInvoice(invoiceDataToSend);

      // Handle the response object
      const isSuccess = result?.isSuccess || false;
      const message = result?.message || "Failed to save invoice";
      const returnedId = result?.returnedId || 0;

      if (!isSuccess) {
        // Invoice failed - no memberships created, so no cleanup needed
        Swal.fire("Error", message, "error");
        setSaveLoading(false);
        return; // Stop execution
      }

      // STEP 2: Invoice saved successfully - NOW create memberships for ALL packages
      // Both regular packages and package series are created AFTER invoice save
      // The stored procedure will update the membership installments when it finds them
      // UPDATED: Create multiple memberships based on quantity (Option A: One membership per quantity)
      if (Object.keys(uniquePackages).length > 0 && form.Invoice_CustomerId && form.Invoice_BranchId) {
        for (const [key, item] of Object.entries(uniquePackages)) {
          try {
            const session = getUserSession();
            const entityType = item.type === 'package' ? 'pac' : 'sep';

            // Get quantity for this package (default to 1 if not set)
            const quantity = parseInt(item.quantity) || 1;

            // For package series, get salesman from the first installment item of this series
            let salesmanId = item.salesmanId || null;
            if (item.type === 'packageSeries' && !salesmanId) {
              const firstInstallmentItem = form.entityItems.find(ei =>
                ei.type === 'packageSeries' &&
                ei.entityId === item.entityId &&
                ei.isNewPackageSeriesInstallment &&
                ei.salesmanId
              );
              if (firstInstallmentItem) {
                salesmanId = firstInstallmentItem.salesmanId;
              }
            }

            // Create N memberships where N = quantity (Option A: Multiple memberships approach)
            console.log(`Creating ${quantity} membership(s) for ${entityType === 'pac' ? 'regular package' : 'package series'} ${item.entityId} (quantity: ${quantity})`);

            for (let i = 0; i < quantity; i++) {
              // Get all salesman IDs from the item (for multi-salesman support)
              const salesmanIds = item.salesmanIds && Array.isArray(item.salesmanIds) && item.salesmanIds.length > 0
                ? item.salesmanIds
                : (item.salesmanId && item.salesmanId > 0 ? [item.salesmanId] : []);

              const membershipData = {
                MembershipId: 0,
                CustomerId: form.Invoice_CustomerId,
                BranchId: form.Invoice_BranchId,
                Entity_Id: item.entityId,
                Entity_Type: entityType,
                PurchaseDate: formatDateForAPI(form.Invoice_Date instanceof Date ? form.Invoice_Date : new Date(form.Invoice_Date), false),
                SalesmanId: salesmanId, // Kept for backward compatibility (will be ignored by stored procedure)
                SalesmanIds: salesmanIds, // Array of all salesman IDs for multi-salesman support
                InvoiceId: returnedId,  // Pass Invoice_Id to copy completion status from booking
                CreatedBy: session.userId,
                ModifiedBy: session.userId,
                CreatedOn: new Date().toISOString(),
                ModifiedOn: new Date().toISOString()
              };

              console.log(`Creating membership ${i + 1} of ${quantity} for ${entityType === 'pac' ? 'regular package' : 'package series'} (after invoice save):`, membershipData);
              const membershipResult = await saveCustomerMembership(membershipData);
              const membershipId = membershipResult?.id || membershipResult?.returnedId;
              if (membershipId) {
                console.log(`Created membership ${membershipId} (${i + 1}/${quantity}) for ${entityType === 'pac' ? 'package' : 'package series'} ${item.entityId} after invoice save`);
                // Note: The stored procedure will update the membership installments during invoice save
                // For regular packages: Matches by CustomerId + BranchId + EntityId + SeriesId IS NULL + InstallmentId = 1
                // For package series: Uses M_InstallmentId if provided, or matches by other criteria
                // IMPORTANT: With multiple memberships, the backend will match the FIRST unpaid installment
                // for each membership when updating BilledAmount
              } else {
                console.warn(`No membership ID returned for ${entityType} ${item.entityId} (membership ${i + 1}/${quantity})`);
              }
            }
          } catch (error) {
            console.error(`Error creating membership(s) for ${item.entityName || item.label} after invoice save:`, error);
            // Don't fail the whole operation - invoice is already saved
            // Just log the error - membership can be created manually if needed
          }
        }
      }

      if (isSuccess) {
        // Cleanup stock locks after successful save
        if (temporaryFormUniqueId) {
          try {
            await deleteStockSelectionLock(temporaryFormUniqueId);
          } catch (error) {
            console.error('Error cleaning up stock locks after save:', error);
          }
        }

        Swal.fire("Success", isReturnMode ? "Sales return created successfully!" : "Invoice saved successfully!", "success").then(async () => {
          // Store current customer ID before reset
          const currentCustomerId = form.Invoice_CustomerId;

          // Clear navigation state to prevent stale bookingId from being sent in future saves
          if (location.state?.bookingId || location.state?.customerId) {
            window.history.replaceState({}, document.title);
          }

          resetForm();
          // Removed loadInvoices() - invoice list is on separate page (/GmsBillingList)
          // Invoice number will be generated automatically via useEffect when BusinessId is set
          window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top after save

          // If customer was selected, refresh unbilled services and clear billable items flag
          if (currentCustomerId) {
            try {
              const [memberships, unbilledServicesData] = await Promise.all([
                getCustomerMemberships({ customerId: currentCustomerId }),
                getCustomerUnbilledServices({
                  CustomerId: currentCustomerId,
                  BranchId: null
                }).catch(err => {
                  console.error('Error fetching unbilled services after save:', err);
                  return [];
                })
              ]);

              // Check if there are still billable items
              const hasUnpaidInstallments = memberships && memberships.some(membership => {
                if (!membership.installments || membership.installments.length === 0) {
                  return false;
                }
                return membership.installments.some(installment => {
                  const billedAmount = installment.billedAmount || installment.paidAmount || 0;
                  const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
                  return remainingAmount > 0;
                });
              });

              const hasUnbilledServices = unbilledServicesData && unbilledServicesData.length > 0;
              const hasBillableItems = hasUnpaidInstallments || hasUnbilledServices;

              // Update billable items flag
              setCustomerHasBillableItems(hasBillableItems);
            } catch (error) {
              console.error('Error refreshing unbilled services after save:', error);
              // Clear the flag anyway to avoid stale state
              setCustomerHasBillableItems(false);
            }
          } else {
            // No customer selected, clear the flag
            setCustomerHasBillableItems(false);
          }

          // Automatically open print modal after save (only for regular sales invoices, not sales returns)
          if (!isReturnMode && returnedId && returnedId > 0) {
            try {
              setPrintLoading(true);
              // Fetch invoice data for print using the same function as handlePrintInvoice
              const invoiceData = await getInvoiceForPrint(returnedId);

              if (invoiceData && invoiceData.header) {
                setInvoicePrintData(invoiceData);
                setShowInvoicePrintModal(true);
              }
              setPrintLoading(false);
            } catch (error) {
              console.error("Error loading invoice for print after save:", error);
              setPrintLoading(false);
              // Don't show error to user as save was successful, just log it
            }
          }
        });
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      Swal.fire("Error", "Failed to save invoice", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = async (item) => {
    // Helper function to get entity name
    const getEntityName = (entityType, entityId) => {
      if (!entityId) return 'N/A';

      // Convert entityId to number for comparison
      const entityIdNum = parseInt(entityId, 10);

      if (entityType === 'pro') {
        const product = products.find(p => {
          const pId = parseInt(p.product_Id || p.productId || p.id || 0, 10);
          return pId === entityIdNum && pId > 0;
        });
        return product ? (product.product_NameEn || product.productName_En || product.name || `Product ID: ${entityId}`) : `Product ID: ${entityId}`;
      } else if (entityType === 'ser') {
        const service = services.find(s => {
          const sId = parseInt(s.spaService_Id || s.spaServiceId || s.id || 0, 10);
          return sId === entityIdNum && sId > 0;
        });
        return service ? (service.spaServiceName_En || service.spaServiceNameEn || service.name || `Service ID: ${entityId}`) : `Service ID: ${entityId}`;
      } else if (entityType === 'pac') {
        const pkg = packages.find(p => {
          const pId = parseInt(p.packageId || p.package_Id || p.id || 0, 10);
          return pId === entityIdNum && pId > 0;
        });
        return pkg ? (pkg.packageName_En || pkg.packageNameEn || pkg.name || `Package ID: ${entityId}`) : `Package ID: ${entityId}`;
      } else if (entityType === 'sep') {
        const series = packageSeries.find(s => {
          const sId = parseInt(s.seriesId || s.series_Id || s.id || 0, 10);
          return sId === entityIdNum && sId > 0;
        });
        return series ? (series.seriesName_En || series.seriesNameEn || series.name || `Package Series ID: ${entityId}`) : `Package Series ID: ${entityId}`;
      }
      return `Entity ID: ${entityId}`;
    };

    // Process invoice details to populate form arrays
    const selectedProducts = [];
    const selectedServices = [];
    const selectedPackages = [];
    const selectedPackageSeries = [];
    const productQuantities = {};
    const serviceQuantities = {};
    const packageQuantities = {};
    const packageSeriesQuantities = {};
    const productPrices = {};
    const servicePrices = {};
    const packagePrices = {};
    const packageSeriesPrices = {};
    const productDiscounts = {};
    const serviceDiscounts = {};
    const packageDiscounts = {};
    const packageSeriesDiscounts = {};
    const productTaxes = {};
    const serviceTaxes = {};
    const packageTaxes = {};
    const packageSeriesTaxes = {};
    const entityItems = [];

    if (item.details && item.details.length > 0) {
      item.details.forEach(detail => {
        if (detail.entityType === 'pro') {
          selectedProducts.push(detail.entityId);
          productQuantities[detail.entityId] = detail.quantity || 0;
          productPrices[detail.entityId] = detail.price || 0;
          productDiscounts[detail.entityId] = detail.disPer || 0;
          productTaxes[detail.entityId] = detail.taxAmt || 0;
          entityItems.push({
            id: detail.entityId,
            entityId: detail.entityId,
            type: 'product',
            entityType: 'pro',
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: detail.taxAmt || 0,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            entityName: getEntityName('pro', detail.entityId),
            m_InstallmentId: detail.m_InstallmentId || null
          });
        } else if (detail.entityType === 'ser') {
          selectedServices.push(detail.entityId);
          serviceQuantities[detail.entityId] = detail.quantity || 0;
          servicePrices[detail.entityId] = detail.price || 0;
          serviceDiscounts[detail.entityId] = detail.disPer || 0;
          serviceTaxes[detail.entityId] = detail.taxAmt || 0;
          entityItems.push({
            id: detail.entityId,
            entityId: detail.entityId,
            type: 'service',
            entityType: 'ser',
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: detail.taxAmt || 0,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            entityName: getEntityName('ser', detail.entityId),
            m_InstallmentId: detail.m_InstallmentId || null
          });
        } else if (detail.entityType === 'pac') {
          selectedPackages.push(detail.entityId);
          packageQuantities[detail.entityId] = detail.quantity || 0;
          packagePrices[detail.entityId] = detail.price || 0;
          packageDiscounts[detail.entityId] = detail.disPer || 0;
          packageTaxes[detail.entityId] = detail.taxAmt || 0;
          entityItems.push({
            id: detail.entityId,
            entityId: detail.entityId,
            type: 'package',
            entityType: 'pac',
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: detail.taxAmt || 0,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            entityName: getEntityName('pac', detail.entityId),
            m_InstallmentId: detail.m_InstallmentId || null
          });
        } else if (detail.entityType === 'sep') {
          selectedPackageSeries.push(detail.entityId);
          packageSeriesQuantities[detail.entityId] = detail.quantity || 0;
          packageSeriesPrices[detail.entityId] = detail.price || 0;
          packageSeriesDiscounts[detail.entityId] = detail.disPer || 0;
          packageSeriesTaxes[detail.entityId] = detail.taxAmt || 0;
          entityItems.push({
            id: detail.entityId,
            entityId: detail.entityId,
            type: 'packageSeries',
            entityType: 'sep',
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: detail.taxAmt || 0,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            entityName: getEntityName('sep', detail.entityId),
            m_InstallmentId: detail.m_InstallmentId || null
          });
        }
      });
    }

    setForm({
      ...item,
      Invoice_Id: item.invoice_Id || item.Invoice_Id,
      Invoice_No: (item.invoice_No || item.Invoice_No) ? String(item.invoice_No || item.Invoice_No) : "",
      Invoice_Date: new Date(item.invoice_Date || item.Invoice_Date),
      Invoice_CustomerId: item.invoice_CustomerId || item.Invoice_CustomerId,
      Invoice_BusinessId: item.invoice_BusinessId || item.Invoice_BusinessId,
      Invoice_BranchId: item.invoice_BranchId || item.Invoice_BranchId,
      Invoice_TotalQty: item.invoice_TotalQty || item.Invoice_TotalQty,
      Invoice_TotalAmount: item.invoice_TotalAmount || item.Invoice_TotalAmount,
      Invoice_DisAmount: item.invoice_DisAmount || item.Invoice_DisAmount,
      Invoice_TaxAmount: item.invoice_TaxAmount || item.Invoice_TaxAmount,
      Invoice_NetAmount: item.invoice_NetAmount || item.Invoice_NetAmount,
      Invoice_Type: item.invoice_Type || item.Invoice_Type,
      PaidAmount: item.paidAmount || item.PaidAmount,
      AmountPaidByCustomer: item.amountPaidByCustomer || item.AmountPaidByCustomer || (item.paidAmount || item.PaidAmount || 0),
      TipAmount: item.tipAmount || item.TipAmount || 0,
      CashAmount: item.cashAmount || item.CashAmount,
      CashlessAmount: item.cashlessAmount || item.CashlessAmount,
      PaymentMethod: item.paymentMethod || item.PaymentMethod,
      AuthCode: item.authCode || item.AuthCode,
      CostAmount: item.costAmount || item.CostAmount,
      TotalCost: item.totalCost || item.TotalCost,
      selectedProducts,
      selectedServices,
      selectedPackages,
      selectedPackageSeries,
      productQuantities,
      serviceQuantities,
      packageQuantities,
      packageSeriesQuantities,
      productPrices,
      servicePrices,
      packagePrices,
      packageSeriesPrices,
      productDiscounts,
      serviceDiscounts,
      packageDiscounts,
      packageSeriesDiscounts,
      productTaxes,
      serviceTaxes,
      packageTaxes,
      packageSeriesTaxes,
      entityItems,
      showEntityModal: false,
      selectedModalItems: {},
      modalEntityType: '',
      modalSearchTerm: '',
      dropdownOpen: {},
      addButtonDropdownOpen: false,
      showMembershipModal: false,
      selectedMembershipInstallments: {},
      customerMemberships: [],
      installmentNames: {},
      entityNames: {},
      newPackageSeriesInstallments: {},
      currentPackageSeriesForInstallments: [],
    });

    // Load customer memberships if there are installment items
    const hasInstallments = entityItems.some(item => item.m_InstallmentId);
    if (hasInstallments && (item.invoice_CustomerId || item.Invoice_CustomerId)) {
      try {
        const customerId = item.invoice_CustomerId || item.Invoice_CustomerId;
        const response = await getCustomerMemberships({ customerId: customerId });
        setForm(prev => ({
          ...prev,
          customerMemberships: response || []
        }));
      } catch (error) {
        console.error('Error loading customer memberships for edit:', error);
      }
    }

    setIsEditing(true);
  };

  const handleSalesReturn = async (invoice) => {
    try {
      setLoading(true);
      const invoiceData = await getSalesInvoiceForReturn(invoice.invoice_Id || invoice.Invoice_Id);

      // Handle different response structures - API might return the data directly or wrapped
      let invoiceDataToUse = invoiceData;
      if (invoiceData && typeof invoiceData === 'object') {
        // If response has a data property, use it
        if (invoiceData.data) {
          invoiceDataToUse = invoiceData.data;
        }
        // If response is an array, take first item
        if (Array.isArray(invoiceData) && invoiceData.length > 0) {
          invoiceDataToUse = invoiceData[0];
        }
      }

      if (!invoiceDataToUse || (!invoiceDataToUse.invoice_Id && !invoiceDataToUse.Invoice_Id)) {
        console.error("Invalid invoice data for return:", invoiceDataToUse);
        Swal.fire("Error", "Failed to load invoice for return: Invalid data received", "error");
        setLoading(false);
        return;
      }

      // Load details for return:
      // - Products require available stock details
      // - Services/packages/package-series do not require stock details
      const details = invoiceDataToUse.details || invoiceDataToUse.Details || [];

      // Ensure products and services are loaded for name resolution if needed
      let currentProducts = products;
      let currentServices = services;
      const needsProducts = details.some(d => (d.entityId || d.EntityId) && (d.entityType || d.EntityType) === 'pro');
      const needsServices = details.some(d => (d.entityId || d.EntityId) && (d.entityType || d.EntityType)?.toLowerCase() === 'ser');

      if ((needsProducts && currentProducts.length === 0) || (needsServices && currentServices.length === 0)) {
        console.log('🔄 Loading missing dependencies for return name resolution...', { needsProducts, needsServices });
        const [pData, sData] = await Promise.all([
          (needsProducts && currentProducts.length === 0) ? getProducts({ status: true }) : Promise.resolve(currentProducts),
          (needsServices && currentServices.length === 0) ? getSpaServices({ status: true }) : Promise.resolve(currentServices)
        ]);

        if (needsProducts && currentProducts.length === 0) {
          currentProducts = pData || [];
          setProducts(currentProducts);
        }
        if (needsServices && currentServices.length === 0) {
          currentServices = (sData || []).filter(service => service.status === true);
          setServices(currentServices);
        }
      }

      const entityItems = [];

      if (details && Array.isArray(details) && details.length > 0) {
        details.forEach((detail, index) => {
          // Check if it's a product and has stock details
          const entityType = detail.entityType || detail.EntityType;
          const stockDetails = detail.stockDetails || detail.StockDetails || [];

          if (entityType === 'pro' && stockDetails && stockDetails.length > 0) {
            // Map stock details to include all necessary fields for stock selection modal
            const mappedStockDetails = stockDetails.map(stock => ({
              purchaseStock_Id: stock.stockTransaction_StockId || stock.purchaseStock_Id,
              purchaseStock_Product_Id: detail.entityId || detail.EntityId,
              purchaseStock_Unit_Id: stock.purchaseStock_Unit_Id || null,
              purchaseStock_BatchNo: stock.purchaseStock_BatchNo || stock.BatchNo,
              purchaseStock_ExpDate: stock.purchaseStock_ExpDate || stock.ExpDate,
              purchaseStock_IsAvailable: stock.purchaseStock_IsAvailable !== undefined ? stock.purchaseStock_IsAvailable : false,
              purchaseStock_SalesPrice: stock.purchaseStock_SalesPrice || stock.SalesPrice || detail.price || detail.Price,
              purchaseStock_CostPrice: stock.purchaseStock_CostPrice || stock.CostPrice || detail.cost || detail.Cost,
              purchaseStock_IsBonus: stock.purchaseStock_IsBonus !== undefined ? stock.purchaseStock_IsBonus : false,
              purchaseStock_Branch_Id: stock.purchaseStock_Branch_Id || invoiceDataToUse.invoice_BranchId || invoiceDataToUse.Invoice_BranchId
            }));

            // Only add products that have stock available for return
            const productId = detail.entityId || detail.EntityId;
            const productIdNum = parseInt(productId, 10);
            const product = currentProducts.find(p => {
              const pId = parseInt(p.product_Id || p.productId || p.id || 0, 10);
              return pId === productIdNum && pId > 0;
            });
            const productName =
              product
                ? (product.product_NameEn || product.productName_En || product.name || `Product ${productId}`)
                : `Product ${productId}`;

            // Extract salesmanIds from API response
            const extractedSalesmanIds = detail.salesmanIds && Array.isArray(detail.salesmanIds)
              ? detail.salesmanIds.map(id => parseInt(id)).filter(id => id > 0)
              : [];

            const entityItem = {
              id: index + 1,
              entityId: productId,
              entityType: 'pro',
              type: 'product',
              entityName: productName,
              originalQuantity: detail.quantity || detail.Quantity || 0, // Original billed qty
              quantity: 0, // Return qty (set via stock selection)
              unitPrice: detail.price || detail.Price || 0,
              price: detail.price || detail.Price || 0,
              discount: detail.disPer || detail.DisPer || 0,
              discountAmount: 0,
              tax: parseFloat((detail.taxPercent || detail.TaxPercent || "0")?.toString().split(',')[0]) || 0,
              taxAmount: 0,
              netAmount: 0,
              cost: detail.cost || detail.Cost || 0,
              salesmanId: detail.salesman_ID || detail.Salesman_ID || 0,
              salesmanIds: extractedSalesmanIds, // Extract salesmanIds array from API response
              stockDetails: mappedStockDetails, // Use mapped stock details
              stockData: null // Will be set when user selects stocks
            };
            entityItems.push(entityItem);
          }

          // Services: returnable without stock details
          if (entityType && (entityType.toLowerCase() === 'ser' || entityType.toUpperCase() === 'SER')) {
            // Look up service name from services array
            const serviceId = detail.entityId || detail.EntityId;
            const service = currentServices.find(s => {
              const sId = parseInt(s.spaService_Id || s.spaServiceId || s.id || 0, 10);
              const entityIdNum = parseInt(serviceId, 10);
              return sId === entityIdNum && sId > 0;
            });
            const serviceName = service
              ? (service.spaServiceName_En || service.spaServiceNameEn || service.name || `Service ${serviceId}`)
              : (detail.service_NameEn || detail.Service_NameEn || detail.spaServiceName_En || detail.SpaServiceName_En || `Service ${serviceId}`);

            // Extract salesmanIds from API response
            const extractedSalesmanIds = detail.salesmanIds && Array.isArray(detail.salesmanIds)
              ? detail.salesmanIds.map(id => parseInt(id)).filter(id => id > 0)
              : [];

            const entityItem = {
              id: `ser_${index + 1}`,
              entityId: serviceId,
              entityType: 'ser',
              type: 'service',
              entityName: serviceName,
              originalQuantity: detail.quantity || detail.Quantity || 0, // Original billed qty
              quantity: 0, // Return qty (user-entered)
              unitPrice: detail.price || detail.Price || 0,
              price: detail.price || detail.Price || 0,
              discount: detail.disPer || detail.DisPer || 0,
              discountAmount: 0,
              tax: parseFloat((detail.taxPercent || detail.TaxPercent || "0")?.toString().split(',')[0]) || 0,
              taxAmount: 0,
              netAmount: 0,
              cost: detail.cost || detail.Cost || 0,
              salesmanId: detail.salesman_ID || detail.Salesman_ID || 0,
              salesmanIds: extractedSalesmanIds, // Extract salesmanIds array from API response
              stockDetails: [], // Not applicable for services
              stockData: null // Not applicable for services
            };
            entityItems.push(entityItem);
          }
        });
      }

      // Check if there are any returnable items before proceeding
      if (entityItems.length === 0) {
        Swal.fire("Info", "No returnable items found in this invoice.", "info");
        setLoading(false);
        return; // Exit early - don't set return mode or redirect
      }

      // Reset form and set return mode (only if we have products to return)
      setIsReturnMode(true);
      setReturnSalesInvoiceId(invoiceDataToUse.invoice_Id || invoiceDataToUse.Invoice_Id);
      setOriginalInvoiceNo(invoiceDataToUse.invoice_No || invoiceDataToUse.Invoice_No);
      // Store original invoice payment info for return mode restrictions
      setOriginalInvoicePaymentMethod(invoiceDataToUse.paymentMethod || invoiceDataToUse.PaymentMethod || "ca");
      setOriginalInvoiceType(invoiceDataToUse.invoice_Type || invoiceDataToUse.Invoice_Type || "ca");
      setIsEditing(false);
      setIsReadOnly(false);

      // Set form data (pre-populate with original invoice data)
      const customerId = invoiceDataToUse.invoice_CustomerId || invoiceDataToUse.Invoice_CustomerId;
      const customer = customers.find(c => c.customer_Id === customerId);
      if (customer) {
        setSelectedCustomerOption({
          value: customer.customer_Id,
          label: customer.customer_NameEn
        });
      }

      // Get original invoice payment details
      const originalPaymentMethod = invoiceDataToUse.paymentMethod || invoiceDataToUse.PaymentMethod || "ca";
      const originalInvoiceType = invoiceDataToUse.invoice_Type || invoiceDataToUse.Invoice_Type || "ca";

      setForm(prev => ({
        ...prev,
        Invoice_Id: null,
        Invoice_No: "",
        Invoice_Date: new Date(),
        Invoice_CustomerId: customerId,
        Invoice_BusinessId: invoiceDataToUse.invoice_BusinessId || invoiceDataToUse.Invoice_BusinessId,
        Invoice_BranchId: invoiceDataToUse.invoice_BranchId || invoiceDataToUse.Invoice_BranchId,
        Invoice_Type: originalInvoiceType,
        PaymentMethod: originalPaymentMethod,
        Invoice_SourceId: invoiceDataToUse.invoice_SourceId || invoiceDataToUse.Invoice_SourceId || null,
        // Don't pre-fill cash/cashless amounts - they will be populated after stock selection
        CashAmount: null,
        CashlessAmount: null,
        entityItems: entityItems,
        selectedStockData: {},
        preloadedStockItems: null
      }));

      // Generate return invoice number (sequential: 1, 2, 3... per business)
      // No longer uses 'R' suffix - uses sequential numbering per business
      if (invoiceDataToUse.invoice_BusinessId || invoiceDataToUse.Invoice_BusinessId) {
        const businessId = invoiceDataToUse.invoice_BusinessId || invoiceDataToUse.Invoice_BusinessId;
        try {
          const result = await getNextSalesInvoiceNumber(
            businessId,
            true, // isReturn = true
            null  // Not used for sequential returns - procedure generates sequential numbers per business
          );
          if (result && result.nextInvoiceNumber) {
            setForm(prev => ({
              ...prev,
              Invoice_No: String(result.nextInvoiceNumber) // Ensure it's a string
            }));
          } else {
            // Fallback: clear invoice number if generation fails
            setForm(prev => ({
              ...prev,
              Invoice_No: "" // Will be auto-generated by procedure if not provided
            }));
          }
        } catch (error) {
          console.error("Error generating return invoice number:", error);
          // Fallback: clear invoice number if generation fails
          setForm(prev => ({
            ...prev,
            Invoice_No: "" // Will be auto-generated by procedure if not provided
          }));
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading invoice for return:", error);
      Swal.fire("Error", `Failed to load invoice for return: ${error.response?.data?.message || error.message || "Unknown error"}`, "error");
      setLoading(false);
    }
  };

  const handleView = async (item) => {
    try {
      // Format date
      const invoiceDate = item.invoice_Date || item.Invoice_Date;

      // Format amounts
      const totalAmount = (item.invoice_TotalAmount || item.Invoice_TotalAmount || 0).toFixed(2);
      const discountAmount = (item.invoice_DisAmount || item.Invoice_DisAmount || 0).toFixed(2);
      const taxAmount = (item.invoice_TaxAmount || item.Invoice_TaxAmount || 0).toFixed(2);
      const netAmount = (item.invoice_NetAmount || item.Invoice_NetAmount || 0).toFixed(2);
      const paidAmount = (item.paidAmount || item.PaidAmount || 0).toFixed(2);
      const tipAmount = (item.tipAmount || item.TipAmount || 0).toFixed(2);
      const cashAmount = (item.cashAmount || item.CashAmount || 0).toFixed(2);
      const cashlessAmount = (item.cashlessAmount || item.CashlessAmount || 0).toFixed(2);

      // Get business name
      const businessName = getBusinessName(item.invoice_BusinessId || item.Invoice_BusinessId);

      // Get branch name
      const branchName = getBranchName(item.invoice_BranchId || item.Invoice_BranchId);

      // Helper function to get entity name
      const getEntityName = (entityType, entityId) => {
        if (!entityId) return 'N/A';

        // Convert entityId to number for comparison
        const entityIdNum = parseInt(entityId, 10);

        if (entityType === 'pro') {
          const product = products.find(p => {
            const pId = parseInt(p.product_Id || p.productId || p.id || 0, 10);
            return pId === entityIdNum && pId > 0;
          });
          return product ? (product.product_NameEn || product.productName_En || product.name || `Product ID: ${entityId}`) : `Product ID: ${entityId}`;
        } else if (entityType === 'ser') {
          const service = services.find(s => {
            const sId = parseInt(s.spaService_Id || s.spaServiceId || s.id || 0, 10);
            return sId === entityIdNum && sId > 0;
          });
          return service ? (service.spaServiceName_En || service.spaServiceNameEn || service.name || `Service ID: ${entityId}`) : `Service ID: ${entityId}`;
        } else if (entityType === 'pac') {
          const pkg = packages.find(p => {
            const pId = parseInt(p.packageId || p.package_Id || p.id || 0, 10);
            return pId === entityIdNum && pId > 0;
          });
          return pkg ? (pkg.packageName_En || pkg.packageNameEn || pkg.name || `Package ID: ${entityId}`) : `Package ID: ${entityId}`;
        } else if (entityType === 'sep') {
          const series = packageSeries.find(s => {
            const sId = parseInt(s.seriesId || s.series_Id || s.id || 0, 10);
            return sId === entityIdNum && sId > 0;
          });
          return series ? (series.seriesName_En || series.seriesNameEn || series.name || `Package Series ID: ${entityId}`) : `Package Series ID: ${entityId}`;
        }
        return `Entity ID: ${entityId}`;
      };

      // Helper function to get tax name
      const getTaxName = (taxId) => {
        const tax = taxes.find(t => t.tax_Id === taxId);
        return tax ? tax.tax_NameEn : `Tax ID: ${taxId}`;
      };

      // Helper function to get invoice type name
      const getInvoiceTypeName = (type) => {
        if (!type) return "N/A";
        switch (type.toLowerCase()) {
          case "cr": return "Credit";
          case "ca": return "Cash";
          default: return type.toUpperCase();
        }
      };

      // Separate details by type
      let productsHtml = '';
      let servicesHtml = '';
      let packagesHtml = '';
      let packageSeriesHtml = '';

      if (item.details && item.details.length > 0) {
        const products = item.details.filter(detail => detail.entityType === 'pro');
        const services = item.details.filter(detail => detail.entityType === 'ser');
        const packages = item.details.filter(detail => detail.entityType === 'pac');
        const packageSeries = item.details.filter(detail => detail.entityType === 'sep');

        // Products section
        if (products.length > 0) {
          // Helper function to get salesman name
          const getSalesmanName = (salesmanId) => {
            if (!salesmanId || salesmanId === 0) return 'N/A';
            const employee = employees.find(emp => emp.employee_Id === salesmanId);
            return employee ? employee.employee_NameEn : `Employee ID: ${salesmanId}`;
          };

          productsHtml = `
            <div class="mt-3">
              <h6 class="text-success">Products</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Product Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                      <th>Salesman</th>
                      <th>Stock Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${products.map(detail => `
                      <tr>
                        <td>${getEntityName('pro', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                        <td>${getSalesmanName(detail.salesman_ID || detail.Salesman_ID)}</td>
                        <td>
                          <button 
                            class="btn btn-outline-info btn-sm" 
                            onclick="window.openStockDetailsModal('${getEntityName('pro', detail.entityId)}', ${JSON.stringify(detail.stockDetails || []).replace(/"/g, '&quot;')})"
                            title="View Stock Details"
                            style="padding: 4px 8px;"
                          >
                            <i class="bx bx-box" style="font-size: 14px;"></i>
                          </button>
                        </td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }

        // Services section
        if (services.length > 0) {
          servicesHtml = `
            <div class="mt-3">
              <h6 class="text-info">Services</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Service Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${services.map(detail => `
                      <tr>
                        <td>${getEntityName('ser', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }

        // Packages section
        if (packages.length > 0) {
          packagesHtml = `
            <div class="mt-3">
              <h6 class="text-warning">Packages</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Package Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${packages.map(detail => `
                      <tr>
                        <td>${getEntityName('pac', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }

        // Package Series section
        if (packageSeries.length > 0) {
          packageSeriesHtml = `
            <div class="mt-3">
              <h6 class="text-secondary">Package Series</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Package Series Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${packageSeries.map(detail => `
                      <tr>
                        <td>${getEntityName('sep', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }

        // Check for installment details and fetch installment information
        const installmentDetails = item.details.filter(detail => detail.m_InstallmentId);
        let installmentHtml = '';

        if (installmentDetails.length > 0) {
          try {
            // Get unique installment IDs
            const installmentIds = [...new Set(installmentDetails.map(detail => detail.m_InstallmentId))];

            // Fetch installment details
            const installmentData = await Promise.all(
              installmentIds.map(async (installmentId) => {
                try {
                  const response = await getCustomerMemberships({
                    installmentId: installmentId
                  });
                  const membership = response.find(m =>
                    m.installments?.some(inst => inst.m_InstallmentId === installmentId)
                  );

                  // Also fetch package series data to get installment names
                  let packageSeriesData = null;
                  if (membership && isPackageSeriesType(membership.entity_Type)) {
                    try {
                      const seriesResponse = await getSpaPackageSeries({ seriesId: membership.entity_Id });
                      packageSeriesData = seriesResponse.find(s => s.seriesId === membership.entity_Id);
                    } catch (error) {
                      console.error(`Error fetching package series ${membership.entity_Id}:`, error);
                    }
                  }

                  return { membership, packageSeriesData };
                } catch (error) {
                  console.error(`Error fetching installment ${installmentId}:`, error);
                  return null;
                }
              })
            );

            // Group installment details by entity type
            const installmentPackages = installmentDetails.filter(detail => detail.entityType === 'pac');
            const installmentPackageSeries = installmentDetails.filter(detail => detail.entityType === 'sep');

            // Add installment info to packages section
            if (installmentPackages.length > 0) {
              const installmentPackageRows = installmentPackages.map(detail => {
                const data = installmentData.find(d =>
                  d?.membership?.installments?.some(inst => inst.m_InstallmentId === detail.m_InstallmentId)
                );
                const installment = data?.membership?.installments?.find(inst => inst.m_InstallmentId === detail.m_InstallmentId);
                const installmentName = installment ? `Installment ${installment.installmentId}` : 'Unknown Installment';

                return `
                  <tr>
                    <td>${getEntityName('pac', detail.entityId)} - ${installmentName}</td>
                    <td>${detail.quantity}</td>
                    <td>${(detail.price || 0).toFixed(2)}</td>
                    <td>${(detail.disAmount || 0).toFixed(2)}</td>
                    <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                  </tr>
                `;
              }).join('');

              packagesHtml += `
                <div class="mt-3">
                  <h6 class="text-warning">Installment Packages</h6>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead class="table-light">
                        <tr>
                          <th>Package Name</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Discount</th>
                          <th>Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${installmentPackageRows}
                      </tbody>
                    </table>
                  </div>
                </div>
              `;
            }

            // Add installment info to package series section
            if (installmentPackageSeries.length > 0) {
              const installmentPackageSeriesRows = installmentPackageSeries.map(detail => {
                const data = installmentData.find(d =>
                  d?.membership?.installments?.some(inst => inst.m_InstallmentId === detail.m_InstallmentId)
                );
                const installment = data?.membership?.installments?.find(inst => inst.m_InstallmentId === detail.m_InstallmentId);

                // Get proper installment name from package series data
                let installmentName = 'Unknown Installment';
                if (installment && data?.packageSeriesData?.installments) {
                  const seriesInstallment = data.packageSeriesData.installments.find(
                    si => si.installmentId === installment.installmentId
                  );
                  installmentName = seriesInstallment ? seriesInstallment.installmentName : `Installment ${installment.installmentId}`;
                } else if (installment) {
                  installmentName = `Installment ${installment.installmentId}`;
                }

                return `
                  <tr>
                    <td>${getEntityName('sep', detail.entityId)} - ${installmentName}</td>
                    <td>${detail.quantity}</td>
                    <td>${(detail.price || 0).toFixed(2)}</td>
                    <td>${(detail.disAmount || 0).toFixed(2)}</td>
                    <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                  </tr>
                `;
              }).join('');

              packageSeriesHtml += `
                <div class="mt-3">
                  <h6 class="text-secondary">Installment Package Series</h6>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead class="table-light">
                        <tr>
                          <th>Package Series Name</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Discount</th>
                          <th>Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${installmentPackageSeriesRows}
                      </tbody>
                    </table>
                  </div>
                </div>
              `;
            }
          } catch (error) {
            console.error('Error fetching installment details:', error);
          }
        }
      }

      // Build taxes HTML (remove duplicates)
      let taxesHtml = '';
      if (item.taxes && item.taxes.length > 0) {
        // Create unique tax entries by tax_Id and taxPer
        const uniqueTaxes = item.taxes.reduce((acc, tax) => {
          const key = `${tax.tax_Id}_${tax.taxPer}`;
          if (!acc.some(t => `${t.tax_Id}_${t.taxPer}` === key)) {
            acc.push(tax);
          }
          return acc;
        }, []);

        taxesHtml = `
          <div class="mt-3">
            <h6 class="text-primary">Tax Details</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Tax Name</th>
                    <th>Tax Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  ${uniqueTaxes.map(tax => `
                    <tr>
                      <td>${getTaxName(tax.tax_Id)}</td>
                      <td>${(tax.taxPer || 0).toFixed(2)}%</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        `;
      }

      // Add global function for stock details modal
      window.openStockDetailsModal = (productName, stockDetails) => {
        handleOpenStockDetailsModal(productName, stockDetails);
      };

      Swal.fire({
        title: `Invoice #${item.invoice_No || item.Invoice_No || 'N/A'}`,
        html: `
          <div class="text-start" style="max-height: 70vh; overflow-y: auto;">
            <div class="row">
              <div class="col-md-6">
                <h6 class="text-primary">Basic Information</h6>
                <p><strong>Invoice Number:</strong> ${item.invoice_No || item.Invoice_No || 'N/A'}</p>
                <p><strong>Invoice Date:</strong> ${invoiceDate ? new Date(invoiceDate).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Customer:</strong> ${getCustomerName(item.invoice_CustomerId || item.Invoice_CustomerId)}</p>
                <p><strong>Business:</strong> ${businessName}</p>
                <p><strong>Branch:</strong> ${branchName}</p>
                <p><strong>Invoice Type:</strong> ${getInvoiceTypeName(item.invoice_Type || item.Invoice_Type)}</p>
              </div>
              <div class="col-md-6">
                <h6 class="text-primary">Financial Summary</h6>
                <p><strong>Total Quantity:</strong> ${item.invoice_TotalQty || item.Invoice_TotalQty || 0}</p>
                <p><strong>Total Amount:</strong> ${totalAmount}</p>
                <p><strong>Discount Amount:</strong> ${discountAmount}</p>
                <p><strong>Tax Amount:</strong> ${taxAmount}</p>
                <p><strong>Net Amount:</strong> <span class="fw-bold text-success">${netAmount}</span></p>
                <p><strong>Payment Method:</strong> ${getPaymentMethodName(item.paymentMethod || item.PaymentMethod)}</p>
              </div>
            </div>
            
            <div class="row mt-3">
              <div class="col-md-6">
                <h6 class="text-primary">Payment Details</h6>
                <p><strong>Paid Amount:</strong> ${paidAmount}</p>
                ${parseFloat(tipAmount) > 0 ? `<p><strong>Tip Amount:</strong> ${tipAmount}</p>` : ''}
                <p><strong>Cash Amount:</strong> ${cashAmount}</p>
                <p><strong>Cashless Amount:</strong> ${cashlessAmount}</p>
                <p><strong>Auth Code:</strong> ${item.authCode || item.AuthCode || 'N/A'}</p>
              </div>
            </div>
            
            ${productsHtml}
            ${servicesHtml}
            ${packagesHtml}
            ${packageSeriesHtml}
            ${taxesHtml}
          </div>
        `,
        icon: "info",
        width: '90%',
        confirmButtonText: 'Close',
        showCloseButton: true,
        customClass: {
          popup: 'swal-wide'
        }
      });
    } catch (error) {
      console.error("Error viewing invoice:", error);
    }
  };

  const handlePrintInvoice = async (item) => {
    try {
      setPrintLoading(true);
      const invoiceId = item.invoice_Id || item.Invoice_Id;

      // Fetch invoice data for print
      const invoiceData = await getInvoiceForPrint(invoiceId);

      if (!invoiceData || !invoiceData.header) {
        Swal.fire("Error", "Invoice not found or unable to load invoice data", "error");
        setPrintLoading(false);
        return;
      }

      setInvoicePrintData(invoiceData);
      setShowInvoicePrintModal(true);
      setPrintLoading(false);
    } catch (error) {
      console.error("Error loading invoice for print:", error);
      Swal.fire("Error", `Failed to load invoice: ${error.response?.data?.message || error.message || "Unknown error"}`, "error");
      setPrintLoading(false);
    }
  };

  // Cleanup stock locks on page refresh/navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Note: Modern browsers may ignore async operations in beforeunload
      // So we'll also cleanup on component unmount
      if (temporaryFormUniqueId) {
        // Try to cleanup synchronously if possible
        navigator.sendBeacon && navigator.sendBeacon(
          `${window.location.origin}/api/StockSelectionLock/delete-lock`,
          JSON.stringify({ temporaryFormUniqueId })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Cleanup on component unmount (page navigation, route change)
      if (temporaryFormUniqueId) {
        deleteStockSelectionLock(temporaryFormUniqueId).catch(() => { });
      }
    };
  }, [temporaryFormUniqueId]);

  const resetForm = () => {
    // Cleanup stock locks before reset
    if (temporaryFormUniqueId) {
      deleteStockSelectionLock(temporaryFormUniqueId).catch(err =>
        console.error('Error cleaning up locks on reset:', err)
      );
    }

    // Get user's company and branch IDs from localStorage
    const userCompanyId = localStorage.getItem("userCompanyId");
    const userBranchId = localStorage.getItem("userBranchId");

    // Reset form to default, but preserve company and branch if they exist in login
    const resetFormData = { ...defaultForm };

    // Auto-populate company if user has companyId and businesses are loaded
    if (userCompanyId && businesses.length > 0) {
      const companyExists = businesses.find(b => b.business_Id === parseInt(userCompanyId));
      if (companyExists) {
        resetFormData.Invoice_BusinessId = parseInt(userCompanyId);

        // Auto-populate branch if user has branchId and branches are loaded
        if (userBranchId && branches.length > 0) {
          const branchExists = branches.find(b => b.branch_Id === parseInt(userBranchId) && b.branch_Buisness_Id === parseInt(userCompanyId));
          if (branchExists) {
            resetFormData.Invoice_BranchId = parseInt(userBranchId);
          }
        }
      }
    }

    setForm(resetFormData);
    setIsEditing(false);
    setSelectedCustomerOption(null);
    setCustomerHasMembership(false);
    setCustomerHasBillableItems(false);

    // Check if we're currently processing booking BEFORE resetting the flag
    const wasProcessingBooking = isFromBooking;

    // Reset booking navigation flag
    console.log('⚠️ [resetFormData] Resetting isFromBooking state to false');
    setIsFromBooking(false);

    // Reset ref when form is reset UNLESS we were processing booking data
    // This ensures that when user starts a NEW invoice directly (not from booking), ref gets reset
    // But if we were processing booking data, keep the ref so prompt doesn't show
    if (!wasProcessingBooking) {
      console.log('🔄 [resetFormData] Was not processing booking - resetting isFromBookingRef and processedBookingRef');
      isFromBookingRef.current = false;
      processedBookingRef.current = false;
      processedBookingIdRef.current = null;
    } else {
      console.log('ℹ️ [resetFormData] Was processing booking - keeping isFromBookingRef for print modal');
    }
    // Reset return mode
    setIsReturnMode(false);
    setReturnSalesInvoiceId(null);
    setOriginalInvoiceNo(null);
    setOriginalInvoicePaymentMethod(null);
    setOriginalInvoiceType(null);
    // Reset form ID
    setTemporaryFormUniqueId(null);
    // Reset file input
    const fileInput = document.getElementById('invoiceDocumentUpload');
    if (fileInput) fileInput.value = '';
    // Invoice number will be generated automatically via useEffect when BusinessId is set
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setCustomerSearchOptions([]);
    setEmployeeSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    setSelectedEmployeeFilterOption(null);
    setHasAppliedFilter(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Helper functions for quantity management
  const handleQuantityChange = (entityType, entityId, quantity) => {
    const quantityField = `${entityType}Quantities`;
    // Allow empty string or valid numbers, set minimum to 1 only when value is provided
    const parsedQuantity = quantity === '' ? '' : (parseInt(quantity) || 1);
    setForm(prev => ({
      ...prev,
      [quantityField]: {
        ...prev[quantityField],
        [entityId]: parsedQuantity
      }
    }));
  };

  const getQuantity = (entityType, entityId) => {
    const quantityField = `${entityType}Quantities`;
    const quantity = form[quantityField][entityId];
    return quantity === '' ? '' : (quantity || 1);
  };

  // Helper functions for price, discount, tax management
  const handlePriceChange = (entityType, entityId, price) => {
    const priceField = `${entityType}Prices`;
    setForm(prev => ({
      ...prev,
      [priceField]: {
        ...prev[priceField],
        [entityId]: parseFloat(price) || 0
      }
    }));
  };

  const handleDiscountChange = (entityType, entityId, discount) => {
    const discountField = `${entityType}Discounts`;
    setForm(prev => ({
      ...prev,
      [discountField]: {
        ...prev[discountField],
        [entityId]: parseFloat(discount) || 0
      }
    }));
  };

  const handleTaxChange = (entityType, entityId, tax) => {
    const taxField = `${entityType}Taxes`;
    setForm(prev => ({
      ...prev,
      [taxField]: {
        ...prev[taxField],
        [entityId]: parseFloat(tax) || 0
      }
    }));
  };

  const getPrice = (entityType, entityId) => {
    const priceField = `${entityType}Prices`;
    const unitPrice = form[priceField][entityId] || 0;
    const quantity = getQuantity(entityType, entityId) || 1;
    return unitPrice * quantity;
  };

  const getDiscount = (entityType, entityId) => {
    const discountField = `${entityType}Discounts`;
    return form[discountField][entityId] || 0;
  };

  const getTax = (entityType, entityId) => {
    const taxField = `${entityType}Taxes`;
    return form[taxField][entityId] || 0;
  };

  // Calculate entity totals from entityItems array
  const getEntityTotalsFromItems = (entityType) => {
    let totalQty = 0;
    let totalAmount = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

    // For packageSeries, separate regular items from installment items
    let items;
    if (entityType === 'packageSeries') {
      items = form.entityItems.filter(item => item.type === entityType && !item.m_InstallmentId);
    } else {
      items = form.entityItems.filter(item => item.type === entityType);
    }

    items.forEach(item => {
      const lineTotal = (item.unitPrice ?? 0) * item.quantity;
      const discountAmount = item.discountAmount || 0;
      const taxAmount = item.taxAmount || 0;

      totalQty += item.quantity;
      totalAmount += lineTotal;
      totalDiscountAmount += discountAmount;
      totalTaxAmount += taxAmount;
    });

    // Calculate effective rates based on totals
    const effectiveDiscountPercentage = totalAmount > 0
      ? (totalDiscountAmount / totalAmount) * 100
      : 0;

    const taxableValue = totalAmount - totalDiscountAmount;
    const effectiveTaxPercentage = taxableValue > 0
      ? (totalTaxAmount / taxableValue) * 100
      : 0;

    // Calculate net amount: totalAmount - discount + tax
    const netAmount = totalAmount - totalDiscountAmount + totalTaxAmount;

    return {
      totalQty,
      totalAmount,
      totalDiscountAmount,
      totalTaxAmount,
      effectiveDiscountPercentage,
      effectiveTaxPercentage,
      netAmount
    };
  };

  // Calculate installment totals from entityItems array
  const getInstallmentTotalsFromItems = () => {
    let totalQty = 0;
    let totalAmount = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

    // Get all installment items (both package and packageSeries with m_InstallmentId)
    const installmentItems = form.entityItems.filter(item => item.m_InstallmentId);

    installmentItems.forEach(item => {
      const lineTotal = (item.unitPrice ?? 0) * item.quantity;
      const discountAmount = item.discountAmount || 0;
      const taxAmount = item.taxAmount || 0;

      totalQty += item.quantity;
      totalAmount += lineTotal;
      totalDiscountAmount += discountAmount;
      totalTaxAmount += taxAmount;
    });

    // Calculate effective rates based on totals
    const effectiveDiscountPercentage = totalAmount > 0
      ? (totalDiscountAmount / totalAmount) * 100
      : 0;

    const taxableValue = totalAmount - totalDiscountAmount;
    const effectiveTaxPercentage = taxableValue > 0
      ? (totalTaxAmount / taxableValue) * 100
      : 0;

    // Calculate net amount: totalAmount - discount + tax
    const netAmount = totalAmount - totalDiscountAmount + totalTaxAmount;

    return {
      totalQty,
      totalAmount,
      totalDiscountAmount,
      totalTaxAmount,
      effectiveDiscountPercentage,
      effectiveTaxPercentage,
      netAmount
    };
  };

  // Calculate entity totals
  const getEntityTotals = (entityType) => {
    let totalQty = 0;
    let totalAmount = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

    const selectedItems = form[`selected${(entityType || '').charAt(0).toUpperCase() + (entityType || '').slice(1)}s`] || [];

    selectedItems.forEach(item => {
      const uniqueKey = item.uniqueKey || item.value; // Fallback for backward compatibility
      const qty = getQuantity(entityType, uniqueKey) || 1;
      const unitPrice = form[`${entityType}Prices`][uniqueKey] || 0;
      const discountPercentage = getDiscount(entityType, uniqueKey);
      const taxPercentage = getTax(entityType, uniqueKey);

      const lineTotal = unitPrice * qty;
      // Cap discount at 100% per line item
      const cappedDiscountPercentage = Math.min(discountPercentage, 100);
      const discountAmount = (lineTotal * cappedDiscountPercentage) / 100;
      const taxableValue = Math.max(lineTotal - discountAmount, 0); // Ensure taxable value is not negative
      const taxAmount = (taxableValue * taxPercentage) / 100;

      totalQty += qty;
      totalAmount += lineTotal;
      totalDiscountAmount += discountAmount;
      totalTaxAmount += taxAmount;
    });

    // Calculate effective rates based on totals
    const effectiveDiscountPercentage = totalAmount > 0
      ? (totalDiscountAmount / totalAmount) * 100
      : 0;

    const taxableValue = totalAmount - totalDiscountAmount;
    const effectiveTaxPercentage = taxableValue > 0
      ? (totalTaxAmount / taxableValue) * 100
      : 0;

    return {
      totalQty,
      totalAmount,
      totalDiscountAmount,
      totalTaxAmount,
      effectiveDiscountPercentage,
      effectiveTaxPercentage
    };
  };

  // Helper functions for entity item management
  const addEntityItem = async (entityType, entityData, quantity = 1, stockData = null) => {
    // Generate form ID when first product is added (for stock selection lock)
    if (entityType === 'product' && !temporaryFormUniqueId) {
      const formId = generateTemporaryFormUniqueId();
      setTemporaryFormUniqueId(formId);
    }

    const uniqueId = Date.now() + Math.random(); // More unique ID generation
    const newItem = {
      id: uniqueId,
      type: entityType,
      entityId: entityData.value,
      entityName: entityData.label,
      quantity: quantity,
      unitPrice: null,
      totalPrice: 0,
      discount: null,
      tax: 0,
      taxAmount: 0,
      netAmount: 0,
      discountAmount: 0, // Store discount amount
      costPrice: 0, // Store cost price separately
      taxIds: [], // Store all Tax IDs for API
      taxBreakdown: [], // Store tax breakdown for display
      uniqueKey: uniqueId, // Add unique key for legacy arrays
      salesmanId: 0, // Add salesman ID (0 for non-products, will be set for products) - kept for backward compatibility
      salesmanIds: [], // Add array of salesman IDs for multi-salesman support
      stockData: stockData // Store stock data for API
    };

    // Fetch cost from API based on entity type
    const cost = await fetchEntityCost(entityType, entityData.value);
    newItem.costPrice = cost;

    // Populate price and other details based on entity type
    let entityDetails = null;
    if (entityType === 'product') {
      // First try to find in modalItems (paginated data), then fall back to products array
      const modalItem = modalItems.find(item => item.value === entityData.value);
      if (modalItem && modalItem.product) {
        entityDetails = modalItem.product;
      } else {
        entityDetails = products.find(p => p.product_Id === entityData.value);
      }

      // If still not found, fetch it
      if (!entityDetails) {
        try {
          const fetchedProducts = await getProducts({ product_Id: entityData.value });
          if (fetchedProducts && fetchedProducts.length > 0) {
            entityDetails = fetchedProducts[0];
          }
        } catch (error) {
          console.error("Error fetching product details:", error);
        }
      }

      if (entityDetails) {
        // Use stock data if available, otherwise use product data
        if (stockData) {
          newItem.unitPrice = stockData.price || null;
          newItem.discount = stockData.discount || null; // Use stock discount percentage
          // Convert stock data to comma-separated stock IDs for API
          const stockIds = stockData.stocks ? stockData.stocks.map(s => s.purchaseStock_Id).join(',') : '';
          newItem.stockData = stockIds; // Store stock IDs as comma-separated string
          newItem.entityName = `${entityData.label} (${stockData.batchNumbers})`; // Include batch info
        } else {
          newItem.unitPrice = entityDetails.product_CostPrice || entityDetails.Product_CostPrice || null; // Start with cost price as unit price
          newItem.discount = entityDetails.product_DiscountPercentage || entityDetails.Product_DiscountPercentage || null;
        }
        newItem.taxIds = entityDetails.taxIds || entityDetails.TaxIds || [];
        newItem.taxBreakdown = calculateTaxBreakdown(entityDetails.taxIds);
        newItem.tax = calculateCombinedTaxPercentage(entityDetails.taxIds);
      }
    } else if (entityType === 'service') {
      // First try to find in modalItems (paginated data), then fall back to services array
      const modalItem = modalItems.find(item => item.value === entityData.value);
      if (modalItem && modalItem.service) {
        entityDetails = modalItem.service;
      } else {
        entityDetails = services.find(s => s.spaService_Id === entityData.value && s.status === true);
      }

      // If still not found, fetch it
      if (!entityDetails) {
        try {
          const fetchedServices = await getSpaServices({ SpaService_Id: entityData.value, status: true });
          if (fetchedServices && fetchedServices.length > 0 && fetchedServices[0].status === true) {
            entityDetails = fetchedServices[0];
          }
        } catch (error) {
          console.error("Error fetching service details:", error);
        }
      }

      if (entityDetails && entityDetails.status === true) {
        newItem.unitPrice = entityDetails.amount || entityDetails.Amount || null;
        newItem.discount = entityDetails.discountPercentage || entityDetails.DiscountPercentage || null;
        newItem.taxIds = entityDetails.taxIds || entityDetails.TaxIds || [];
        newItem.taxBreakdown = calculateTaxBreakdown(entityDetails.taxIds || entityDetails.TaxIds || []);
        newItem.tax = calculateCombinedTaxPercentage(entityDetails.taxIds || entityDetails.TaxIds || []);
      }
    } else if (entityType === 'package') {
      entityDetails = packages.find(p => p.packageId === entityData.value && p.status === true);
      if (entityDetails && entityDetails.status === true) {
        newItem.unitPrice = entityDetails.amount || null;
        newItem.discount = entityDetails.discountPercentage || null;
        newItem.taxIds = entityDetails.taxIds || [];
        newItem.taxBreakdown = calculateTaxBreakdown(entityDetails.taxIds);
        newItem.tax = calculateCombinedTaxPercentage(entityDetails.taxIds);
      }
    } else if (entityType === 'packageSeries') {
      entityDetails = packageSeries.find(s => s.seriesId === entityData.value && s.status === true);
      if (entityDetails && entityDetails.status === true) {
        // Check for duplicate membership when package series is added
        if (form.Invoice_CustomerId && entityDetails) {
          try {
            const existingMemberships = await getCustomerMemberships({
              customerId: form.Invoice_CustomerId
            });

            if (existingMemberships && existingMemberships.length > 0) {
              const hasDuplicate = existingMemberships.some(m =>
                (m.entity_Type === 'sep' || m.entity_Type === 'SEP') &&
                m.entity_Id === entityData.value
              );

              if (hasDuplicate) {
                const seriesName = entityDetails.seriesName_En || `Package Series ${entityData.value}`;
                const confirmResult = await Swal.fire({
                  icon: 'question',
                  title: 'Duplicate Membership',
                  text: `This customer already has this series (${seriesName}) in membership. Do you want to proceed?`,
                  showCancelButton: true,
                  confirmButtonText: 'Yes, Proceed',
                  cancelButtonText: 'Cancel',
                  confirmButtonColor: '#556ee6',
                  cancelButtonColor: '#6c757d'
                });

                if (!confirmResult.isConfirmed) {
                  return; // Don't proceed if user cancels
                }
              }
            }
          } catch (error) {
            console.error('Error checking duplicate membership:', error);
            // Continue even if check fails
          }
        }

        // For package series, don't add to entityItems - only open installment modal
        // Only selected installments will be added to entityItems
        if (entityDetails.installments && entityDetails.installments.length > 0) {
          // Load package series installments and open modal
          // This is a fallback - normally package series are handled in addSelectedModalItems
          const seriesInstallments = entityDetails.installments.map(inst => ({
            installmentId: inst.installmentId,
            installmentName: inst.installmentName || `Installment ${inst.installmentId}`,
            amount: inst.installmentAmount || inst.InstallmentAmount || 0
          }));

          setForm(prev => ({
            ...prev,
            currentPackageSeriesForInstallments: [{
              seriesId: entityData.value,
              seriesName: entityDetails.seriesName_En || `Package Series ${entityData.value}`,
              installments: seriesInstallments,
              seriesEntity: entityDetails
            }],
            showMembershipModal: true
          }));

          // Don't add the package series itself to entityItems - only installments will be added
          return;
        } else {
          // If no installments, treat it like a package (shouldn't happen, but handle it)
          newItem.unitPrice = entityDetails.amount || null;
          newItem.discount = entityDetails.discountPercentage || null;
          newItem.taxIds = entityDetails.taxIds || [];
          newItem.taxBreakdown = calculateTaxBreakdown(entityDetails.taxIds);
          newItem.tax = calculateCombinedTaxPercentage(entityDetails.taxIds);
        }
      } else {
        // If package series not found, return early
        return;
      }
    } else if (entityType === 'giftcard') {
      entityDetails = giftCards.find(g => g.card_Id === entityData.value);
      if (entityDetails) {
        newItem.unitPrice = entityDetails.amount || 0;
        newItem.discount = 0;
        newItem.taxIds = [];
        newItem.taxBreakdown = [];
        newItem.tax = 0;

        // Fetch unique code from database
        try {
          newItem.giftCardCode = await generateUniqueGiftCardCode();
        } catch (err) {
          console.error("Failed to generate unique code, falling back to frontend generator", err);
          newItem.giftCardCode = generateGiftCardCode();
        }

        newItem.entityName = `${entityData.label} (${newItem.giftCardCode})`;
        newItem.stockData = newItem.giftCardCode; // Use stockData to pass the code to SP
      }
    }

    // Calculate derived amounts (handle null values)
    const unitPrice = newItem.unitPrice ?? 0;
    const discount = newItem.discount ?? 0;
    newItem.totalPrice = unitPrice * quantity;
    // Calculate discount amount from percentage
    let discountAmount = (unitPrice * discount / 100) * quantity;
    // Validation: Cap discount amount at totalPrice
    discountAmount = Math.min(discountAmount, newItem.totalPrice);
    // Validation: Ensure discount amount is not negative
    discountAmount = Math.max(discountAmount, 0);
    newItem.discountAmount = discountAmount;
    // Ensure taxable value is not negative
    const taxableValue = Math.max(newItem.totalPrice - discountAmount, 0);
    newItem.taxAmount = (taxableValue * newItem.tax / 100);
    newItem.netAmount = newItem.totalPrice - discountAmount + newItem.taxAmount;

    console.log('🔍 [addEntityItem] Final newItem before setForm:', {
      id: newItem.id,
      entityName: newItem.entityName,
      salesmanId: newItem.salesmanId,
      salesmanIds: newItem.salesmanIds,
      m_InstallmentId: newItem.m_InstallmentId,
      hasSalesmanIds: !!newItem.salesmanIds && newItem.salesmanIds.length > 0
    });

    setForm(prev => {
      const updatedItems = [...prev.entityItems, newItem];
      console.log('🔍 [addEntityItem] Updated entityItems in setForm:', {
        totalItems: updatedItems.length,
        lastItem: updatedItems[updatedItems.length - 1],
        lastItem_salesmanIds: updatedItems[updatedItems.length - 1]?.salesmanIds
      });
      return {
        ...prev,
        entityItems: updatedItems
      };
    });

    // Also update the old arrays for backward compatibility with totals calculation
    updateLegacyArrays(entityType, entityData, newItem);
  };

  // Helper function to add entity item with custom unit price (for membership installments)
  const addEntityItemWithCustomPrice = async (entityType, entityData, quantity = 1, customUnitPrice = 0, parentEntityData = null) => {
    const uniqueId = Date.now() + Math.random(); // More unique ID generation

    // Fetch cost from API based on entity type
    const cost = await fetchEntityCost(entityType, entityData.value);

    // Check if this is an installment (package series installment)
    const isInstallment = entityData.isNewPackageSeriesInstallment || entityData.m_InstallmentId || entityData.installmentId;

    // For installments: Apply discount/tax from parent entity (package series)
    // Unit price remains as gross (500) for display, but net amount (495) is calculated for billing
    let discountPercentage = 0;
    let taxPercentage = 0;

    if (isInstallment && parentEntityData) {
      // Apply discount and tax from package series for installments
      discountPercentage = parentEntityData.discountPercentage || 0;

      // Calculate total tax percentage from tax IDs
      // Fix race condition: If taxes array is empty, try to reload it
      if (parentEntityData.taxIds && parentEntityData.taxIds.length > 0) {
        if (taxes.length === 0) {
          console.log('Taxes array is empty, reloading taxes...');
          try {
            const fetchedTaxes = await getTaxes({ status: true });
            if (fetchedTaxes && fetchedTaxes.length > 0) {
              setTaxes(fetchedTaxes);
              // Recalculate with new taxes array
              taxPercentage = parentEntityData.taxIds.reduce((total, taxId) => {
                const taxData = fetchedTaxes.find(t => t.tax_Id === taxId);
                return total + (taxData?.tax_Perc || 0);
              }, 0);
            }
          } catch (error) {
            console.error('Error reloading taxes:', error);
          }
        } else {
          taxPercentage = parentEntityData.taxIds.reduce((total, taxId) => {
            const taxData = taxes.find(t => t.tax_Id === taxId);
            return total + (taxData?.tax_Perc || 0);
          }, 0);
        }
      }
    } else if (!isInstallment) {
      // For non-installments, use parent entity discount/tax if available
      discountPercentage = parentEntityData?.discountPercentage || 0;
      if (parentEntityData?.taxIds && parentEntityData.taxIds.length > 0) {
        taxPercentage = parentEntityData.taxIds.reduce((total, taxId) => {
          const taxData = taxes.find(t => t.tax_Id === taxId);
          return total + (taxData?.tax_Perc || 0);
        }, 0);
      }
    }

    // Calculate amounts
    // For installments: unitPrice stays as gross (500), but netAmount is calculated (495)
    const totalPrice = customUnitPrice * quantity;
    const discountAmount = (totalPrice * discountPercentage) / 100;
    const taxableValue = Math.max(totalPrice - discountAmount, 0);
    const taxAmount = (taxableValue * taxPercentage) / 100;
    const netAmount = totalPrice - discountAmount + taxAmount;

    const newItem = {
      id: uniqueId,
      type: entityType,
      entityId: entityData.value,
      entityName: entityData.label,
      quantity: quantity,
      // Lock qty when the item originates from a booking/unbilled-services selection.
      // Those flows must stay qty=1 per instance to preserve booking-resource linking + membership behavior.
      qtyLocked: !!(entityData && entityData.bookingResourceDetailId),
      unitPrice: customUnitPrice,
      totalPrice: totalPrice,
      discount: discountPercentage,
      tax: taxPercentage,
      taxAmount: taxAmount,
      netAmount: netAmount,
      discountAmount: discountAmount,
      costPrice: cost, // Use fetched cost
      taxIds: parentEntityData?.taxIds || [], // Use parent entity tax IDs if available
      taxBreakdown: [], // Empty array as requested
      uniqueKey: uniqueId, // Add unique key for legacy arrays
      m_InstallmentId: entityData.m_InstallmentId, // Store the membership installment ID
      bookingResourceDetailId: entityData.bookingResourceDetailId || null, // Store booking resource detail ID for unbilled services
      salesmanId: (() => {
        // Use first salesman from array if available, otherwise 0
        const salesmanIds = entityData.membershipSalesmanIds && Array.isArray(entityData.membershipSalesmanIds) && entityData.membershipSalesmanIds.length > 0
          ? entityData.membershipSalesmanIds
          : [];
        const firstSalesmanId = salesmanIds.length > 0 ? parseInt(salesmanIds[0]) : 0;
        console.log('🔍 [addEntityItemWithCustomPrice] Setting salesmanId:', {
          membershipSalesmanIds_from_entityData: entityData.membershipSalesmanIds,
          extractedSalesmanIds: salesmanIds,
          firstSalesmanId,
          hasM_InstallmentId: !!entityData.m_InstallmentId
        });
        return firstSalesmanId;
      })(), // Use first salesman from membership array if available (for existing installments), otherwise 0 - kept for backward compatibility
      salesmanIds: (() => {
        // Use salesmanIds array from membership if available
        if (entityData.membershipSalesmanIds && Array.isArray(entityData.membershipSalesmanIds) && entityData.membershipSalesmanIds.length > 0) {
          const extractedIds = entityData.membershipSalesmanIds.map(id => parseInt(id)).filter(id => id > 0);
          console.log('🔍 [addEntityItemWithCustomPrice] Setting salesmanIds array:', {
            membershipSalesmanIds_from_entityData: entityData.membershipSalesmanIds,
            extractedSalesmanIds: extractedIds,
            hasM_InstallmentId: !!entityData.m_InstallmentId
          });
          return extractedIds;
        }
        console.log('🔍 [addEntityItemWithCustomPrice] No salesmanIds found:', {
          membershipSalesmanIds_from_entityData: entityData.membershipSalesmanIds,
          hasM_InstallmentId: !!entityData.m_InstallmentId
        });
        return [];
      })(), // Array of salesman IDs for multi-salesman support
      // Store package series installment fields for membership creation
      installmentId: entityData.installmentId, // Store installment ID from package series
      installmentName: entityData.installmentName, // Store installment name
      isNewPackageSeriesInstallment: entityData.isNewPackageSeriesInstallment, // Flag for new installments
      seriesEntityData: entityData.seriesEntityData || parentEntityData // Store series entity data for membership creation
    };

    setForm(prev => ({
      ...prev,
      entityItems: [...prev.entityItems, newItem]
    }));

    // Also update the old arrays for backward compatibility with totals calculation
    updateLegacyArrays(entityType, entityData, newItem);
  };

  // Helper function to calculate combined tax percentage from multiple tax IDs
  const calculateCombinedTaxPercentage = (taxIds) => {
    if (!taxIds || taxIds.length === 0) return 0;

    let combinedPercentage = 0;
    taxIds.forEach(taxId => {
      const tax = taxes.find(t => t.tax_Id === taxId);
      if (tax && tax.tax_Perc) {
        combinedPercentage += tax.tax_Perc;
      }
    });

    return combinedPercentage;
  };

  // Helper function to calculate tax breakdown for display
  const calculateTaxBreakdown = (taxIds) => {
    if (!taxIds || taxIds.length === 0) return [];

    const breakdown = [];
    taxIds.forEach(taxId => {
      const tax = taxes.find(t => t.tax_Id === taxId);
      if (tax) {
        breakdown.push({
          id: tax.tax_Id,
          name: tax.tax_NameEn,
          percentage: tax.tax_Perc || 0
        });
      }
    });

    return breakdown;
  };

  // Helper function to calculate net amount from gross amount with discount and tax
  const calculateNetAmount = (grossAmount, discountPercentage = 0, taxIds = []) => {
    if (!grossAmount || grossAmount <= 0) return 0;

    // Calculate discount amount
    const discountAmount = (grossAmount * discountPercentage) / 100;

    // Calculate taxable value (after discount)
    const taxableValue = Math.max(grossAmount - discountAmount, 0);

    // Calculate total tax percentage from tax IDs
    let totalTaxPercentage = 0;
    if (taxIds && taxIds.length > 0 && taxes.length > 0) {
      totalTaxPercentage = taxIds.reduce((total, taxId) => {
        const taxData = taxes.find(t => t.tax_Id === taxId);
        return total + (taxData?.tax_Perc || 0);
      }, 0);
    }

    // Calculate tax amount
    const taxAmount = (taxableValue * totalTaxPercentage) / 100;

    // Calculate net amount (gross - discount + tax)
    const netAmount = grossAmount - discountAmount + taxAmount;

    return Math.round(netAmount * 100) / 100; // Round to 2 decimal places
  };

  const updateLegacyArrays = (entityType, entityData, itemData) => {
    const entityField = `selected${(entityType || '').charAt(0).toUpperCase() + (entityType || '').slice(1)}s`;
    const quantityField = `${entityType}Quantities`;
    const priceField = `${entityType}Prices`;
    const discountField = `${entityType}Discounts`;
    const taxField = `${entityType}Taxes`;

    // Create a unique entity object with the unique key and include taxIds
    const uniqueEntityData = {
      ...entityData,
      uniqueKey: itemData.uniqueKey,
      taxIds: itemData.taxIds || [] // Include taxIds for API compatibility
    };

    setForm(prev => ({
      ...prev,
      [entityField]: [...(prev[entityField] || []), uniqueEntityData],
      [quantityField]: {
        ...prev[quantityField],
        [itemData.uniqueKey]: itemData.quantity
      },
      [priceField]: {
        ...prev[priceField],
        [itemData.uniqueKey]: itemData.unitPrice
      },
      [discountField]: {
        ...prev[discountField],
        [itemData.uniqueKey]: itemData.discount
      },
      [taxField]: {
        ...prev[taxField],
        [itemData.uniqueKey]: itemData.tax
      }
    }));
  };

  const updateEntityItemQuantity = async (itemId, quantity) => {
    // 1. Initial values from the current item in state
    const currentItem = form.entityItems.find(i => i.id === itemId);
    if (!currentItem) return;

    // 2. Quantity validation/normalization
    const rawValue = quantity === '' || quantity === null || quantity === undefined ? '' : String(quantity);
    let newQuantity = rawValue === '' ? 0 : (parseInt(rawValue, 10) || 0);

    // 3. Qty constraints
    if (!isReturnMode) {
      if (newQuantity < 1 && rawValue !== '') newQuantity = 1;
    } else if ((currentItem.type || '').toLowerCase() === 'service') {
      const maxAvailable = parseInt(currentItem.originalQuantity ?? 0, 10) || 0;
      if (newQuantity < 0) newQuantity = 0;
      if (newQuantity > maxAvailable) newQuantity = maxAvailable;
    }

    // 4. Permission check (already handled in UI via readOnly/disabled, but safeguard here)
    if (isReadOnly || (!isReturnMode && currentItem.qtyLocked)) return;

    let updatedStockData = null;
    let updatedEntityName = null;

    // 5. PRODUCT AUTO STOCK SELECTION (Main feature request)
    // Only in normal mode (not return mode) for products
    if (!isReturnMode && currentItem.type === 'product' && newQuantity !== currentItem.quantity && rawValue !== '') {
      try {
        const currentStockIds = (currentItem.stockData || '').split(',').filter(id => id);
        const currentStockCount = currentStockIds.length;

        if (newQuantity > currentStockCount) {
          // NEED MORE STOCKS
          const businessId = form.Invoice_BusinessId || session.companyId;
          const branchId = form.Invoice_BranchId || session.branchId;
          const neededCount = newQuantity - currentStockCount;

          const stockRes = await getStockByProduct(currentItem.entityId, businessId, branchId, 1, 100);
          if (stockRes.success && stockRes.stockItems) {
            // Check current locks by others
            const locksRes = await getStockSelectionLocks(currentItem.entityId, temporaryFormUniqueId, 7);
            const lockedByOthers = locksRes.isSuccess ? (locksRes.lockedStocks || []).map(l => l.purchaseStock_Id) : [];

            // Filter available candidates
            const candidates = stockRes.stockItems.filter(s =>
              !currentStockIds.includes(s.purchaseStock_Id.toString()) &&
              !lockedByOthers.includes(s.purchaseStock_Id)
            );

            // Sort FIFO (by expiry date)
            candidates.sort((a, b) => {
              const d1 = parseDDMMYYYY(a.purchaseStock_ExpDate);
              const d2 = parseDDMMYYYY(b.purchaseStock_ExpDate);
              if (d1 && d2) return d1 - d2;
              if (d1) return -1;
              if (d2) return 1;
              return 0;
            });

            // Pick needed amount
            const toAdd = candidates.slice(0, neededCount);
            if (toAdd.length > 0) {
              const toAddIds = toAdd.map(s => s.purchaseStock_Id.toString());

              // Lock them on server
              await insertStockSelectionLock(toAddIds.join(','), temporaryFormUniqueId, 7, getUserSession().userId);

              const finalStockIds = [...currentStockIds, ...toAddIds];
              newQuantity = finalStockIds.length; // Max possible if not enough stock found

              updatedStockData = finalStockIds.join(',');

              // Update Batch Info in Name
              let baseName = currentItem.entityName.split(' (')[0];
              const batchNumbers = (currentItem.entityName.match(/\((.*?)\)/)?.[1]?.split(', ') || []);
              toAdd.forEach(s => {
                if (s.purchaseStock_BatchNo && !batchNumbers.includes(s.purchaseStock_BatchNo)) {
                  batchNumbers.push(s.purchaseStock_BatchNo);
                }
              });
              updatedEntityName = `${baseName} (${batchNumbers.join(', ')})`;
            } else {
              // No additional stock found - revert quantity to what was actually available
              newQuantity = currentStockCount;
              if (newQuantity < 1) newQuantity = 1;
            }
          }
        } else if (newQuantity < currentStockCount && newQuantity >= 1) {
          // REDUCING QUANTITY - Unlock removed stocks
          const toKeepIds = currentStockIds.slice(0, newQuantity);
          const toUnlockIds = currentStockIds.slice(newQuantity);

          if (toUnlockIds.length > 0) {
            await deleteStockSelectionLock(temporaryFormUniqueId, toUnlockIds.join(','));
          }

          updatedStockData = toKeepIds.join(',');

          // Also update Batch Info in Name by keeping only the first N batches
          let baseName = currentItem.entityName.split(' (')[0];
          const batchNumbers = (currentItem.entityName.match(/\((.*?)\)/)?.[1]?.split(', ') || []);
          const updatedBatchNumbers = batchNumbers.slice(0, newQuantity);
          updatedEntityName = updatedBatchNumbers.length > 0 ? `${baseName} (${updatedBatchNumbers.join(', ')})` : baseName;
        }
      } catch (err) {
        console.error("❌ updateEntityItemQuantity (Auto Stock) Error:", err);
      }
    }

    // 6. Final State Update (setForm)
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          const finalQty = newQuantity;
          const price = item.unitPrice || 0;
          const totalPrice = finalQty * price;

          // Recalculate derivative amounts
          let discountAmount = (price * (item.discount || 0) / 100) * finalQty;
          discountAmount = Math.max(0, Math.min(discountAmount, totalPrice));
          const taxableValue = Math.max(totalPrice - discountAmount, 0);
          const taxAmount = (taxableValue * (item.tax || 0) / 100);
          const netAmount = totalPrice - discountAmount + taxAmount;

          return {
            ...item,
            quantity: finalQty,
            totalPrice: totalPrice,
            discountAmount: discountAmount,
            taxAmount: taxAmount,
            netAmount: netAmount,
            ...(updatedStockData !== null && { stockData: updatedStockData }),
            ...(updatedEntityName !== null && { entityName: updatedEntityName })
          };
        }
        return item;
      });

      // Sync legacy arrays
      const updatedItemInArr = updatedEntityItems.find(item => item.id === itemId);
      if (updatedItemInArr) {
        const typeField = `${updatedItemInArr.type}Quantities`;
        return {
          ...prev,
          entityItems: updatedEntityItems,
          [typeField]: {
            ...prev[typeField],
            [updatedItemInArr.uniqueKey]: updatedItemInArr.quantity
          }
        };
      }

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const updateEntityItemUnitPrice = (itemId, unitPrice) => {
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          const newUnitPrice = unitPrice === '' ? null : (parseFloat(unitPrice) || null);
          const unitPriceForCalc = newUnitPrice ?? 0;
          const discountForCalc = (item.discount ?? 0);
          const totalPrice = item.quantity * unitPriceForCalc;
          // Calculate discount amount from percentage
          let discountAmount = (unitPriceForCalc * discountForCalc / 100) * item.quantity;
          // Validation: Cap discount amount at totalPrice
          discountAmount = Math.min(discountAmount, totalPrice);
          // Validation: Ensure discount amount is not negative
          discountAmount = Math.max(discountAmount, 0);
          // Ensure taxable value is not negative
          const taxableValue = Math.max(totalPrice - discountAmount, 0);
          const taxAmount = (taxableValue * item.tax / 100);
          const netAmount = totalPrice - discountAmount + taxAmount;

          // Validation for installment items
          let validationError = '';
          if (item.m_InstallmentId && unitPrice !== '' && unitPrice !== null && unitPrice !== undefined && !isNaN(unitPrice)) {
            // Find the original installment data
            const installment = prev.customerMemberships
              ?.flatMap(membership => membership.installments)
              ?.find(inst => inst.m_InstallmentId === item.m_InstallmentId);

            if (installment) {
              const remainingAmount = Math.round((installment.amount - installment.paidAmount) * 100) / 100;
              if (newUnitPrice > remainingAmount) {
                validationError = `Amount cannot exceed remaining balance of ${remainingAmount.toFixed(2)}`;
              }
            }
          } else if (item.m_InstallmentId) {
            // Clear validation error if unitPrice is empty or invalid
            validationError = '';
          }

          return {
            ...item,
            unitPrice: newUnitPrice,
            totalPrice: totalPrice,
            discountAmount: discountAmount,
            taxAmount: taxAmount,
            netAmount: netAmount,
            validationError: validationError
          };
        }
        return item;
      });

      // Find the updated item to update legacy arrays
      const updatedItem = updatedEntityItems.find(item => item.id === itemId);
      if (updatedItem) {
        const priceField = `${updatedItem.type}Prices`;
        return {
          ...prev,
          entityItems: updatedEntityItems,
          [priceField]: {
            ...prev[priceField],
            [updatedItem.uniqueKey]: updatedItem.unitPrice
          }
        };
      }

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const updateEntityItemDiscount = (itemId, discount) => {
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          const newDiscount = discount === '' ? null : (parseFloat(discount) || null);
          const unitPriceForCalc = (item.unitPrice ?? 0);
          const discountForCalc = (newDiscount ?? 0);
          const totalPrice = item.quantity * unitPriceForCalc;

          // Calculate discount amount from percentage
          let discountAmount = (unitPriceForCalc * discountForCalc / 100) * item.quantity;

          // Validation: Cap discount amount at totalPrice
          discountAmount = Math.min(discountAmount, totalPrice);
          // Validation: Ensure discount amount is not negative
          discountAmount = Math.max(discountAmount, 0);

          // Ensure taxable value is not negative
          const taxableValue = Math.max(totalPrice - discountAmount, 0);
          const taxAmount = (taxableValue * item.tax / 100);
          const netAmount = totalPrice - discountAmount + taxAmount;

          return {
            ...item,
            discount: newDiscount,
            discountAmount: discountAmount,
            taxAmount: taxAmount,
            netAmount: netAmount,
            validationError: null // Clear any previous validation errors
          };
        }
        return item;
      });

      // Find the updated item to update legacy arrays
      const updatedItem = updatedEntityItems.find(item => item.id === itemId);
      if (updatedItem) {
        const discountField = `${updatedItem.type}Discounts`;
        return {
          ...prev,
          entityItems: updatedEntityItems,
          [discountField]: {
            ...prev[discountField],
            [updatedItem.uniqueKey]: updatedItem.discount
          }
        };
      }

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const updateEntityItemDiscountAmount = (itemId, discountAmount) => {
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          const unitPriceForCalc = (item.unitPrice ?? 0);
          const totalPrice = item.quantity * unitPriceForCalc;

          // Parse the discount amount input
          let newDiscountAmount = discountAmount === '' ? 0 : (parseFloat(discountAmount) || 0);

          // Validation: Ensure discount amount is not negative
          newDiscountAmount = Math.max(newDiscountAmount, 0);

          // Validation: Cap discount amount at totalPrice
          if (newDiscountAmount > totalPrice) {
            newDiscountAmount = totalPrice;
            // Set validation error
            return {
              ...item,
              discountAmount: newDiscountAmount,
              validationError: 'Discount amount cannot exceed total price'
            };
          }

          // Calculate discount percentage from discount amount (with 2 decimal precision)
          let calculatedDiscount = 0;
          if (totalPrice > 0) {
            calculatedDiscount = (newDiscountAmount / totalPrice) * 100;
            // Round to 2 decimal places
            calculatedDiscount = Math.round(calculatedDiscount * 100) / 100;
            // Cap at 100%
            calculatedDiscount = Math.min(calculatedDiscount, 100);
          } else {
            // Handle division by zero: if totalPrice is 0, discount % should be 0
            calculatedDiscount = 0;
          }

          // Ensure taxable value is not negative
          const taxableValue = Math.max(totalPrice - newDiscountAmount, 0);
          const taxAmount = (taxableValue * item.tax / 100);
          const netAmount = totalPrice - newDiscountAmount + taxAmount;

          return {
            ...item,
            discount: calculatedDiscount,
            discountAmount: newDiscountAmount,
            taxAmount: taxAmount,
            netAmount: netAmount,
            validationError: null // Clear validation error if valid
          };
        }
        return item;
      });

      // Find the updated item to update legacy arrays
      const updatedItem = updatedEntityItems.find(item => item.id === itemId);
      if (updatedItem) {
        const discountField = `${updatedItem.type}Discounts`;
        return {
          ...prev,
          entityItems: updatedEntityItems,
          [discountField]: {
            ...prev[discountField],
            [updatedItem.uniqueKey]: updatedItem.discount
          }
        };
      }

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const updateEntityItemSalesman = (itemId, selectedOptions) => {
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          // Handle both single value (backward compatibility) and array of options
          const salesmanIds = selectedOptions
            ? (Array.isArray(selectedOptions) ? selectedOptions.map(opt => parseInt(opt.value || opt)) : [parseInt(selectedOptions.value || selectedOptions)])
            : [];
          const firstSalesmanId = salesmanIds.length > 0 ? salesmanIds[0] : 0;

          return {
            ...item,
            salesmanId: firstSalesmanId, // Keep for backward compatibility
            salesmanIds: salesmanIds // Array of salesman IDs
          };
        }
        return item;
      });

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const removeEntityItem = (itemId) => {
    const itemToRemove = form.entityItems.find(item => item.id === itemId);
    if (!itemToRemove) return;

    setForm(prev => ({
      ...prev,
      entityItems: prev.entityItems.filter(item => item.id !== itemId)
    }));

    // Remove from legacy arrays
    const entityField = `selected${(itemToRemove.type || '').charAt(0).toUpperCase() + (itemToRemove.type || '').slice(1)}s`;
    const quantityField = `${itemToRemove.type}Quantities`;
    const priceField = `${itemToRemove.type}Prices`;
    const discountField = `${itemToRemove.type}Discounts`;
    const taxField = `${itemToRemove.type}Taxes`;

    setForm(prev => {
      const newLegacyArray = (prev[entityField] || []).filter(item => item.uniqueKey !== itemToRemove.uniqueKey);
      const newQuantities = { ...prev[quantityField] };
      const newPrices = { ...prev[priceField] };
      const newDiscounts = { ...prev[discountField] };
      const newTaxes = { ...prev[taxField] };

      delete newQuantities[itemToRemove.uniqueKey];
      delete newPrices[itemToRemove.uniqueKey];
      delete newDiscounts[itemToRemove.uniqueKey];
      delete newTaxes[itemToRemove.uniqueKey];

      return {
        ...prev,
        [entityField]: newLegacyArray,
        [quantityField]: newQuantities,
        [priceField]: newPrices,
        [discountField]: newDiscounts,
        [taxField]: newTaxes
      };
    });
  };

  // Load modal items with pagination (for products and services only)
  const loadModalItems = async (entityType, searchText = '', page = 1, append = false) => {
    if (entityType !== 'product' && entityType !== 'service') {
      return; // Only paginate products and services
    }

    try {
      setModalLoading(true);
      const pageSize = 30;
      let response;

      if (entityType === 'product') {
        response = await searchProducts(searchText || "", page, pageSize);
      } else if (entityType === 'service') {
        response = await searchSpaServices(searchText || "", page, pageSize);
      }

      if (response && response.items) {
        // Convert to options format
        const newItems = response.items.map(item => {
          if (entityType === 'product') {
            return {
              value: item.product_Id,
              label: item.product_NameEn || item.product_Name_En || '',
              product: item // Store full product object for later use
            };
          } else {
            return {
              value: item.spaService_Id || item.SpaService_Id,
              label: item.spaServiceName_En || item.SpaServiceName_En || '',
              service: item // Store full service object for later use
            };
          }
        });

        if (append) {
          setModalItems(prev => {
            const updated = [...prev, ...newItems];
            setModalHasMore((response.totalCount || 0) > updated.length);
            return updated;
          });
        } else {
          setModalItems(newItems);
          setModalHasMore((response.totalCount || 0) > newItems.length);
        }

        setModalPage(page);
        setModalTotalCount(response.totalCount || 0);
      }
    } catch (error) {
      console.error(`Error loading ${entityType}s:`, error);
      Swal.fire('Error', `Failed to load ${entityType}s. Please try again.`, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Load more items (next 30)
  const loadMoreModalItems = () => {
    if (form.modalEntityType === 'product' || form.modalEntityType === 'service') {
      const nextPage = modalPage + 1;
      loadModalItems(form.modalEntityType, form.modalSearchTerm, nextPage, true);
    }
  };

  // Modal helper functions
  const openEntityModal = async (entityType) => {
    if (!form.Invoice_BusinessId || !form.Invoice_BranchId) {
      Swal.fire('Select Company and Branch', 'Please select a company and branch before adding items.', 'warning');
      return;
    }

    setForm(prev => ({
      ...prev,
      modalEntityType: entityType,
      showEntityModal: true,
      selectedModalItems: {},
      modalSearchTerm: ''
    }));

    // Reset pagination state
    setModalItems([]);
    setModalPage(1);
    setModalHasMore(false);
    setModalTotalCount(0);
    // Reset initial load ref
    isInitialLoadRef.current = false;

    // Load initial 30 items for products and services only
    if (entityType === 'product' || entityType === 'service') {
      await loadModalItems(entityType, '', 1, false);
      // Mark initial load as done after loading
      isInitialLoadRef.current = true;
    }

    if (entityType === 'giftcard') {
      const gCards = giftCards.map(g => ({
        value: g.card_Id,
        label: g.card_Name,
        giftcard: g
      }));
      setModalItems(gCards);
      isInitialLoadRef.current = true;
    }
  };

  const closeEntityModal = () => {
    setForm(prev => ({
      ...prev,
      showEntityModal: false,
      selectedModalItems: {},
      selectedStockData: {}, // Clear stock data when closing modal
      productStockStatus: {}, // Clear product stock status when closing modal
      modalEntityType: '',
      modalSearchTerm: ''
    }));
    // Reset pagination state
    setModalItems([]);
    setModalPage(1);
    setModalHasMore(false);
    setModalTotalCount(0);
    // Reset initial load ref
    isInitialLoadRef.current = false;
  };

  // Handle search with debouncing for products and services (2-character minimum)
  const searchTimeoutRef = useRef(null);
  const isInitialLoadRef = useRef(false);

  useEffect(() => {
    if (!form.showEntityModal || (form.modalEntityType !== 'product' && form.modalEntityType !== 'service')) {
      isInitialLoadRef.current = false;
      return;
    }

    const searchTerm = form.modalSearchTerm?.trim() || '';

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Skip if this is the initial load (searchTerm is empty and it's the first render after modal opens)
    // The initial load is handled by openEntityModal
    if (searchTerm === '' && !isInitialLoadRef.current) {
      isInitialLoadRef.current = true;
      return;
    }

    // Mark that initial load is done
    isInitialLoadRef.current = true;

    // If search term is less than 2 characters, load all items (no search)
    // If 2+ characters, search with debouncing
    const performSearch = async () => {
      // Reset pagination when search changes
      setModalItems([]);
      setModalPage(1);
      setModalHasMore(false);
      setModalTotalCount(0);

      // Load items with search term (empty string if less than 2 chars)
      const searchText = searchTerm.length >= 2 ? searchTerm : '';
      await loadModalItems(form.modalEntityType, searchText, 1, false);
    };

    // Debounce search - wait 500ms after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      performSearch();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [form.modalSearchTerm, form.showEntityModal, form.modalEntityType]);

  const handleModalItemCheck = async (entityId, checked) => {
    setForm(prev => {
      const newSelectedModalItems = { ...prev.selectedModalItems };
      const newSelectedStockData = { ...prev.selectedStockData };
      const newProductStockStatus = { ...prev.productStockStatus };

      if (checked) {
        // When checked, set quantity to 1
        newSelectedModalItems[entityId] = 1;

        // If it's a product, check stock availability
        if (prev.modalEntityType === 'product') {
          // Check stock availability asynchronously
          checkProductStockAvailability(entityId);
        }
      } else {
        // When unchecked, remove from selectedModalItems and clear stock data
        delete newSelectedModalItems[entityId];
        delete newSelectedStockData[entityId];
        delete newProductStockStatus[entityId];
      }

      return {
        ...prev,
        selectedModalItems: newSelectedModalItems,
        selectedStockData: newSelectedStockData,
        productStockStatus: newProductStockStatus
      };
    });
  };

  // Function to check if a product has stock available
  const checkProductStockAvailability = async (productId) => {
    try {
      // Get business and branch IDs from form state
      // Note: Modal can only be opened when business and branch are selected (see openEntityModal)
      const businessId = form.Invoice_BusinessId || null;
      const branchId = form.Invoice_BranchId || null;

      // Check stock availability
      const stockResponse = await getStockByProduct(productId, businessId, branchId, 1, 1);

      // Update stock status
      const hasStock = stockResponse.success && stockResponse.totalAvailableStock > 0;

      setForm(prev => ({
        ...prev,
        productStockStatus: {
          ...prev.productStockStatus,
          [productId]: hasStock
        }
      }));
    } catch (error) {
      console.error("Error checking stock availability:", error);
      // On error, assume no stock to be safe
      setForm(prev => ({
        ...prev,
        productStockStatus: {
          ...prev.productStockStatus,
          [productId]: false
        }
      }));
    }
  };

  const handleModalQuantityChange = (entityId, quantity) => {
    setForm(prev => ({
      ...prev,
      selectedModalItems: {
        ...prev.selectedModalItems,
        [entityId]: quantity === '' ? '' : (parseInt(quantity) || 1)
      }
    }));
  };

  // Stock modal handlers
  const handleOpenStockModal = (productId, productName, quantity, itemIndex = null) => {
    // Generate form ID when opening stock modal if not already generated
    if (!temporaryFormUniqueId) {
      const formId = generateTemporaryFormUniqueId();
      setTemporaryFormUniqueId(formId);
    }

    // For return mode, get stock details from the entity item
    let preloadedStock = null;
    if (isReturnMode && itemIndex !== null) {
      const item = form.entityItems[itemIndex];
      if (item && item.stockDetails && item.stockDetails.length > 0) {
        // Map stock details to format expected by StockSelectionModal
        preloadedStock = item.stockDetails.map(stock => ({
          purchaseStock_Id: stock.purchaseStock_Id || stock.stockTransaction_StockId,
          purchaseStock_Product_Id: stock.purchaseStock_Product_Id || stock.purchaseStock_Product_Id,
          purchaseStock_Unit_Id: stock.purchaseStock_Unit_Id || stock.purchaseStock_Unit_Id,
          purchaseStock_BatchNo: stock.purchaseStock_BatchNo || stock.purchaseStock_BatchNo,
          purchaseStock_ExpDate: stock.purchaseStock_ExpDate || stock.purchaseStock_ExpDate,
          purchaseStock_IsAvailable: stock.purchaseStock_IsAvailable !== undefined ? stock.purchaseStock_IsAvailable : stock.purchaseStock_IsAvailable,
          purchaseStock_SalesPrice: stock.purchaseStock_SalesPrice || stock.purchaseStock_SalesPrice,
          purchaseStock_CostPrice: stock.purchaseStock_CostPrice || stock.purchaseStock_CostPrice,
          purchaseStock_IsBonus: stock.purchaseStock_IsBonus !== undefined ? stock.purchaseStock_IsBonus : stock.purchaseStock_IsBonus,
          purchaseStock_Branch_Id: stock.purchaseStock_Branch_Id || stock.purchaseStock_Branch_Id
        }));
      }
    }

    // Clear previous stock data when opening modal for a different product
    // This prevents showing stale data from previous product
    setForm(prev => {
      // If opening for a different product, clear its stock data temporarily
      const newSelectedStockData = { ...prev.selectedStockData };
      // Don't clear here - let the modal handle it based on productId change

      return {
        ...prev,
        showStockModal: true,
        currentStockProduct: {
          id: productId,
          name: productName,
          quantity: quantity || 1,
          itemIndex: itemIndex
        },
        preloadedStockItems: preloadedStock
      };
    });
  };

  const handleStockSelected = (stockData) => {
    const productId = form.currentStockProduct?.id;
    const itemIndex = form.currentStockProduct?.itemIndex;

    if (productId && stockData.length > 0) {
      // Extract stock IDs from selected stock data
      const stockIds = [];
      stockData.forEach(group => {
        group.stocks.forEach(stock => {
          stockIds.push(stock.purchaseStock_Id || stock.stockTransaction_StockId);
        });
      });

      // Calculate selected stock quantity
      const selectedQty = stockData.reduce((sum, group) => {
        if (group && typeof group.quantity === 'number') return sum + group.quantity;
        if (group && Array.isArray(group.stocks)) return sum + group.stocks.length;
        return sum;
      }, 0);

      // Update selectedStockData and modal quantity if opened from modal
      setForm(prev => {
        const updates = {
          ...prev,
          selectedStockData: {
            ...prev.selectedStockData,
            [productId]: stockData
          }
        };

        // If opened from modal (itemIndex is null), update modal quantity to match selected stock qty
        if (itemIndex === null && prev.selectedModalItems[productId]) {
          updates.selectedModalItems = {
            ...prev.selectedModalItems,
            [productId]: selectedQty
          };
        }

        return updates;
      });

      // For return mode, update entity item with stock data
      if (isReturnMode && itemIndex !== null) {
        setForm(prev => {
          const updatedEntityItems = [...prev.entityItems];
          if (updatedEntityItems[itemIndex]) {
            const item = updatedEntityItems[itemIndex];
            const newQuantity = stockIds.length;

            // Update item with stock data and quantity
            const totalPrice = newQuantity * (item.unitPrice || 0);
            // Calculate discount amount from percentage
            let discountAmount = totalPrice * ((item.discount || 0) / 100);
            // Validation: Cap discount amount at totalPrice
            discountAmount = Math.min(discountAmount, totalPrice);
            // Validation: Ensure discount amount is not negative
            discountAmount = Math.max(discountAmount, 0);
            // Ensure taxable value is not negative
            const amountAfterDiscount = Math.max(totalPrice - discountAmount, 0);
            const taxAmount = amountAfterDiscount * ((item.tax || 0) / 100);
            const netAmount = amountAfterDiscount + taxAmount;

            updatedEntityItems[itemIndex] = {
              ...item,
              stockData: stockIds.join(','),
              quantity: newQuantity,
              discountAmount: discountAmount,
              taxAmount: taxAmount,
              netAmount: netAmount
            };
          }

          // Calculate totals immediately with updated items
          let totalQty = 0;
          let totalAmount = 0;
          let totalDiscount = 0;
          let totalTax = 0;
          let totalCost = 0;

          updatedEntityItems.forEach(item => {
            const lineTotal = item.unitPrice * item.quantity;
            const discountAmount = item.discountAmount || 0;
            const taxAmount = item.taxAmount || 0;
            const costAmount = (item.cost || 0) * item.quantity;

            totalQty += item.quantity;
            totalAmount += lineTotal;
            totalDiscount += discountAmount;
            totalTax += taxAmount;
            totalCost += costAmount;
          });

          // Apply overall discount percentage
          const overallDiscountAmount = (totalAmount * (prev.discountPercentage || 0)) / 100;
          const finalTotalDiscount = totalDiscount + overallDiscountAmount;

          // Calculate initial net amount: amount - discount + tax
          const initialNetAmount = totalAmount - finalTotalDiscount + totalTax;

          // Apply additional overall discount percentage to initial net amount
          const additionalOverallDiscountAmount = (initialNetAmount * (prev.overallDiscountPercentage || 0)) / 100;
          const finalNetAmount = initialNetAmount - additionalOverallDiscountAmount;

          return {
            ...prev,
            entityItems: updatedEntityItems,
            Invoice_TotalQty: totalQty,
            Invoice_TotalAmount: totalAmount,
            Invoice_DisAmount: finalTotalDiscount,
            Invoice_TaxAmount: totalTax,
            Invoice_NetAmount: finalNetAmount,
            CostAmount: totalCost,
            TotalCost: totalCost,
            overallDiscountAmount: additionalOverallDiscountAmount,
          };
        });
      }
    }
  };

  // Stock details modal handlers
  const handleOpenStockDetailsModal = (productName, stockDetails) => {
    // Temporarily lower SweetAlert z-index to ensure stock modal appears on top
    const sweetAlertContainers = document.querySelectorAll('.swal2-container');
    sweetAlertContainers.forEach(container => {
      container.style.zIndex = '1000';
    });

    setForm(prev => ({
      ...prev,
      showStockDetailsModal: true,
      currentStockDetailsProduct: productName,
      currentStockDetails: stockDetails || []
    }));
  };

  const addSelectedModalItems = async () => {
    // For products and services, use modalItems (paginated); for packages/packageSeries, use options
    const entityOptions =
      form.modalEntityType === 'product' ? modalItems :
        form.modalEntityType === 'service' ? modalItems :
          form.modalEntityType === 'package' ? packageOptions :
            form.modalEntityType === 'packageSeries' ? packageSeriesOptions :
              form.modalEntityType === 'giftcard' ? modalItems : [];

    // Track updated quantities for products (declared outside if block for scope)
    const updatedQuantities = {};

    // Enforce stock selection for products before adding
    if (form.modalEntityType === 'product') {
      const productsNeedingStock = [];

      // Only check products that are actually selected (have a valid quantity > 0)
      Object.entries(form.selectedModalItems)
        .filter(([entityId, quantity]) => {
          // Filter out undefined, null, empty string, or 0 values
          if (quantity === undefined || quantity === null || quantity === '') return false;
          const qty = parseInt(quantity);
          return !isNaN(qty) && qty > 0;
        })
        .forEach(([entityId, quantity]) => {
          const finalQuantity = parseInt(quantity) || 1;
          const stockData = form.selectedStockData[entityId];
          const productMeta = entityOptions.find(option => option.value == entityId);
          const productName = productMeta ? productMeta.label : `Product ${entityId}`;

          if (!stockData || stockData.length === 0) {
            productsNeedingStock.push(productName);
          } else {
            // Sum selected stock quantities for this product
            const selectedQty = stockData.reduce((sum, group) => {
              if (group && typeof group.quantity === 'number') return sum + group.quantity;
              if (group && Array.isArray(group.stocks)) return sum + group.stocks.length;
              return sum;
            }, 0);
            // Auto-update qty to match selected stock qty
            if (selectedQty !== finalQuantity) {
              updatedQuantities[entityId] = selectedQty;
            }
          }
        });

      // Update quantities if there are mismatches
      if (Object.keys(updatedQuantities).length > 0) {
        setForm(prev => ({
          ...prev,
          selectedModalItems: {
            ...prev.selectedModalItems,
            ...updatedQuantities
          }
        }));
      }

      // Only show alert if stock is not selected
      if (productsNeedingStock.length > 0) {
        Swal.fire(
          'Select Stock',
          `Please select stock for the highlighted products before adding.\n\n- Stock not selected: ${productsNeedingStock.join(', ')}`,
          'warning'
        );
        return;
      }
    }

    // Only process products that are actually selected (have a valid quantity > 0)
    // Use updated quantities if they were modified
    const quantitiesToUse = Object.keys(updatedQuantities || {}).length > 0
      ? { ...form.selectedModalItems, ...updatedQuantities }
      : form.selectedModalItems;

    // Separate package series from other items
    const selectedPackageSeries = [];
    const otherItems = [];

    Object.entries(quantitiesToUse)
      .filter(([entityId, quantity]) => {
        // Filter out undefined, null, empty string, or 0 values
        if (quantity === undefined || quantity === null || quantity === '') return false;
        const qty = parseInt(quantity);
        return !isNaN(qty) && qty > 0;
      })
      .forEach(([entityId, quantity]) => {
        const entityData = entityOptions.find(option => option.value == entityId);
        if (entityData) {
          if (form.modalEntityType === 'packageSeries') {
            // Collect package series to process together
            selectedPackageSeries.push({ entityId, entityData, quantity: 1 });
          } else if (form.modalEntityType === 'package') {
            // Packages can be added directly
            addEntityItem('package', entityData, 1);
          } else {
            // Products and services
            otherItems.push({ entityId, entityData, quantity });
          }
        }
      });

    // Process products and services
    otherItems.forEach(({ entityData, quantity }) => {
      const finalQuantity = parseInt(quantity) || 1;
      const stockData = form.selectedStockData[entityData.value];
      if (stockData && stockData.length > 0) {
        stockData.forEach(stockGroup => {
          addEntityItem(form.modalEntityType, entityData, stockGroup.quantity, stockGroup);
        });
      } else {
        addEntityItem(form.modalEntityType, entityData, finalQuantity);
      }
    });

    // Process package series - collect all and open modal with all series
    if (selectedPackageSeries.length > 0) {
      const packageSeriesForModal = [];

      // Check for duplicates and collect installments
      for (const { entityData } of selectedPackageSeries) {
        // Fix race condition: Try to find in packageSeries array first, if not found, fetch it
        let seriesEntity = packageSeries.find(s => s.seriesId === entityData.value && s.status === true);

        // If not found in array (race condition), fetch it from API
        if (!seriesEntity) {
          try {
            console.log(`Package series ${entityData.value} not found in cache, fetching from API...`);
            const fetchedSeries = await getSpaPackageSeries({ seriesId: entityData.value, status: true });
            if (fetchedSeries && fetchedSeries.length > 0 && fetchedSeries[0].status === true) {
              seriesEntity = fetchedSeries[0];
              // Update the packageSeries array for future use
              setPackageSeries(prev => {
                const exists = prev.find(s => s.seriesId === entityData.value);
                if (!exists) {
                  return [...prev, seriesEntity];
                }
                return prev;
              });
            }
          } catch (error) {
            console.error(`Error fetching package series ${entityData.value}:`, error);
          }
        }

        if (seriesEntity && seriesEntity.status === true) {
          // Check for duplicate membership
          if (form.Invoice_CustomerId) {
            try {
              const existingMemberships = await getCustomerMemberships({
                customerId: form.Invoice_CustomerId
              });

              if (existingMemberships && existingMemberships.length > 0) {
                const hasDuplicate = existingMemberships.some(m =>
                  (m.entity_Type === 'sep' || m.entity_Type === 'SEP') &&
                  m.entity_Id === entityData.value
                );

                if (hasDuplicate) {
                  const seriesName = seriesEntity.seriesName_En || `Package Series ${entityData.value}`;
                  const confirmResult = await Swal.fire({
                    icon: 'question',
                    title: 'Duplicate Membership',
                    text: `This customer already has this series (${seriesName}) in membership. Do you want to proceed?`,
                    showCancelButton: true,
                    confirmButtonText: 'Yes, Proceed',
                    cancelButtonText: 'Cancel',
                    confirmButtonColor: '#556ee6',
                    cancelButtonColor: '#6c757d'
                  });

                  if (!confirmResult.isConfirmed) {
                    continue; // Skip this series if user cancels
                  }
                }
              }
            } catch (error) {
              console.error('Error checking duplicate membership:', error);
              // Continue even if check fails
            }
          }

          if (seriesEntity.installments && seriesEntity.installments.length > 0) {
            const seriesInstallments = seriesEntity.installments.map(inst => ({
              installmentId: inst.installmentId,
              installmentName: inst.installmentName || `Installment ${inst.installmentId}`,
              amount: inst.installmentAmount || inst.InstallmentAmount || 0
            }));

            packageSeriesForModal.push({
              seriesId: entityData.value,
              seriesName: seriesEntity.seriesName_En || `Package Series ${entityData.value}`,
              installments: seriesInstallments,
              seriesEntity: seriesEntity
            });
          }
        }
      }

      if (packageSeriesForModal.length > 0) {
        setForm(prev => ({
          ...prev,
          currentPackageSeriesForInstallments: packageSeriesForModal,
          showMembershipModal: true
        }));
      }
    }

    closeEntityModal();
  };

  const handleGiftCardRedeem = async () => {
    if (!form.Invoice_CustomerId) {
      Swal.fire("Warning", "Please select a customer first", "warning");
      return;
    }

    const { value: code } = await Swal.fire({
      title: 'Redeem Gift Card',
      input: 'text',
      inputLabel: 'Enter Gift Card Code',
      inputPlaceholder: '10-character alphanumeric code',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'You need to enter a code!';
        }
      }
    });

    if (code) {
      try {
        setLoading(true);
        const response = await getGiftCardBalance(code);
        setLoading(false);

        if (response.isValid) {
          const { value: redeemAmount } = await Swal.fire({
            title: `Card: ${response.cardName}`,
            text: `Available Balance: ${currencyCode} ${response.balance.toFixed(2)}`,
            input: 'number',
            inputLabel: 'Redemption Amount',
            inputValue: Math.min(response.balance, form.Invoice_NetAmount).toFixed(2),
            showCancelButton: true,
            inputValidator: (value) => {
              if (!value || parseFloat(value) <= 0) {
                return 'Enter a valid amount!';
              }
              if (parseFloat(value) > response.balance) {
                return 'Amount exceeds available balance!';
              }
              // Allow redemption up to Invoice_NetAmount - but wait, 
              // we calculate NetAmount after redemption in calculateTotals.
              // So here we should probably check against current NetAmount correctly.
            }
          });

          if (redeemAmount) {
            const amountToRedeem = parseFloat(redeemAmount);
            setForm(prev => {
              const cashAmount = parseFloat(prev.CashAmount) || 0;
              const cashlessAmount = parseFloat(prev.CashlessAmount) || 0;
              const net = parseFloat(prev.Invoice_NetAmount) || 0;
              const tip = parseFloat(prev.TipAmount) || 0;

              let newCash = cashAmount;
              let newCashless = cashlessAmount;

              if (cashAmount > 0) {
                // Deduct from CashAmount
                newCash = Math.max(0, Math.round((cashAmount - amountToRedeem) * 100) / 100);
              } else if (cashlessAmount > 0) {
                // No cash — deduct from CashlessAmount
                newCashless = Math.max(0, Math.round((cashlessAmount - amountToRedeem) * 100) / 100);
              } else {
                // Fallback: derive from net + tip
                newCash = Math.max(0, Math.round((net + tip - amountToRedeem) * 100) / 100);
              }

              return {
                ...prev,
                GiftCardAmount: amountToRedeem,
                RedeemedGiftCardCode: code,
                CashAmount: newCash,
                CashlessAmount: newCashless === 0 ? null : newCashless
              };
            });
            Swal.fire("Success", "Gift card applied successfully", "success");
          }
        } else {
          Swal.fire("Error", response.message || "Invalid Gift Card Code", "error");
        }
      } catch (error) {
        setLoading(false);
        console.error("Error redeeming gift card:", error);
        Swal.fire("Error", "Failed to validate gift card", "error");
      }
    }
  };

  // Check if customer has memberships
  const [customerHasMembership, setCustomerHasMembership] = useState(false);
  // Track if customer has billable items (memberships with unpaid installments or unbilled services)
  const [customerHasBillableItems, setCustomerHasBillableItems] = useState(false);

  // Customer membership modal functions
  const openMembershipModal = async (customerId, forceOpen = false) => {
    if (!customerId) return;

    try {
      // Fetch both memberships and unbilled services in parallel
      // Note: We always pass BranchId as null for unbilled services to get ALL services
      // regardless of branch, because services might have been booked in different branches
      // and we want to allow billing them all in the same invoice
      const [memberships, unbilledServicesData] = await Promise.all([
        getCustomerMemberships({ customerId: customerId }),
        getCustomerUnbilledServices({
          CustomerId: customerId,
          BranchId: null // Always null to get all unbilled services regardless of branch
        }).catch(err => {
          console.error('Error fetching unbilled services:', err);
          return []; // Return empty array on error
        })
      ]);


      // Set membership status for button visibility
      setCustomerHasMembership(memberships && memberships.length > 0);

      // IMPORTANT: We need to check for billable items AFTER filtering out items already in the invoice
      // This prevents showing the button when all items are already in the invoice
      // Get already-added booking resource IDs from current entityItems using ref
      const alreadyAddedBookingResourceIds = entityItemsRef.current
        .map(item => item.bookingResourceDetailId)
        .filter(id => id !== null && id !== undefined);

      // Filter out unbilled services that are already in Selected Items
      const filteredUnbilledServices = (unbilledServicesData || []).filter(service => {
        const serviceId = service.bookingResourceDetail_Id || service.BookingResourceDetail_Id;
        return !alreadyAddedBookingResourceIds.includes(serviceId);
      });

      // Check if there are unpaid installments
      // Note: Installments are filtered in the modal display, but for button visibility,
      // we check if there are ANY unpaid installments regardless of whether they're in the invoice
      const hasUnpaidInstallments = memberships && memberships.some(membership => {
        if (!membership.installments || membership.installments.length === 0) {
          return false;
        }
        return membership.installments.some(installment => {
          const billedAmount = installment.billedAmount || installment.paidAmount || 0;
          const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
          return remainingAmount > 0;
        });
      });

      // Check if there are unbilled services AFTER filtering out already-added ones
      const hasUnbilledServices = filteredUnbilledServices && filteredUnbilledServices.length > 0;

      // Set billable items status (for icon display) - only if there are items after filtering
      setCustomerHasBillableItems(hasUnpaidInstallments || hasUnbilledServices);

      // Always process membership data if forceOpen is true OR if there are items to show
      // This ensures data is available when manually opening even if all items are filtered
      let membershipsWithPackageData = [];
      let installmentNames = {};
      let entityNames = {};

      // Always process data when manually opening (forceOpen) OR when there are items
      if (forceOpen || hasUnpaidInstallments || hasUnbilledServices || (memberships && memberships.length > 0)) {
        // Transform the data to match our expected format
        const transformedMemberships = memberships.map(membership => {
          const allInstallments = (membership.installments || []).map(installment => ({
            m_InstallmentId: installment.m_InstallmentId,
            membershipId: installment.membershipId,
            seriesId: installment.seriesId,
            installmentId: installment.installmentId,
            amount: installment.amount,
            paidAmount: installment.billedAmount || installment.paidAmount || 0
          }));

          // Calculate minimum installmentId from all installments (both paid and unpaid)
          const minInstallmentId = allInstallments.length > 0
            ? Math.min(...allInstallments.map(inst => inst.installmentId))
            : null;

          return {
            membershipId: membership.membershipId,
            customerId: membership.customerId,
            branchId: membership.branchId,
            entity_Id: membership.entity_Id,
            entity_Type: membership.entity_Type,
            purchaseDate: membership.purchaseDate,
            salesmanId: membership.salesmanId || membership.SalesmanId || membership.salesman_ID || membership.Salesman_ID || null,
            installments: allInstallments,
            minInstallmentId: minInstallmentId
          };
        });

        // Fetch entity names and package data for packages and series
        const packageDataMap = {};

        for (const membership of transformedMemberships) {
          try {
            if (isPackageType(membership.entity_Type)) {
              const packageData = await getSpaPackages({ packageId: membership.entity_Id });
              if (packageData && packageData.length > 0) {
                entityNames[membership.entity_Id] = packageData[0].packageName_En || `Package ${membership.entity_Id}`;
                packageDataMap[membership.entity_Id] = packageData[0];
              }
            } else if (isPackageSeriesType(membership.entity_Type)) {
              const seriesData = await getSpaPackageSeries({ seriesId: membership.entity_Id });
              if (seriesData && seriesData.length > 0) {
                entityNames[membership.entity_Id] = seriesData[0].seriesName_En || `Series ${membership.entity_Id}`;
              }
            }
          } catch (error) {
            console.error('Error fetching entity name:', error);
            entityNames[membership.entity_Id] = `${isPackageType(membership.entity_Type) ? 'Package' : 'Series'} ${membership.entity_Id}`;
          }
        }

        // Add package data to transformed memberships
        membershipsWithPackageData = transformedMemberships.map(membership => ({
          ...membership,
          packageData: isPackageType(membership.entity_Type) ? packageDataMap[membership.entity_Id] : null
        }));

        // Fetch installment names for package series
        for (const membership of membershipsWithPackageData) {
          if (isPackageSeriesType(membership.entity_Type) && membership.installments.length > 0) {
            try {
              const seriesData = await getSpaPackageSeries({ seriesId: membership.entity_Id });
              if (seriesData && seriesData.length > 0 && seriesData[0].installments) {
                seriesData[0].installments.forEach(installment => {
                  installmentNames[installment.installmentId] = installment.installmentName;
                });
              }
            } catch (error) {
              console.error('Error fetching installment names:', error);
            }
          }
        }
      }

      // Open modal if:
      // 1. forceOpen is true (manual click on icon/button), OR
      // 2. There are actually unpaid installments OR unbilled services (automatic)
      // Note: hasUnpaidInstallments and hasUnbilledServices are already calculated above with filtering
      if (forceOpen || hasUnpaidInstallments || hasUnbilledServices) {
        setForm(prev => {
          return {
            ...prev,
            showMembershipModal: true,
            selectedMembershipInstallments: {},
            selectedCompletePayments: {},
            customerMemberships: membershipsWithPackageData,
            installmentNames: installmentNames,
            entityNames: entityNames,
            unbilledServices: filteredUnbilledServices, // Use filtered list (already calculated above)
            selectedUnbilledServices: {}
          };
        });

      } else {
        // If no unpaid installments or unbilled services and not forceOpen, don't open modal
        // Just set empty data
        setForm(prev => ({
          ...prev,
          customerMemberships: [],
          installmentNames: {},
          entityNames: {},
          unbilledServices: [],
          selectedUnbilledServices: {}
        }));
      }
    } catch (error) {
      console.error('Error loading customer memberships:', error);
      Swal.fire("Error", "Failed to load customer data", "error");
    }
  };

  const closeMembershipModal = () => {
    setForm(prev => ({
      ...prev,
      showMembershipModal: false,
      selectedMembershipInstallments: {},
      selectedCompletePayments: {},
      newPackageSeriesInstallments: {},
      currentPackageSeriesForInstallments: [],
      // Keep customerMemberships for validation purposes
      installmentNames: {},
      entityNames: {}
    }));
  };

  // Function to manually open membership modal (for the button)
  const openMembershipModalManually = async () => {
    if (form.Invoice_CustomerId) {
      await openMembershipModal(form.Invoice_CustomerId, true);
    } else {
      Swal.fire("Warning", "Please select a customer first", "warning");
    }
  };

  const handleMembershipInstallmentCheck = (installmentId, checked) => {
    setForm(prev => ({
      ...prev,
      selectedMembershipInstallments: {
        ...prev.selectedMembershipInstallments,
        [installmentId]: {
          ...prev.selectedMembershipInstallments[installmentId],
          checked: checked,
          // Clear validation error when unchecking
          validationError: checked ? prev.selectedMembershipInstallments[installmentId]?.validationError : ''
        }
      }
    }));
  };

  const handleMembershipInstallmentAmountChange = (installmentId, amount) => {
    // Find the installment to get its remaining amount for validation
    const installment = form.customerMemberships
      .flatMap(membership => membership.installments)
      .find(inst => inst.installmentId === installmentId);

    const remainingAmount = installment ? Math.round((installment.amount - installment.paidAmount) * 100) / 100 : 0;

    // Allow empty string, but validate when there's a value
    let validatedAmount = amount;
    let validationError = '';

    if (amount !== '' && amount !== null && amount !== undefined && !isNaN(amount)) {
      const inputAmount = parseFloat(amount);
      // Validate that input amount doesn't exceed remaining amount
      if (inputAmount > remainingAmount) {
        validationError = `Amount cannot exceed remaining balance of ${remainingAmount.toFixed(2)}`;
        validatedAmount = amount; // Keep the original input to show the error
      } else {
        validatedAmount = inputAmount;
      }
    }

    setForm(prev => ({
      ...prev,
      selectedMembershipInstallments: {
        ...prev.selectedMembershipInstallments,
        [installmentId]: {
          ...prev.selectedMembershipInstallments[installmentId],
          amount: validatedAmount,
          validationError: validationError
        }
      }
    }));
  };

  // Handler for Complete Payment checkbox (for packages without installments)
  const handleCompletePaymentCheck = (membershipId, checked) => {
    setForm(prev => {
      const membership = prev.customerMemberships.find(m => m.membershipId === membershipId);
      // Use only Amount (before tax), never NetAmount to avoid double tax calculation
      const packageAmount = membership?.packageData?.amount || 0;

      return {
        ...prev,
        selectedCompletePayments: {
          ...prev.selectedCompletePayments,
          [membershipId]: {
            ...prev.selectedCompletePayments[membershipId],
            checked: checked,
            amount: checked ? packageAmount : (prev.selectedCompletePayments[membershipId]?.amount || ''),
            validationError: ''
          }
        }
      };
    });
  };

  // Handler for Complete Payment amount change
  const handleCompletePaymentAmountChange = (membershipId, amount) => {
    const membership = form.customerMemberships.find(m => m.membershipId === membershipId);
    // Use only Amount (before tax), never NetAmount to avoid double tax calculation
    const packageAmount = membership?.packageData?.amount || 0;

    let validatedAmount = amount;
    let validationError = '';

    if (amount !== '' && amount !== null && amount !== undefined && !isNaN(amount)) {
      const numAmount = parseFloat(amount);
      if (numAmount <= 0) {
        validationError = 'Amount must be greater than 0';
      } else if (numAmount > packageAmount) {
        validationError = `Amount cannot exceed package amount of ${packageAmount.toFixed(2)}`;
      }
    }

    setForm(prev => ({
      ...prev,
      selectedCompletePayments: {
        ...prev.selectedCompletePayments,
        [membershipId]: {
          ...prev.selectedCompletePayments[membershipId],
          amount: validatedAmount,
          validationError: validationError
        }
      }
    }));
  };

  const addSelectedMembershipInstallments = async () => {
    // Check for validation errors first (installments, complete payments, and unbilled services)
    const hasInstallmentValidationErrors = Object.values(form.selectedMembershipInstallments).some(data =>
      data.checked && data.validationError
    );
    const hasCompletePaymentValidationErrors = Object.values(form.selectedCompletePayments).some(data =>
      data.checked && data.validationError
    );
    const hasNewPackageSeriesValidationErrors = Object.values(form.newPackageSeriesInstallments).some(data =>
      data.checked && data.validationError
    );
    const hasUnbilledServicesValidationErrors = Object.values(form.selectedUnbilledServices).some(data =>
      data.checked && data.validationError
    );

    if (hasInstallmentValidationErrors || hasCompletePaymentValidationErrors || hasNewPackageSeriesValidationErrors || hasUnbilledServicesValidationErrors) {
      Swal.fire("Validation Error", "Please fix the validation errors before proceeding.", "error");
      return;
    }

    // Handle new package series installments first (can be multiple series)
    if (form.currentPackageSeriesForInstallments && form.currentPackageSeriesForInstallments.length > 0) {
      for (const seriesData of form.currentPackageSeriesForInstallments) {
        const seriesId = seriesData.seriesId;
        const seriesName = seriesData.seriesName;

        // Fix race condition: Try stored seriesEntity first, then packageSeries array, then fetch from API
        let seriesEntity = seriesData.seriesEntity || packageSeries.find(s => s.seriesId === seriesId);

        // If still not found, fetch from API
        if (!seriesEntity) {
          try {
            console.log(`Package series ${seriesId} not found in cache, fetching from API...`);
            const fetchedSeries = await getSpaPackageSeries({ seriesId: seriesId });
            if (fetchedSeries && fetchedSeries.length > 0) {
              seriesEntity = fetchedSeries[0];
              // Update the packageSeries array for future use
              setPackageSeries(prev => {
                const exists = prev.find(s => s.seriesId === seriesId);
                if (!exists) {
                  return [...prev, seriesEntity];
                }
                return prev;
              });
            }
          } catch (error) {
            console.error(`Error fetching package series ${seriesId}:`, error);
          }
        }

        if (!seriesEntity) {
          Swal.fire("Error", `Package series ${seriesId} not found. Please refresh the page.`, "error");
          continue;
        }

        for (const [installmentKey, data] of Object.entries(form.newPackageSeriesInstallments)) {
          if (data.checked && data.amount > 0 && installmentKey.startsWith(`new_${seriesId}_`)) {
            const installmentId = parseInt(installmentKey.split('_')[2]);
            const installment = seriesData.installments.find(inst => inst.installmentId === installmentId);

            if (installment) {
              // Store installment info in the entity item for later use when saving membership
              // For new installments, we treat the amount as NET and reverse-calculate GROSS for addEntityItemWithCustomPrice
              let grossAmount = data.amount;
              if (seriesEntity) {
                const discountPercentage = seriesEntity.discountPercentage || 0;
                let totalTaxPercentage = 0;
                if (seriesEntity.taxIds && seriesEntity.taxIds.length > 0) {
                  totalTaxPercentage = seriesEntity.taxIds.reduce((total, taxId) => {
                    const taxData = taxes.find(t => t.tax_Id === taxId);
                    return total + (taxData?.tax_Perc || 0);
                  }, 0);
                }
                const combinedFactor = (1 - (discountPercentage / 100)) * (1 + (totalTaxPercentage / 100));
                if (combinedFactor > 0) {
                  grossAmount = Math.round((data.amount / combinedFactor) * 100) / 100;
                }
              }

              const entityData = {
                value: seriesId,
                label: `${seriesName} - ${installment.installmentName}`,
                installmentId: installmentId,
                installmentName: installment.installmentName,
                originalAmount: installment.amount,
                isNewPackageSeriesInstallment: true, // Flag to identify new installments
                seriesEntityData: seriesEntity // Store full series entity data for membership creation
              };

              // Add to entity items with reverse-calculated unit price
              await addEntityItemWithCustomPrice('packageSeries', entityData, 1, grossAmount, seriesEntity);
            }
          }
        }

      }

      // Check if at least one installment was selected from any series
      const hasSelectedInstallments = Object.values(form.newPackageSeriesInstallments).some(data =>
        data.checked && data.amount > 0
      );

      if (!hasSelectedInstallments) {
        Swal.fire("Warning", "Please select at least one installment for the package series.", "warning");
        return;
      }
    }

    for (const [installmentId, data] of Object.entries(form.selectedMembershipInstallments)) {
      if (data.checked && data.amount > 0) {
        // Find the membership and installment details
        let membership = form.customerMemberships.find(m =>
          m.installments && m.installments.some(inst => inst.installmentId.toString() === installmentId)
        );

        console.log('🔍 [Membership Installment] Finding membership:', {
          installmentId,
          foundMembership: !!membership,
          membershipId: membership?.membershipId,
          membershipSalesmanIds: membership?.salesmanIds,
          membershipSalesmanId: membership?.salesmanId,
          fullMembershipObject: membership, // Log full object to see what properties it has
          allMemberships: form.customerMemberships.map(m => ({
            membershipId: m.membershipId,
            salesmanIds: m.salesmanIds,
            salesmanId: m.salesmanId,
            allKeys: Object.keys(m) // Show all keys in the membership object
          }))
        });

        // If membership doesn't have salesmanIds, fetch fresh from API
        if (membership && (!membership.salesmanIds || !Array.isArray(membership.salesmanIds) || membership.salesmanIds.length === 0)) {
          try {
            console.log('🔍 [Membership Installment] Fetching fresh membership data from API...');
            const freshMemberships = await getCustomerMemberships({ customerId: form.Invoice_CustomerId });
            const freshMembership = freshMemberships?.find(m => m.membershipId === membership.membershipId);
            if (freshMembership && freshMembership.salesmanIds && Array.isArray(freshMembership.salesmanIds) && freshMembership.salesmanIds.length > 0) {
              console.log('🔍 [Membership Installment] Found salesmanIds in fresh API data:', {
                freshSalesmanIds: freshMembership.salesmanIds
              });
              // Update the membership object with fresh data
              membership.salesmanIds = freshMembership.salesmanIds.map(id => parseInt(id)).filter(id => id > 0);
              // Also update in form.customerMemberships for future lookups
              const membershipIndex = form.customerMemberships.findIndex(m => m.membershipId === membership.membershipId);
              if (membershipIndex !== -1) {
                form.customerMemberships[membershipIndex].salesmanIds = membership.salesmanIds;
              }
            }
          } catch (error) {
            console.error('🔍 [Membership Installment] Error fetching fresh membership:', error);
          }
        }

        if (membership) {
          const installment = membership.installments.find(inst =>
            inst.installmentId.toString() === installmentId
          );

          if (installment) {
            // Determine entity type and create appropriate entity data
            const entityType = isPackageType(membership.entity_Type) ? 'package' : 'packageSeries';
            const entityId = membership.entity_Id;

            // Find the entity name and get parent entity data
            let entityName = '';
            let parentEntityData = null;
            if (entityType === 'package') {
              const pkg = packages.find(p => p.packageId === entityId && p.status === true);
              entityName = pkg?.packageName_En || `Package ${entityId}`;
              parentEntityData = pkg; // Use package data for discount/tax
            } else {
              // Fix race condition: Try to find in packageSeries array first, if not found, fetch it
              let series = packageSeries.find(s => s.seriesId === entityId && s.status === true);

              // If not found, fetch from API
              if (!series) {
                try {
                  console.log(`Package series ${entityId} not found in cache, fetching from API...`);
                  const fetchedSeries = await getSpaPackageSeries({ seriesId: entityId, status: true });
                  if (fetchedSeries && fetchedSeries.length > 0 && fetchedSeries[0].status === true) {
                    series = fetchedSeries[0];
                    // Update the packageSeries array for future use
                    setPackageSeries(prev => {
                      const exists = prev.find(s => s.seriesId === entityId);
                      if (!exists) {
                        return [...prev, series];
                      }
                      return prev;
                    });
                  }
                } catch (error) {
                  console.error(`Error fetching package series ${entityId}:`, error);
                }
              }

              entityName = series?.seriesName_En || `Package Series ${entityId}`;
              parentEntityData = series; // Use package series data for discount/tax
            }

            // Get installment name
            const installmentName = form.installmentNames[installmentId] || `Installment ${installmentId}`;

            // For existing membership installments, calculate gross amount from net amount
            // installment.amount is the NET amount (396), but we need GROSS amount (400) as unit price
            let grossAmount = data.amount; // Default to data.amount

            if (entityType === 'packageSeries' && parentEntityData) {
              // Reverse calculate gross amount from net amount
              // Formula: Net = Gross * (1 - Discount%) * (1 + Tax%)
              // Therefore: Gross = Net / ((1 - Discount%) * (1 + Tax%))
              const discountPercentage = parentEntityData.discountPercentage || 0;
              let totalTaxPercentage = 0;

              if (parentEntityData.taxIds && parentEntityData.taxIds.length > 0) {
                totalTaxPercentage = parentEntityData.taxIds.reduce((total, taxId) => {
                  const taxData = taxes.find(t => t.tax_Id === taxId);
                  return total + (taxData?.tax_Perc || 0);
                }, 0);
              }

              // Calculate gross amount from net amount
              const discountFactor = 1 - (discountPercentage / 100);
              const taxFactor = 1 + (totalTaxPercentage / 100);
              const combinedFactor = discountFactor * taxFactor;

              if (combinedFactor > 0) {
                grossAmount = data.amount / combinedFactor;
                // Round to 2 decimal places
                grossAmount = Math.round(grossAmount * 100) / 100;
              }
            }

            // Create entity data object
            // Get salesmanIds array from membership (prefer array, fallback to single salesmanId)
            // IMPORTANT: Check all possible property names and also check the raw API response if available
            let membershipSalesmanIds = [];

            // First, try to get from the membership object directly
            if (membership.salesmanIds && Array.isArray(membership.salesmanIds) && membership.salesmanIds.length > 0) {
              membershipSalesmanIds = membership.salesmanIds.map(id => parseInt(id)).filter(id => id > 0);
            } else {
              // Fallback: Try to get from raw API response if membership was fetched directly
              // Check if we can access the original API response
              const rawMembership = form.customerMemberships.find(m => m.membershipId === membership.membershipId);
              if (rawMembership && rawMembership.salesmanIds && Array.isArray(rawMembership.salesmanIds) && rawMembership.salesmanIds.length > 0) {
                membershipSalesmanIds = rawMembership.salesmanIds.map(id => parseInt(id)).filter(id => id > 0);
              } else {
                // Final fallback: single salesmanId
                const singleSalesmanId = membership.salesmanId || membership.SalesmanId || membership.salesman_ID || membership.Salesman_ID || null;
                if (singleSalesmanId && singleSalesmanId > 0) {
                  membershipSalesmanIds = [parseInt(singleSalesmanId)];
                }
              }
            }

            console.log('🔍 [Membership Installment] Extracted salesmanIds:', {
              membershipId: membership.membershipId,
              membershipObject: membership,
              membershipSalesmanIds_raw: membership.salesmanIds,
              membershipSalesmanId_single: membership.salesmanId,
              allMembershipKeys: Object.keys(membership),
              extractedSalesmanIds: membershipSalesmanIds,
              entityId,
              installmentId
            });

            const entityData = {
              value: entityId,
              label: `${entityName} - ${installmentName}`,
              installmentId: installmentId,
              installmentName: installmentName,
              originalAmount: grossAmount, // Store gross amount for reference
              m_InstallmentId: installment.m_InstallmentId, // Add the membership installment ID
              membershipSalesmanIds: membershipSalesmanIds // Pass salesmanIds array from membership
            };

            console.log('🔍 [Membership Installment] EntityData created:', {
              entityData,
              membershipSalesmanIds_in_entityData: entityData.membershipSalesmanIds
            });

            // Add to entity items with GROSS amount as unit price (400), not net amount (396)
            // The discount/tax will be applied in addEntityItemWithCustomPrice to calculate net amount
            await addEntityItemWithCustomPrice(entityType, entityData, 1, grossAmount, parentEntityData);
          } else {
            console.error('Installment not found in membership:', { installmentId, membership });
          }
        } else {
          console.error('Membership not found for installment:', { installmentId, availableMemberships: form.customerMemberships });
        }
      }
    }

    // Handle Complete Payment selections (for packages without installments)
    for (const [membershipId, data] of Object.entries(form.selectedCompletePayments)) {
      if (data.checked && data.amount > 0) {
        const membership = form.customerMemberships.find(m => m.membershipId.toString() === membershipId);

        if (membership && isPackageType(membership.entity_Type)) {
          const entityId = membership.entity_Id;
          const entityName = form.entityNames[entityId] || `Package ${entityId}`;
          const packageData = membership.packageData;

          // Create entity data object
          const entityData = {
            value: entityId,
            label: `${entityName} - Complete Payment`,
            isCompletePayment: true,
            membershipId: membership.membershipId
          };

          // Add to entity items with custom price, using package data for discount/tax
          await addEntityItemWithCustomPrice('package', entityData, 1, data.amount, packageData);
        }
      }
    }

    // Handle Unbilled Services from Bookings
    let sourceToSetForInvoice = null;

    for (const [serviceKey, data] of Object.entries(form.selectedUnbilledServices)) {
      if (data.checked && data.amount > 0) {
        // Extract BookingResourceDetail_Id from serviceKey (format: "unbilled_123")
        const bookingResourceDetailId = parseInt(serviceKey.replace('unbilled_', ''));
        // Handle both camelCase (from API) and PascalCase (if transformed)
        const service = form.unbilledServices.find(s =>
          (s.bookingResourceDetail_Id || s.BookingResourceDetail_Id) === bookingResourceDetailId
        );

        if (service) {
          // Handle both camelCase and PascalCase from API
          const packageId = service.package_Id || service.Package_Id;
          const serviceId = service.service_Id || service.Service_Id;
          const bookingId = service.booking_Id || service.Booking_Id;
          const bookingDetailId = service.bookingDetail_Id || service.BookingDetail_Id;

          // Determine entity type based on whether it's a service or package
          const entityType = packageId ? 'package' : 'service';
          const entityId = packageId || serviceId;
          const serviceName = service.spaServiceName_En || service.SpaServiceName_En;
          const packageName = service.packageName_En || service.PackageName_En;

          // For packages, show package name; for services, show service name
          const entityName = packageId
            ? packageName || `Package ${packageId}`
            : serviceName || `Service ${serviceId}`;

          // Get parent entity data for discount/tax
          let parentEntityData = null;
          if (entityType === 'package') {
            parentEntityData = packages.find(p => p.packageId === entityId);
          } else {
            // For services, try to find in services array first
            parentEntityData = services.find(s => s.value === entityId);

            // Get tax information from API response (handle both camelCase and PascalCase)
            const serviceTaxIds = service.serviceTaxIds || service.ServiceTaxIds;
            const serviceDiscountPercentage = service.serviceDiscountPercentage || service.ServiceDiscountPercentage || 0;
            const serviceTaxPercentage = service.serviceTaxPercentage || service.ServiceTaxPercentage || 0;

            // If not found or missing taxIds, parse from serviceTaxIds
            if (parentEntityData && serviceTaxIds) {
              // Parse comma-separated tax IDs and ensure parentEntityData has taxIds array
              const taxIds = serviceTaxIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
              if (taxIds.length > 0) {
                parentEntityData = {
                  ...parentEntityData,
                  taxIds: taxIds
                };
              }
            } else if (!parentEntityData && serviceTaxIds) {
              // If service not found in services array, create a basic parentEntityData with tax info
              const taxIds = serviceTaxIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
              parentEntityData = {
                taxIds: taxIds,
                discountPercentage: serviceDiscountPercentage,
                taxPercentage: serviceTaxPercentage
              };
            } else if (!parentEntityData) {
              // Fallback: create minimal parentEntityData
              parentEntityData = {
                taxIds: [],
                discountPercentage: serviceDiscountPercentage,
                taxPercentage: serviceTaxPercentage
              };
            }
          }

          // Create entity data object with booking reference
          // For packages, include BookingDetail_Id and Booking_Id so invoice procedure can find all services
          const entityData = {
            value: entityId,
            label: `${entityName} (Booking #${bookingId})`,
            bookingResourceDetailId: bookingResourceDetailId, // Store for later reference
            bookingId: bookingId,
            bookingDetailId: bookingDetailId, // Store BookingDetail_Id for package billing
            serviceId: serviceId,
            packageId: packageId
          };

          // For unbilled services, reverse-calculate gross from net (data.amount is net)
          let grossAmount = data.amount;
          if (parentEntityData) {
            const discountPercentage = parentEntityData.discountPercentage || 0;
            let totalTaxPercentage = 0;

            // For services, we calculate tax based on parentEntityData (which we populated from service.ServiceTaxIds)
            // For packages, we use the package definition taxes
            if (parentEntityData.taxIds && parentEntityData.taxIds.length > 0) {
              totalTaxPercentage = parentEntityData.taxIds.reduce((total, taxId) => {
                const taxData = taxes.find(t => t.tax_Id === taxId);
                return total + (taxData?.tax_Perc || 0);
              }, 0);
            }

            const combinedFactor = (1 - (discountPercentage / 100)) * (1 + (totalTaxPercentage / 100));
            if (combinedFactor > 0) {
              grossAmount = Math.round((data.amount / combinedFactor) * 100) / 100;
            }
          }

          // Add to entity items with reverse-calculated price
          await addEntityItemWithCustomPrice(entityType, entityData, 1, grossAmount, parentEntityData);


          // Auto-populate invoice source from the first unbilled service that has a source ID
          // and if the invoice doesn't already have one
          const serviceSourceId = service.source_Id || service.Source_Id || service.invoiceSourceId || service.InvoiceSourceId;
          if (serviceSourceId && !sourceToSetForInvoice && !form.Invoice_SourceId) {
            sourceToSetForInvoice = serviceSourceId;
          }
        }
      }
    }

    if (sourceToSetForInvoice) {
      setForm(prev => ({
        ...prev,
        Invoice_SourceId: sourceToSetForInvoice
      }));
    }

    closeMembershipModal();
  };


  // Dropdown helper function
  const toggleDropdown = (itemId) => {
    setForm(prev => ({
      ...prev,
      dropdownOpen: {
        ...prev.dropdownOpen,
        [itemId]: !prev.dropdownOpen[itemId]
      }
    }));
  };

  // Helper functions
  const getCustomerName = (customerId) => {
    if (!customerId) return "N/A";
    const customer = customers.find(c => c.customer_Id === customerId);
    return customer ? customer.customer_NameEn : "N/A";
  };

  const getBusinessName = (businessId) => {
    const business = businesses.find(b => b.business_Id === businessId);
    return business ? business.business_NameEn : "N/A";
  };

  const getBranchName = (branchId) => {
    if (!branchId) return "N/A";
    const branch = branches.find(b => b.branch_Id === branchId);
    return branch ? branch.branch_NameEn : "N/A";
  };

  const getPaymentMethodName = (method) => {
    if (!method) return "N/A";
    const option = paymentMethodOptions.find(opt => opt.value === method);
    return option ? option.label : method;
  };

  const getInvoiceTypeName = (type) => {
    if (!type) return "N/A";
    switch (type.toLowerCase()) {
      case "cr": return "Credit";
      case "ca": return "Cash";
      default: return type.toUpperCase();
    }
  };

  // Filter invoices based on isReturn flag
  const filteredInvoices = useMemo(() => {
    if (invoiceListType === 'return') {
      return invoices.filter(inv => inv.isReturn === true || inv.IsReturn === true);
    } else {
      return invoices.filter(inv => !inv.isReturn && !inv.IsReturn);
    }
  }, [invoices, invoiceListType]);

  // Calculate pagination based on filtered invoices
  const filteredTotalCount = filteredInvoices.length;
  const totalPages = Math.ceil(filteredTotalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredInvoices.slice(startIndex, endIndex);

  // Reset to page 1 when switching between invoice types
  useEffect(() => {
    setPage(1);
  }, [invoiceListType]);

  // Options for dropdowns
  const customerOptions = useMemo(() => {
    return customers.map(customer => ({
      value: customer.customer_Id,
      label: customer.customer_NameEn
    }));
  }, [customers]);

  const businessOptions = useMemo(() => {
    return businesses.map(business => ({
      value: business.business_Id,
      label: business.business_NameEn
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    // Filter branches based on selected business
    const filteredBranches = form.Invoice_BusinessId
      ? branches.filter(branch => branch.branch_Buisness_Id === form.Invoice_BusinessId)
      : branches;

    return filteredBranches.map(branch => ({
      value: branch.branch_Id,
      label: branch.branch_NameEn
    }));
  }, [branches, form.Invoice_BusinessId]);

  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.product_Id,
      label: product.product_NameEn
    }));
  }, [products]);

  const serviceOptions = useMemo(() => {
    // Filter to only include active services
    return services
      .filter(service => service.status === true)
      .map(service => ({
        value: service.spaService_Id,
        label: service.spaServiceName_En
      }));
  }, [services]);

  const packageOptions = useMemo(() => {
    // Filter to only include active packages
    return packages
      .filter(pkg => pkg.status === true)
      .map(pkg => ({
        value: pkg.packageId,
        label: pkg.packageName_En
      }));
  }, [packages]);

  const packageSeriesOptions = useMemo(() => {
    // Filter to only include active package series
    return packageSeries
      .filter(series => series.status === true)
      .map(series => ({
        value: series.seriesId,
        label: series.seriesName_En
      }));
  }, [packageSeries]);

  // Handle print modal close - show prompt to go to booking page
  const handlePrintModalClose = async () => {
    console.log('🔍 [handlePrintModalClose] Called');
    console.log('🔍 [handlePrintModalClose] isFromBooking state:', isFromBooking);
    console.log('🔍 [handlePrintModalClose] location.state:', location.state);

    if (!invoicePrintData || !invoicePrintData.header) {
      setShowInvoicePrintModal(false);
      setInvoicePrintData(null);
      return;
    }

    // Extract services from invoice (excluding products)
    const invoiceDetails = invoicePrintData.details || [];
    const servicesFromInvoice = invoiceDetails.filter(detail => {
      const entityType = (detail.entityType || detail.EntityType || '').toLowerCase();
      // Include only services, packages, and package series (exclude products)
      return entityType === 'ser' || entityType === 'pac' || entityType === 'sep';
    });

    // Check if coming from booking page
    // The key insight: location.state can persist with bookingId even after user starts a new invoice
    // So we only consider it "from booking" if the ref is true
    // The ref is set to true when booking data is processed, and reset to false when:
    // 1. No booking data in location.state (user navigated directly or cleared state)
    // 2. Form is reset AND no booking data in location.state (user started new invoice)
    // This way, if user comes from booking, creates invoice, then starts NEW invoice directly, ref will be false
    const hasBookingId = location.state?.bookingId || location.state?.BookingId;
    // Only consider it from booking if ref is true (was set when booking data was processed)
    // AND we have bookingId in location.state (confirms we came from booking)
    const comingFromBooking = isFromBookingRef.current && hasBookingId;

    console.log('🔍 [handlePrintModalClose] isFromBooking state:', isFromBooking);
    console.log('🔍 [handlePrintModalClose] isFromBookingRef.current:', isFromBookingRef.current);
    console.log('🔍 [handlePrintModalClose] hasBookingId:', hasBookingId);
    console.log('🔍 [handlePrintModalClose] comingFromBooking (calculated):', comingFromBooking);
    console.log('🔍 [handlePrintModalClose] servicesFromInvoice.length:', servicesFromInvoice.length);

    // Only show prompt if there are services (not just products) AND not coming from booking page
    if (servicesFromInvoice.length > 0 && !comingFromBooking) {
      console.log('✅ [handlePrintModalClose] Showing prompt - not from booking');
      const customerId = invoicePrintData.header.invoice_CustomerId || invoicePrintData.header.Invoice_CustomerId;
      const businessId = invoicePrintData.header.invoice_BusinessId || invoicePrintData.header.Invoice_BusinessId;
      const branchId = invoicePrintData.header.invoice_BranchId || invoicePrintData.header.Invoice_BranchId;

      Swal.fire({
        title: 'Proceed to Booking?',
        text: 'Would you like to go to the booking page to book the services from this invoice?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#556ee6',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, Go to Booking',
        cancelButtonText: 'No, Stay Here'
      }).then((result) => {
        // Close modal first
        setShowInvoicePrintModal(false);

        if (result.isConfirmed && customerId) {
          // Navigate to booking page with invoice data
          navigate('/GmsAddBooking', {
            state: {
              fromInvoice: true,
              customerId: customerId,
              businessId: businessId,
              branchId: branchId,
              sourceId: invoicePrintData.header.invoice_SourceId || invoicePrintData.header.Invoice_SourceId,
              invoiceId: invoicePrintData.header.invoice_Id || invoicePrintData.header.Invoice_Id,
              invoiceDetails: servicesFromInvoice.map(detail => ({
                detailId: detail.detail_Id || detail.Detail_Id,
                entityId: detail.entityId || detail.EntityId,
                entityType: detail.entityType || detail.EntityType,
                quantity: detail.quantity || detail.Quantity,
                serviceId: (detail.entityType || detail.EntityType || '').toLowerCase() === 'ser'
                  ? (detail.entityId || detail.EntityId)
                  : null
              }))
            }
          });
        }

        // Clear invoice print data after navigation decision
        setInvoicePrintData(null);
      });
    } else {
      // No services or coming from booking page, just close modal
      console.log('❌ [handlePrintModalClose] NOT showing prompt - comingFromBooking:', comingFromBooking, 'servicesFromInvoice.length:', servicesFromInvoice.length);
      setShowInvoicePrintModal(false);
      setInvoicePrintData(null);
    }
  };

  // Invoice Print Modal Component
  const InvoicePrintModal = ({ isOpen, toggle, invoiceData }) => {
    if (!invoiceData || !invoiceData.header) return null;

    const printRef = useRef(null);

    const header = invoiceData.header;
    const details = invoiceData.details || [];
    const taxes = invoiceData.taxes || [];

    // Extract values with fallback for camelCase/PascalCase
    const invoiceNo = header.invoice_No || header.Invoice_No;
    const invoiceDate = header.invoice_Date || header.Invoice_Date;
    const customerName = header.customer_NameEn || header.Customer_NameEn;
    const customerAddress = header.customer_AddressEn || header.Customer_AddressEn;
    const customerTRN = header.customer_TRN || header.Customer_TRN;
    const customerContactNo = header.customer_ContactNo || header.Customer_ContactNo;
    const businessName = header.business_NameEn || header.Business_NameEn;
    const branchName = header.branch_NameEn || header.Branch_NameEn;
    const businessTRN = header.business_TRN || header.Business_TRN;
    const companyLogoPath = header.company_LogoPath || header.Company_LogoPath;
    const invoiceType = header.invoice_Type || header.Invoice_Type;

    // Get business logo with fallback to company logo
    const invoiceBusinessId = header.invoice_BusinessId || header.Invoice_BusinessId;
    const business = businesses.find(b => b.business_Id === invoiceBusinessId);
    const businessLogoPath = business?.business_LogoPath || null;
    const logoPath = businessLogoPath || companyLogoPath;
    const cashAmount = header.cashAmount || header.CashAmount || 0;
    const cashlessAmount = header.cashlessAmount || header.CashlessAmount || 0;
    const paidAmount = header.paidAmount || header.PaidAmount || 0;
    const tipAmount = header.tipAmount || header.TipAmount || 0;
    const totalVAT = header.invoice_TaxAmount || header.Invoice_TaxAmount || 0;
    const overallDiscount = header.overallDiscountAmount || header.OverallDiscountAmount || 0;
    const grandTotal = header.invoice_NetAmount || header.Invoice_NetAmount || 0;
    const giftCardAmount = header.giftCardAmount || header.GiftCardAmount || 0;
    const pointsAmount = header.pointsAmount || header.PointsAmount || 0;
    const paymentMethod = header.paymentMethod || header.PaymentMethod || '';

    const formatAmount = (amount) => {
      return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getCashlessLabel = (method) => {
      switch (method) {
        case 'cc': return 'Credit Card Amount';
        case 'ba': return 'Bank Transfer Amount';
        case 'on': return 'Online Payment Amount';
        case 'ta': return 'Tabee Amount';
        default: return 'Cashless Amount';
      }
    };
    const cashlessLabel = getCashlessLabel(paymentMethod);

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        return 'N/A';
      }
    };

    const handlePrint = () => {
      if (!printRef.current) return;

      const now = new Date();
      const formattedDate = now.toISOString().replace(/[-:]/g, "").replace(/\..+/, "");
      const fileName = `Invoice_${invoiceNo}_${formattedDate}`;

      const element = printRef.current;

      const options = {
        margin: [10, 10, 10, 10],
        filename: `${fileName}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
        },
        jsPDF: {
          unit: "pt",
          format: "a4",
          orientation: "portrait",
        },
      };

      html2pdf().set(options).from(element).save();
    };

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <style>
          {`
            .invoice-print-content,
            .invoice-print-content * {
              color: black !important;
            }
          `}
        </style>
        <ModalHeader toggle={toggle}>
          Invoice - {header.invoice_No || header.Invoice_No}
        </ModalHeader>
        <ModalBody>
          <div ref={printRef} className="border p-4 invoice-print-content" style={{ color: 'black' }}>
            <div className="text-center mb-4">
              {logoPath && (
                <div className="mb-3">
                  <img
                    src={`${BASE_URL}${logoPath.startsWith('/') ? logoPath : '/' + logoPath}`}
                    alt="Business Logo"
                    style={{
                      maxHeight: '60px',
                      maxWidth: '200px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <h4><strong>{(businessName || 'COMPANY').toUpperCase()}</strong></h4>
              {branchName && <h5>{branchName}</h5>}
              <h4>TAX INVOICE</h4>
              {businessTRN && businessTRN.trim() !== '' && (
                <p className="mb-0"><strong>TRN:</strong> {businessTRN}</p>
              )}
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <table className="table table-bordered table-sm">
                  <tbody>
                    <tr>
                      <td><strong>Customer Name:</strong> {customerName || 'N/A'}{customerContactNo && customerContactNo.trim() !== '' && ` (${customerContactNo})`}</td>
                      <td><strong>Date:</strong> {formatDate(invoiceDate)}</td>
                    </tr>
                    {customerTRN && customerTRN.trim() !== '' && (
                      <tr>
                        <td colSpan="2"><strong>TRN:</strong> {customerTRN}</td>
                      </tr>
                    )}
                    {customerAddress && customerAddress.trim() !== '' && (
                      <tr>
                        <td colSpan="2"><strong>Address:</strong> {customerAddress}</td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="2"><strong>Invoice No.:</strong> {invoiceNo}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th>S.No / الرقم</th>
                      <th>Description / الوصف</th>
                      <th className="text-end">Price / السعر</th>
                      <th className="text-end">Qty / الكمية</th>
                      <th className="text-end">Disc. / الخصم</th>
                      <th className="text-end">Taxable Amount / المبلغ الخاضع للضريبة</th>
                      <th className="text-end">VAT / ضريبة القيمة المضافة</th>
                      <th className="text-end">Total / الإجمالي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((detail, index) => {
                      const netAmount = detail.netAmount || detail.NetAmount || 0;
                      const taxAmt = detail.taxAmt || detail.TaxAmt || 0;
                      const taxableAmount = netAmount - taxAmt;
                      return (
                        <tr key={detail.detail_Id || detail.Detail_Id}>
                          <td>{index + 1}</td>
                          <td>
                            {detail.entityName || detail.EntityName || 'Unknown Item'}
                            {(detail.entityType === 'gif' || detail.EntityType === 'gif') && detail.stockData && ` (${detail.stockData})`}
                          </td>
                          <td className="text-end">{formatAmount(detail.price || detail.Price || 0)}</td>
                          <td className="text-end">{Math.round(detail.quantity || detail.Quantity || 0)}</td>
                          <td className="text-end">{formatAmount(detail.disAmount || detail.DisAmount || 0)}</td>
                          <td className="text-end">{formatAmount(taxableAmount)}</td>
                          <td className="text-end">{formatAmount(taxAmt)}</td>
                          <td className="text-end">{formatAmount(netAmount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tax Summary Section */}
            {taxes && taxes.length > 0 && (
              <div className="row mt-2">
                <div className="col-12">
                  {(() => {
                    const taxSummary = {};
                    taxes.forEach(tax => {
                      const taxName = tax.tax_NameEn || tax.Tax_NameEn || 'Tax';
                      const taxPerc = tax.tax_Perc || tax.Tax_Perc || 0;
                      const key = `${taxName}_${taxPerc}`;

                      if (!taxSummary[key]) {
                        taxSummary[key] = {
                          name: taxName,
                          percentage: taxPerc
                        };
                      }
                    });

                    const taxLabels = Object.values(taxSummary).map(tax => `${tax.name} ${tax.percentage}%`);
                    const taxSummaryText = taxLabels.join(', ');

                    return (
                      <p className="mb-2"><strong>Tax Summary / ملخص الضرائب:</strong> {taxSummaryText}</p>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Summary Totals */}
            <div className="row mt-2">
              <div className="col-12">
                <table className="table table-sm table-bordered">
                  <tbody>
                    {overallDiscount > 0 && (
                      <tr>
                        <td colSpan="5" className="text-end"><strong>Additional Discount / خصم إضافي:</strong></td>
                        <td className="text-end"><strong>{formatAmount(overallDiscount)}</strong></td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="5" className="text-end"><strong>Grand Total / الإجمالي الكلي:</strong></td>
                      <td className="text-end"><strong>{formatAmount(grandTotal)}</strong></td>
                    </tr>
                    {tipAmount > 0 && (
                      <tr>
                        <td colSpan="5" className="text-end"><strong>Tip Amount / إكرامية:</strong></td>
                        <td className="text-end"><strong>{formatAmount(tipAmount)}</strong></td>
                      </tr>
                    )}
                    {giftCardAmount > 0 && (
                      <tr>
                        <td colSpan="5" className="text-end"><strong>Gift Card Amount / مبلغ بطاقة الهدايا:</strong></td>
                        <td className="text-end"><strong>{formatAmount(giftCardAmount)}</strong></td>
                      </tr>
                    )}
                    {pointsAmount > 0 && (
                      <tr>
                        <td colSpan="5" className="text-end"><strong>Loyalty Amount / مبلغ الولاء:</strong></td>
                        <td className="text-end"><strong>{formatAmount(pointsAmount)}</strong></td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="5" className="text-end"><strong>Amount Paid / المبلغ المدفوع:</strong></td>
                      <td className="text-end"><strong>{formatAmount(paidAmount)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-12">
                <table className="table table-bordered table-sm">
                  <tbody>
                    {tipAmount > 0 && (
                      <tr>
                        <td><strong>Tip Amount:</strong> {currencyCode} {formatAmount(tipAmount)}</td>
                      </tr>
                    )}
                    {paidAmount > 0 && (
                      <tr>
                        <td><strong>Amount Paid:</strong> {currencyCode} {formatAmount(paidAmount)}</td>
                      </tr>
                    )}
                    {cashAmount > 0 && (
                      <tr>
                        <td><strong>Cash Amount:</strong> {currencyCode} {formatAmount(cashAmount)}</td>
                      </tr>
                    )}
                    {cashlessAmount > 0 && (
                      <tr>
                        <td><strong>{cashlessLabel}:</strong> {currencyCode} {formatAmount(cashlessAmount)}</td>
                      </tr>
                    )}
                    {giftCardAmount > 0 && (
                      <tr>
                        <td><strong>Gift Card Amount:</strong> {currencyCode} {formatAmount(giftCardAmount)}</td>
                      </tr>
                    )}
                    {pointsAmount > 0 && (
                      <tr>
                        <td><strong>Loyalty Amount:</strong> {currencyCode} {formatAmount(pointsAmount)}</td>
                      </tr>
                    )}
                    {invoiceType === 'cr' && (
                      <tr>
                        <td><strong>Credit</strong></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-6">
                <p className="mb-1"><strong>Received By:</strong> {customerName || 'N/A'}</p>
              </div>
              <div className="col-6 text-end">
                <p className="mb-1"><strong>Prepared By:</strong> Jijo Thomas</p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handlePrint}>
            <Printer size={16} className="me-2" />
            Print
          </Button>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <style>
        {`
          .swal-wide {
            max-width: 90% !important;
            width: 90% !important;
          }
          .swal-wide .swal2-html-container {
            max-height: 70vh;
            overflow-y: auto;
          }
          .swal-wide table {
            font-size: 0.875rem;
          }
          .swal-wide .table th,
          .swal-wide .table td {
            padding: 0.5rem;
            vertical-align: middle;
          }
        `}
      </style>
      <Container fluid>
        {/* Loading overlay for booking data */}
        {loadingBookingData && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            <ClipLoader size={50} color="#556ee6" />
            <div style={{ color: '#fff', fontSize: '16px', fontWeight: '500' }}>
              Loading billing details and preparing items...
            </div>
          </div>
        )}
        <Breadcrumb title="Billing Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-4">
                <div>
                  {isReadOnly && (
                    <div className="alert alert-info mb-4" role="alert">
                      <i className="fas fa-eye me-2"></i>
                      <strong>Read-Only Mode:</strong> You are viewing invoice details. No changes can be made.
                    </div>
                  )}
                  <Form onSubmit={(e) => { e.preventDefault(); if (!isReadOnly) handleSave(); }}>
                    <Card className="mb-3" style={{ border: '1px solid #556ee6', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                      <CardBody className="p-3">
                        <h6 className="mb-3" style={{ color: '#556ee6', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #e9ecef', paddingBottom: '8px' }}>
                          <FileText size={16} className="me-2" /> Invoice Information
                        </h6>
                        <Row className="mb-3 align-items-end">
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label for="Invoice_BusinessId" style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Company <span className="text-danger">*</span></Label>
                              <Select
                                value={businessOptions.find(option => option.value === form.Invoice_BusinessId) || null}
                                onChange={(selected) => {
                                  const newBusinessId = selected?.value || null;
                                  setForm({
                                    ...form,
                                    Invoice_BusinessId: newBusinessId,
                                    Invoice_BranchId: null,
                                    Invoice_No: ""
                                  });
                                }}
                                options={businessOptions}
                                placeholder="Select Company"
                                styles={{
                                  ...selectStyles,
                                  control: (provided) => ({ ...provided, minHeight: '35px', fontSize: '12px' })
                                }}
                                isClearable
                                isSearchable
                                required
                                isDisabled={isReadOnly || isReturnMode || !!localStorage.getItem("userCompanyId") || isFromBooking}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label for="Invoice_BranchId" style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Branch <span className="text-danger">*</span></Label>
                              <Select
                                value={branchOptions.find(option => option.value === form.Invoice_BranchId) || null}
                                onChange={(selected) => setForm({ ...form, Invoice_BranchId: selected?.value || null })}
                                options={branchOptions}
                                placeholder="Select Branch"
                                styles={{
                                  ...selectStyles,
                                  control: (provided) => ({ ...provided, minHeight: '35px', fontSize: '12px' })
                                }}
                                isClearable
                                isSearchable
                                isDisabled={isReadOnly || isReturnMode || !form.Invoice_BusinessId || !!localStorage.getItem("userBranchId") || isFromBooking}
                                noOptionsMessage={() => !form.Invoice_BusinessId ? "Please select a company first" : "No branches found"}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label for="Invoice_Date" style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Date <span className="text-danger">*</span></Label>
                              <div className="custom-datepicker-wrapper">
                                <DatePicker
                                  selected={form.Invoice_Date}
                                  onChange={(date) => setForm({ ...form, Invoice_Date: date })}
                                  className="form-control"
                                  dateFormat="dd/MM/yyyy"
                                  required
                                />
                                <style>{`
                                  .custom-datepicker-wrapper .react-datepicker-wrapper { width: 100%; }
                                  .custom-datepicker-wrapper .form-control { 
                                    height: 38px !important; 
                                    font-size: 13px !important; 
                                    border: 1px solid #ced4da;
                                    border-radius: 4px;
                                    padding: 0.375rem 0.75rem;
                                  }
                                `}</style>
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label for="Invoice_SourceId" style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Source <span className="text-danger">*</span></Label>
                              <Select
                                value={sourceOptions.find(option => option.value === form.Invoice_SourceId) || null}
                                onChange={(selected) => setForm({ ...form, Invoice_SourceId: selected?.value || null })}
                                options={sourceOptions}
                                placeholder="Select Source"
                                styles={{
                                  ...selectStyles,
                                  control: (provided) => ({ ...provided, minHeight: '35px', fontSize: '12px' })
                                }}
                                isClearable
                                isSearchable
                                isDisabled={isReadOnly}
                                isLoading={sourcesLoading}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row className="align-items-end">
                          <Col md={8}>
                            <FormGroup className="mb-0">
                              <Label for="Invoice_CustomerId" style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Customer <span className="text-danger">*</span></Label>
                              <div className="d-flex gap-1 align-items-center">
                                <div style={{ flex: 1 }}>
                                  <Select
                                    value={selectedCustomerOption}
                                    onChange={(selected) => {
                                      setSelectedCustomerOption(selected);
                                      setForm(prev => ({
                                        ...prev,
                                        Invoice_CustomerId: selected?.value || null,
                                        Invoice_SourceId: selected ? (selected.customer?.customer_LastSourceId || null) : null
                                      }));
                                      setCustomerHasMembership(false);
                                      setCustomerHasBillableItems(false);
                                      if (selected?.value) {
                                        openMembershipModal(selected.value);
                                      }
                                    }}
                                    options={customerSearchOptions}
                                    placeholder="Search Customer..."
                                    styles={{
                                      ...selectStyles,
                                      control: (provided) => ({ ...provided, minHeight: '35px', fontSize: '12px' })
                                    }}
                                    isClearable
                                    isSearchable
                                    filterOption={() => true}
                                    required
                                    isDisabled={isReadOnly || isReturnMode || isFromBooking}
                                    isLoading={customerSearchLoading}
                                    onInputChange={(inputValue) => {
                                      if (searchTimeout) clearTimeout(searchTimeout);
                                      const timeout = setTimeout(() => searchCustomersManually(inputValue), 300);
                                      setSearchTimeout(timeout);
                                      return inputValue;
                                    }}
                                  />
                                </div>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => setShowAdvancedSearchModal(true)}
                                  disabled={isReadOnly || isReturnMode || isFromBooking}
                                  title="Advanced Search"
                                  style={{ height: '35px', padding: '0 10px', display: 'flex', alignItems: 'center' }}
                                >
                                  <Search size={14} className="me-1" /> <span style={{ fontSize: '11px' }}>Search</span>
                                </Button>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => setShowQuickAddCustomer(true)}
                                  disabled={isReadOnly}
                                  title="Add Customer"
                                  style={{ height: '35px', padding: '0 10px', display: 'flex', alignItems: 'center' }}
                                >
                                  <i className="fas fa-plus me-1" style={{ fontSize: '12px' }}></i> <span style={{ fontSize: '11px' }}>Add</span>
                                </Button>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => setShowCustomerProfile(true)}
                                  disabled={!form.Invoice_CustomerId}
                                  title="Customer Profile"
                                  style={{ height: '35px', padding: '0 10px', display: 'flex', alignItems: 'center' }}
                                >
                                  <i className="fas fa-user me-1" style={{ fontSize: '12px' }}></i> <span style={{ fontSize: '11px' }}>Profile</span>
                                </Button>
                              </div>

                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup className="mb-0">
                              <Label for="Invoice_Type" style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Invoice Type <span className="text-danger">*</span></Label>
                              <div className="d-flex align-items-center gap-3 p-1 rounded border" style={{ height: '35px', backgroundColor: '#f8f9fa' }}>
                                <div className="form-check form-check-inline m-0 ms-2">
                                  <Input
                                    className="form-check-input"
                                    type="radio"
                                    name="Invoice_Type"
                                    id="cash"
                                    value="ca"
                                    checked={form.Invoice_Type === "ca"}
                                    onChange={(e) => setForm({ ...form, Invoice_Type: e.target.value })}
                                    disabled={isReturnMode || isReadOnly}
                                    style={{ width: '13px', height: '13px' }}
                                  />
                                  <Label className="form-check-label ms-1" for="cash" style={{ fontSize: '12px', fontWeight: '600' }}>Cash/Direct</Label>
                                </div>
                                <div className="form-check form-check-inline m-0">
                                  <Input
                                    className="form-check-input"
                                    type="radio"
                                    name="Invoice_Type"
                                    id="credit"
                                    value="cr"
                                    checked={form.Invoice_Type === "cr"}
                                    onChange={(e) => setForm({ ...form, Invoice_Type: e.target.value })}
                                    disabled={isReturnMode || isReadOnly}
                                    style={{ width: '13px', height: '13px' }}
                                  />
                                  <Label className="form-check-label ms-1" for="credit" style={{ fontSize: '12px', fontWeight: '600' }}>Credit Account</Label>
                                </div>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        {/* Status indicators and extra buttons */}
                        <div className="d-flex gap-3 mt-2 align-items-center">
                          {form.Invoice_No && (
                            <span className="badge bg-light text-dark border p-2" style={{ fontSize: '11px' }}>
                              <strong>Invoice No:</strong> {form.Invoice_No}
                            </span>
                          )}

                          {/* Outstanding Credit & Advance Balance - Relocated here */}
                          {customerOutstandingCredit && (
                            <span className="badge border border-warning text-warning p-2" style={{ fontSize: '11px', backgroundColor: '#fffdf0' }}>
                              <i className="fas fa-file-invoice-dollar me-1"></i>
                              <strong>OUTSTANDING:</strong> {loadingCustomerInfo ? <ClipLoader size={10} color="#ffc107" /> : `${currencyCode} ${(customerOutstandingCredit.totalOutstanding || customerOutstandingCredit.TotalOutstanding || 0).toFixed(2)}`}
                            </span>
                          )}
                          {customerAdvanceBalance && (
                            <span className="badge border border-success text-success p-2" style={{ fontSize: '11px', backgroundColor: '#f6fff8' }}>
                              <i className="fas fa-wallet me-1"></i>
                              <strong>ADVANCE:</strong> {loadingCustomerInfo ? <ClipLoader size={10} color="#28a745" /> : `${currencyCode} ${(customerAdvanceBalance.availableAdvance || customerAdvanceBalance.AvailableAdvance || 0).toFixed(2)}`}
                            </span>
                          )}

                          {customerHasBillableItems && selectedCustomerOption?.value && (
                            <span className="text-info" style={{ fontSize: '11px', cursor: 'pointer' }} onClick={() => openMembershipModal(form.Invoice_CustomerId, true)}>
                              <i className="mdi mdi-information-outline me-1"></i>
                              Unbilled services available.
                            </span>
                          )}
                          <div className="ms-auto d-flex gap-2">
                            {customerHasMembership && (
                              <Button color="outline-primary" size="sm" onClick={() => showAllCustomerMemberships(form.Invoice_CustomerId)} disabled={isReadOnly} style={{ fontSize: '11px', padding: '2px 8px' }}>
                                <i className="mdi mdi-crown me-1"></i> Membership
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardBody>
                    </Card>


                    {/* Row 2: Add Items to Invoice */}
                    {form.Invoice_BusinessId && form.Invoice_BranchId ? (
                      <Card className="mb-3" style={{ border: '1px solid #17a2b8', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <CardBody className="p-3">
                          <h6 className="mb-3" style={{ color: '#17a2b8', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #e9ecef', paddingBottom: '8px' }}>
                            <Package size={16} className="me-2" /> Add Items to Invoice
                          </h6>
                          <div className="d-flex flex-wrap gap-2 justify-content-start align-items-center">
                            <Button
                              color="primary"
                              outline
                              onClick={() => openEntityModal('product')}
                              disabled={isReadOnly || isReturnMode}
                              style={{ height: '80px', minWidth: '135px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}
                            >
                              <i className="fas fa-box mb-2" style={{ fontSize: '20px' }}></i>
                              PRODUCTS
                            </Button>
                            <Button
                              color="info"
                              outline
                              onClick={() => openEntityModal('service')}
                              disabled={isReadOnly || isReturnMode}
                              style={{ height: '80px', minWidth: '135px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}
                            >
                              <i className="fas fa-spa mb-2" style={{ fontSize: '20px' }}></i>
                              SERVICES
                            </Button>
                            <Button
                              color="warning"
                              outline
                              onClick={() => openEntityModal('package')}
                              disabled={isReadOnly || isReturnMode}
                              style={{ height: '80px', minWidth: '135px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}
                            >
                              <i className="fas fa-gift mb-2" style={{ fontSize: '20px' }}></i>
                              PACKAGES
                            </Button>
                            <Button
                              color="secondary"
                              outline
                              onClick={() => openEntityModal('packageSeries')}
                              disabled={isReadOnly || isReturnMode}
                              style={{ height: '80px', minWidth: '135px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}
                            >
                              <i className="fas fa-layer-group mb-2" style={{ fontSize: '20px' }}></i>
                              PACKAGE SERIES
                            </Button>
                            <Button
                              color="danger"
                              outline
                              onClick={() => openEntityModal('giftcard')}
                              disabled={isReadOnly || isReturnMode}
                              style={{ height: '80px', minWidth: '135px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}
                            >
                              <i className="fas fa-id-card-alt mb-2" style={{ fontSize: '20px' }}></i>
                              GIFT CARD
                            </Button>
                            {customerHasBillableItems && selectedCustomerOption?.value && (
                              <Button
                                color="success"
                                outline
                                onClick={openMembershipModalManually}
                                disabled={isReadOnly || isReturnMode}
                                style={{ height: '80px', minWidth: '135px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}
                              >
                                <i className="fas fa-id-card mb-2" style={{ fontSize: '20px' }}></i>
                                UNBILLED
                              </Button>
                            )}
                            {isReturnMode && (
                              <div className="ms-auto">
                                <span className="badge p-2 bg-soft-warning text-warning border border-warning" style={{ fontSize: '12px' }}>
                                  <RotateCcw size={14} className="me-1" />
                                  SALES RETURN MODE
                                </span>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    ) : (
                      /* <div className="alert alert-warning mb-3 py-2" style={{ fontSize: '12px' }}>
                        <i className="mdi mdi-alert-outline me-2"></i>
                        Please select a company and branch to add items.
                      </div> */
                      null
                    )}

                    {/* Row 3: Selected Items List */}
                    {form.entityItems.length > 0 && (
                      <Card className="mb-3" style={{ border: '2px solid #556ee6', borderRadius: '8px', boxShadow: '0 4px 6px rgba(85,110,230,0.1)' }}>
                        <CardBody className="p-3">
                          <h6 className="mb-3" style={{ color: '#495057', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #e9ecef', paddingBottom: '8px' }}>
                            <i className="fas fa-list-ul me-2"></i> Selected Items List
                          </h6>
                          <div
                            style={{
                              maxHeight: '400px',
                              overflowY: 'auto',
                              overflowX: 'auto',
                              padding: '2px',
                              width: '100%'
                            }}
                          >
                            <div style={{ minWidth: '1100px' }}>
                              {form.entityItems.map((item, index) => (
                                <div key={item.id} className="border p-2 mb-2 rounded bg-light" style={{ transition: 'all 0.2s ease' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    {/* Item Type and Name */}
                                    <div style={{ flex: '0 0 200px' }}>
                                      <div className="small fw-bold text-primary">{(item.type || '').charAt(0).toUpperCase() + (item.type || '').slice(1)}</div>
                                      <div className="small" style={{ lineHeight: '1.2', wordWrap: 'break-word' }} title={item.entityName}>
                                        {item.entityName}
                                      </div>
                                    </div>

                                    {/* Qty (Billed) + Return Qty */}
                                    <div style={{ flex: isReturnMode ? '0 0 140px' : '0 0 60px' }}>
                                      {isReturnMode ? (
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                                          <div style={{ width: '62px' }}>
                                            <Label className="small mb-1">Billed:</Label>
                                            <Input
                                              type="number"
                                              min="0"
                                              value={item.originalQuantity ?? 0}
                                              onFocus={(e) => e.target.select()}
                                              size="sm"
                                              style={{ fontSize: '11px', padding: '3px 5px', width: '62px' }}
                                              readOnly
                                              disabled
                                            />
                                          </div>
                                          <div style={{ width: '62px' }}>
                                            <Label className="small mb-1">Return:</Label>
                                            <Input
                                              type="number"
                                              min="0"
                                              max={parseInt(item.originalQuantity ?? 0, 10) || 0}
                                              value={item.quantity}
                                              onChange={(e) => updateEntityItemQuantity(item.id, e.target.value)}
                                              onFocus={(e) => e.target.select()}
                                              size="sm"
                                              style={{ fontSize: '11px', padding: '3px 5px', width: '62px' }}
                                              readOnly={isReadOnly || item.m_InstallmentId || item.isNewPackageSeriesInstallment || (item.type === 'product')}
                                              disabled={isReadOnly || item.m_InstallmentId || item.isNewPackageSeriesInstallment || (item.type === 'product')}
                                            />
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ width: '60px' }}>
                                          <Label className="small mb-1">Qty:</Label>
                                          <Input
                                            type="number"
                                            min="1"
                                            value={item.quantity}
                                            onChange={(e) => updateEntityItemQuantity(item.id, e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                            size="sm"
                                            style={{ fontSize: '11px', padding: '3px 5px', width: '50px' }}
                                            readOnly={
                                              isReadOnly ||
                                              (!isReturnMode && item.qtyLocked) ||
                                              item.m_InstallmentId ||
                                              item.isNewPackageSeriesInstallment ||
                                              false
                                            }
                                            disabled={
                                              isReadOnly ||
                                              (!isReturnMode && item.qtyLocked) ||
                                              item.m_InstallmentId ||
                                              item.isNewPackageSeriesInstallment ||
                                              false
                                            }
                                          />
                                        </div>
                                      )}
                                    </div>

                                    {/* Unit Price */}
                                    <div style={{ flex: '0 0 90px' }}>
                                      <Label className="small mb-1">Unit Price:</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formatNumericValue(item.unitPrice)}
                                        onChange={(e) => updateEntityItemUnitPrice(item.id, e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        size="sm"
                                        placeholder="0.00"
                                        style={{
                                          fontSize: '11px',
                                          padding: '3px 5px',
                                          borderColor: item.validationError ? '#dc3545' : undefined,
                                          backgroundColor: isReturnMode ? '#f8f9fa' : 'white'
                                        }}
                                        readOnly={isReadOnly || isReturnMode}
                                        disabled={isReadOnly || isReturnMode}
                                      />
                                      {item.validationError && (
                                        <div className="text-danger" style={{ fontSize: '10px', marginTop: '2px' }}>
                                          {item.validationError}
                                        </div>
                                      )}
                                    </div>


                                    {/* Total Price */}
                                    <div style={{ flex: '0 0 100px' }}>
                                      <Label className="small mb-1">Total Price:</Label>
                                      <Input
                                        type="text"
                                        value={((item.quantity * (item.unitPrice ?? 0)) || 0).toFixed(2)}
                                        readOnly
                                        size="sm"
                                        style={{ backgroundColor: '#f8f9fa', fontSize: '11px', padding: '3px 5px' }}
                                      />
                                    </div>

                                    {/* Dis(%) */}
                                    <div style={{ flex: '0 0 50px' }}>
                                      <Label className="small mb-1">Dis(%):</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={formatNumericValue(item.discount)}
                                        onChange={(e) => updateEntityItemDiscount(item.id, e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        size="sm"
                                        style={{ fontSize: '11px', padding: '3px 5px', width: '40px' }}
                                        readOnly={isReadOnly || isReturnMode}
                                        disabled={isReadOnly || isReturnMode}
                                      />
                                    </div>

                                    {/* Dis Amt */}
                                    <div style={{ flex: '0 0 85px' }}>
                                      <Label className="small mb-1">Dis Amt:</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formatNumericValue(item.discountAmount)}
                                        onChange={(e) => updateEntityItemDiscountAmount(item.id, e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        size="sm"
                                        style={{
                                          fontSize: '11px',
                                          padding: '3px 5px',
                                          borderColor: item.validationError ? '#dc3545' : undefined,
                                          backgroundColor: isReturnMode ? '#f8f9fa' : 'white'
                                        }}
                                        readOnly={isReadOnly || isReturnMode}
                                        disabled={isReadOnly || isReturnMode}
                                      />
                                    </div>

                                    {/* Tax(%) */}
                                    <div style={{ flex: '0 0 50px' }}>
                                      <Label className="small mb-1">Tax(%):</Label>
                                      <div
                                        style={{
                                          position: 'relative',
                                          backgroundColor: '#f8f9fa',
                                          fontSize: '11px',
                                          padding: '3px 5px',
                                          width: '40px',
                                          border: '1px solid #ced4da',
                                          borderRadius: '4px',
                                          cursor: item.taxBreakdown && item.taxBreakdown.length > 1 ? 'help' : 'default',
                                          textAlign: 'center',
                                          lineHeight: '1.2',
                                          whiteSpace: 'nowrap'
                                        }}
                                        title={item.taxBreakdown && item.taxBreakdown.length > 1 ?
                                          `Tax Breakdown:\n${item.taxBreakdown.map(tax => `${tax.name}: ${tax.percentage}%`).join('\n')}` :
                                          item.taxBreakdown && item.taxBreakdown.length === 1 ?
                                            `${item.taxBreakdown[0].name}: ${item.taxBreakdown[0].percentage}%` :
                                            'No tax'
                                        }
                                      >
                                        {(parseFloat(item.tax) || 0).toFixed(1)}
                                        {item.taxBreakdown && item.taxBreakdown.length > 1 && (
                                          <span style={{ fontSize: '8px', color: '#6c757d' }}>+</span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Tax Amount */}
                                    <div style={{ flex: '0 0 75px' }}>
                                      <Label className="small mb-1">Tax Amount:</Label>
                                      <Input
                                        type="text"
                                        value={(item.taxAmount || 0).toFixed(2)}
                                        readOnly
                                        size="sm"
                                        style={{ backgroundColor: '#f8f9fa', fontSize: '11px', padding: '3px 5px' }}
                                      />
                                    </div>

                                    {/* Net Amount */}
                                    <div style={{ flex: '0 0 100px' }}>
                                      <Label className="small mb-1">Net Amount:</Label>
                                      <Input
                                        type="text"
                                        value={(item.netAmount || 0).toFixed(2)}
                                        readOnly
                                        size="sm"
                                        style={{ backgroundColor: '#e8f5e8', fontSize: '11px', padding: '3px 5px', fontWeight: 'bold' }}
                                      />
                                    </div>

                                    {/* Salesman - For Products, Packages, and Package Series */}
                                    {(item.type === 'product' || item.type === 'package' || item.type === 'packageSeries') && (
                                      <div style={{ flex: '0 0 100px' }}>
                                        <Label className="small mb-1">
                                          Salesman:
                                          {item.m_InstallmentId && (
                                            <i className="mdi mdi-information-outline ms-1 text-info"
                                              title="Salesman is set from membership and cannot be changed"
                                              style={{ fontSize: '12px', cursor: 'help' }}></i>
                                          )}
                                        </Label>
                                        <div
                                          title={(() => {
                                            // Get all selected salesmen names for tooltip
                                            const salesmanIds = item.salesmanIds && Array.isArray(item.salesmanIds) && item.salesmanIds.length > 0
                                              ? item.salesmanIds
                                              : (item.salesmanId && item.salesmanId > 0 ? [item.salesmanId] : []);

                                            if (salesmanIds.length === 0) return "No salesmen selected";

                                            const salesmanNames = salesmanIds
                                              .map(id => {
                                                const employee = employees.find(emp => parseInt(emp.employee_Id) === parseInt(id));
                                                return employee ? employee.employee_NameEn : null;
                                              })
                                              .filter(name => name !== null);

                                            return salesmanNames.length > 0 ? salesmanNames.join(", ") : "No salesmen selected";
                                          })()}
                                          style={{ cursor: 'help' }}
                                        >
                                          <Select
                                            isMulti
                                            value={(() => {
                                              // Use salesmanIds array if available, otherwise fallback to single salesmanId
                                              let salesmanIds = item.salesmanIds && Array.isArray(item.salesmanIds) && item.salesmanIds.length > 0
                                                ? item.salesmanIds.map(id => parseInt(id)).filter(id => id > 0)
                                                : (item.salesmanId && item.salesmanId > 0 ? [parseInt(item.salesmanId)] : []);

                                              console.log('🔍 [Select Component] Initial salesmanIds from item:', {
                                                itemId: item.id,
                                                entityName: item.entityName,
                                                item_salesmanIds: item.salesmanIds,
                                                item_salesmanId: item.salesmanId,
                                                m_InstallmentId: item.m_InstallmentId,
                                                initialSalesmanIds: salesmanIds
                                              });

                                              // Fallback: If no salesmen but we have m_InstallmentId, get it from membership
                                              if (salesmanIds.length === 0 && item.m_InstallmentId && form.customerMemberships.length > 0) {
                                                const membership = form.customerMemberships.find(m =>
                                                  m.installments && m.installments.some(inst => inst.m_InstallmentId === item.m_InstallmentId)
                                                );

                                                console.log('🔍 [Select Component] Looking up membership for fallback:', {
                                                  m_InstallmentId: item.m_InstallmentId,
                                                  foundMembership: !!membership,
                                                  membershipId: membership?.membershipId,
                                                  membershipSalesmanIds: membership?.salesmanIds,
                                                  allMemberships: form.customerMemberships.map(m => ({
                                                    membershipId: m.membershipId,
                                                    salesmanIds: m.salesmanIds
                                                  }))
                                                });

                                                if (membership) {
                                                  // Prefer salesmanIds array, fallback to single salesmanId
                                                  const membershipSalesmanIds = membership.salesmanIds && Array.isArray(membership.salesmanIds) && membership.salesmanIds.length > 0
                                                    ? membership.salesmanIds.map(id => parseInt(id)).filter(id => id > 0)
                                                    : (() => {
                                                      const singleSalesmanId = membership.salesmanId || membership.SalesmanId || membership.salesman_ID || membership.Salesman_ID || null;
                                                      return singleSalesmanId && singleSalesmanId > 0 ? [parseInt(singleSalesmanId)] : [];
                                                    })();
                                                  if (membershipSalesmanIds.length > 0) {
                                                    salesmanIds = membershipSalesmanIds;
                                                    console.log('🔍 [Select Component] Updated salesmanIds from membership fallback:', {
                                                      membershipSalesmanIds: membershipSalesmanIds,
                                                      finalSalesmanIds: salesmanIds
                                                    });
                                                  }
                                                }
                                              }

                                              console.log('🔍 [Select Component] Final salesmanIds before converting to options:', {
                                                itemId: item.id,
                                                finalSalesmanIds: salesmanIds,
                                                employeesCount: employees.length
                                              });

                                              // Convert salesman IDs to select options - ensure value format matches options format (string)
                                              const selectOptions = salesmanIds
                                                .map(id => {
                                                  const employee = employees.find(emp => {
                                                    const empId = parseInt(emp.employee_Id);
                                                    const salesmanId = parseInt(id);
                                                    return empId === salesmanId;
                                                  });

                                                  // Use String(employee.employee_Id) to match options format (always string)
                                                  return employee ? { value: String(employee.employee_Id), label: employee.employee_NameEn } : null;
                                                })
                                                .filter(opt => opt !== null);

                                              console.log('🔍 [Select Component] Final select options:', {
                                                itemId: item.id,
                                                selectOptions,
                                                selectOptionsCount: selectOptions.length
                                              });

                                              return selectOptions;
                                            })()}
                                            onChange={(selectedOptions) => updateEntityItemSalesman(item.id, selectedOptions || [])}
                                            options={employees.map(emp => ({
                                              value: String(emp.employee_Id), // Ensure value is always a string for consistent matching
                                              label: emp.employee_NameEn
                                            }))}
                                            placeholder="Select Salesmen"
                                            menuPortalTarget={document.body}
                                            styles={{
                                              ...selectStyles,
                                              control: (provided, state) => ({
                                                ...selectStyles.control(provided, state),
                                                minHeight: '28px',
                                                fontSize: '11px',
                                                padding: '0px 2px'
                                              }),
                                              valueContainer: (provided) => ({
                                                ...provided,
                                                padding: '0px 4px',
                                                height: '28px'
                                              }),
                                              indicatorsContainer: (provided) => ({
                                                ...provided,
                                                height: '28px'
                                              }),
                                              dropdownIndicator: (provided) => ({
                                                ...provided,
                                                padding: '0px 4px'
                                              }),
                                              menu: (provided) => ({
                                                ...provided,
                                                zIndex: 9999
                                              }),
                                              menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                            }}
                                            isClearable={false}
                                            isDisabled={isReadOnly || isReturnMode || (item.m_InstallmentId ? true : false)}
                                            title={item.m_InstallmentId ? "Salesman is set from membership and cannot be changed" : "Select Salesman"}
                                            isSearchable
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {/* Stock Selection Button - Only for Products in Return Mode */}
                                    {item.type === 'product' && isReturnMode && (
                                      <div style={{ flex: '0 0 44px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', minWidth: '44px', paddingTop: '20px' }}>
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => handleOpenStockModal(item.entityId, item.entityName, item.quantity, form.entityItems.findIndex(i => i.id === item.id))}
                                          style={{ fontSize: '11px', padding: '4px 6px', width: '34px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                          title="Select Stock to Return"
                                          disabled={isReadOnly}
                                        >
                                          <Package size={14} />
                                        </Button>
                                      </div>
                                    )}

                                    {/* Remove Button - Same row, far right */}
                                    <div style={{ flex: '0 0 40px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', minWidth: '40px', paddingTop: '20px' }}>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeEntityItem(item.id)}
                                        style={{ fontSize: '12px', padding: '2px 4px', width: '25px', height: '25px' }}
                                        title="Remove Item"
                                        disabled={isReadOnly}
                                      >
                                        ×
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    )}

                    {/* Row 4: Entity Totals */}
                    {(form.entityItems.filter(item => item.type === 'product').length > 0 ||
                      form.entityItems.filter(item => item.type === 'service').length > 0 ||
                      form.entityItems.filter(item => item.type === 'package').length > 0 ||
                      form.entityItems.filter(item => item.type === 'packageSeries').length > 0 ||
                      form.entityItems.filter(item => item.m_InstallmentId).length > 0) && (
                        <Card className="mb-3" style={{ border: '1px solid #007bff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                          <CardBody className="p-3">
                            <h6 className="mb-3" style={{ color: '#007bff', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #e9ecef', paddingBottom: '8px' }}>
                              <i className="fas fa-calculator me-2"></i> Entity Totals
                            </h6>
                            <Row className="g-2">
                              {form.entityItems.filter(item => item.type === 'product').length > 0 && (
                                <Col md={2}>
                                  <div className="text-center p-2 rounded h-100" style={{ backgroundColor: '#e8f5e9', border: '1px solid #28a745' }}>
                                    <Label className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>PRODUCTS</Label>
                                    {(() => {
                                      const totals = getEntityTotalsFromItems('product');
                                      return (
                                        <>
                                          <div className="d-flex justify-content-between mb-0" style={{ fontSize: '11px' }}>
                                            <span>Qty:</span>
                                            <span className="fw-bold">{totals.totalQty || 0}</span>
                                          </div>
                                          <h6 className="mb-0 mt-1" style={{ color: '#28a745', fontWeight: 'bold', fontSize: '13px' }}>
                                            {currencyCode} {(totals.netAmount || 0).toFixed(2)}
                                          </h6>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </Col>
                              )}
                              {form.entityItems.filter(item => item.type === 'product').length > 0 && (
                                <Col md={2}>
                                  <div className="p-2 rounded h-100" style={{ backgroundColor: '#f0f4ff', border: '1px solid #556ee6' }}>
                                    <Label className="d-block text-center border-bottom pb-1 mb-2" style={{ fontSize: '11px', fontWeight: 'bold', color: '#556ee6' }}>PRODUCTS</Label>
                                    {(() => {
                                      const totals = getEntityTotalsFromItems('product');
                                      return (
                                        <div style={{ fontSize: '10px' }}>
                                          <div className="d-flex justify-content-between mb-1"><span>Qty:</span><b>{totals.totalQty || 0}</b></div>
                                          <div className="d-flex justify-content-between mb-1"><span>Amount:</span><span>{(totals.totalAmount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-danger"><span>Disc:</span><span>-{(totals.totalDiscount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-info"><span>Tax:</span><span>+{(totals.totalTax || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mt-1 pt-1 border-top fw-bold text-primary" style={{ fontSize: '11px' }}>
                                            <span>Net:</span><span>{currencyCode} {(totals.netAmount || 0).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </Col>
                              )}
                              {form.entityItems.filter(item => item.type === 'service').length > 0 && (
                                <Col md={2}>
                                  <div className="p-2 rounded h-100" style={{ backgroundColor: '#e3f2fd', border: '1px solid #17a2b8' }}>
                                    <Label className="d-block text-center border-bottom pb-1 mb-2" style={{ fontSize: '11px', fontWeight: 'bold', color: '#17a2b8' }}>SERVICES</Label>
                                    {(() => {
                                      const totals = getEntityTotalsFromItems('service');
                                      return (
                                        <div style={{ fontSize: '10px' }}>
                                          <div className="d-flex justify-content-between mb-1"><span>Qty:</span><b>{totals.totalQty || 0}</b></div>
                                          <div className="d-flex justify-content-between mb-1"><span>Amount:</span><span>{(totals.totalAmount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-danger"><span>Disc:</span><span>-{(totals.totalDiscount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-info"><span>Tax:</span><span>+{(totals.totalTax || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mt-1 pt-1 border-top fw-bold" style={{ fontSize: '11px', color: '#17a2b8' }}>
                                            <span>Net:</span><span>{currencyCode} {(totals.netAmount || 0).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </Col>
                              )}
                              {form.entityItems.filter(item => item.type === 'package').length > 0 && (
                                <Col md={2}>
                                  <div className="p-2 rounded h-100" style={{ backgroundColor: '#fff8e1', border: '1px solid #ffc107' }}>
                                    <Label className="d-block text-center border-bottom pb-1 mb-2" style={{ fontSize: '11px', fontWeight: 'bold', color: '#ffc107' }}>PACKAGES</Label>
                                    {(() => {
                                      const totals = getEntityTotalsFromItems('package');
                                      return (
                                        <div style={{ fontSize: '10px' }}>
                                          <div className="d-flex justify-content-between mb-1"><span>Qty:</span><b>{totals.totalQty || 0}</b></div>
                                          <div className="d-flex justify-content-between mb-1"><span>Amount:</span><span>{(totals.totalAmount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-danger"><span>Disc:</span><span>-{(totals.totalDiscount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-info"><span>Tax:</span><span>+{(totals.totalTax || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mt-1 pt-1 border-top fw-bold" style={{ fontSize: '11px', color: '#ffc107' }}>
                                            <span>Net:</span><span>{currencyCode} {(totals.netAmount || 0).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </Col>
                              )}
                              {form.entityItems.filter(item => item.type === 'packageSeries' && !item.m_InstallmentId).length > 0 && (
                                <Col md={2}>
                                  <div className="p-2 rounded h-100" style={{ backgroundColor: '#f3e5f5', border: '1px solid #9c27b0' }}>
                                    <Label className="d-block text-center border-bottom pb-1 mb-2" style={{ fontSize: '11px', fontWeight: 'bold', color: '#9c27b0' }}>SERIES</Label>
                                    {(() => {
                                      const totals = getEntityTotalsFromItems('packageSeries');
                                      return (
                                        <div style={{ fontSize: '10px' }}>
                                          <div className="d-flex justify-content-between mb-1"><span>Qty:</span><b>{totals.totalQty || 0}</b></div>
                                          <div className="d-flex justify-content-between mb-1"><span>Amount:</span><span>{(totals.totalAmount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-danger"><span>Disc:</span><span>-{(totals.totalDiscount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-info"><span>Tax:</span><span>+{(totals.totalTax || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mt-1 pt-1 border-top fw-bold" style={{ fontSize: '11px', color: '#9c27b0' }}>
                                            <span>Net:</span><span>{currencyCode} {(totals.netAmount || 0).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </Col>
                              )}
                              {form.entityItems.filter(item => item.m_InstallmentId).length > 0 && (
                                <Col md={2}>
                                  <div className="p-2 rounded h-100" style={{ backgroundColor: '#ffebee', border: '1px solid #f44336' }}>
                                    <Label className="d-block text-center border-bottom pb-1 mb-2" style={{ fontSize: '11px', fontWeight: 'bold', color: '#f44336' }}>INSTALLMENTS</Label>
                                    {(() => {
                                      const totals = getInstallmentTotalsFromItems();
                                      return (
                                        <div style={{ fontSize: '10px' }}>
                                          <div className="d-flex justify-content-between mb-1"><span>Qty:</span><b>{totals.totalQty || 0}</b></div>
                                          <div className="d-flex justify-content-between mb-1"><span>Amount:</span><span>{(totals.totalAmount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-danger"><span>Disc:</span><span>-{(totals.totalDiscount || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mb-1 text-info"><span>Tax:</span><span>+{(totals.totalTax || 0).toFixed(2)}</span></div>
                                          <div className="d-flex justify-content-between mt-1 pt-1 border-top fw-bold" style={{ fontSize: '11px', color: '#f44336' }}>
                                            <span>Net:</span><span>{currencyCode} {(totals.netAmount || 0).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </CardBody>
                        </Card>
                      )}

                    {/* Row 5: Overall Totals */}
                    <Card className="mb-3" style={{ border: '2px solid #28a745', borderRadius: '8px', boxShadow: '0 4px 6px rgba(40,167,69,0.1)' }}>
                      <CardBody className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0" style={{ color: '#28a745', fontWeight: 'bold', fontSize: '15px' }}>
                            <i className="fas fa-file-invoice-dollar me-2"></i> Overall Invoice Summary
                          </h6>
                        </div>
                        <Row className="g-2">
                          <Col md={2}>
                            <div className="text-center p-2 rounded h-100" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Total Quantity</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{form.Invoice_TotalQty || 0} Items</h6>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div className="text-center p-2 rounded h-100" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px' }}>Gross Total</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>{currencyCode} {(parseFloat(form.Invoice_TotalAmount) || 0).toFixed(2)}</h6>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div className="text-center p-2 rounded h-100" style={{ backgroundColor: '#fff5f5', border: '1px solid #ffcdd2' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px', color: '#d32f2f' }}>Total Discount</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600', color: '#d32f2f' }}>{currencyCode} {(parseFloat(form.Invoice_DisAmount) || 0).toFixed(2)}</h6>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div className="text-center p-2 rounded h-100" style={{ backgroundColor: '#e8f4fd', border: '1px solid #bbdefb' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px', color: '#1976d2' }}>Total Tax (VAT)</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600', color: '#1976d2' }}>{currencyCode} {(parseFloat(form.Invoice_TaxAmount) || 0).toFixed(2)}</h6>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div className="text-center p-2 rounded h-100" style={{ backgroundColor: '#f3e5f5', border: '1px solid #e1bee7' }}>
                              <Label className="text-muted mb-0" style={{ fontSize: '11px', color: '#7b1fa2' }}>Loyalty & Gift Card</Label>
                              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600', color: '#7b1fa2' }}>{currencyCode} {(parseFloat((form.LoyaltyAmount || form.PointsAmount || 0) + (form.GiftCardAmount || 0))).toFixed(2)}</h6>
                            </div>
                          </Col>
                          <Col md={2}>
                            <div className="text-center p-2 rounded h-100" style={{ backgroundColor: '#28a745', border: '1px solid #28a745', color: 'white' }}>
                              <Label className="mb-0" style={{ fontSize: '12px', color: 'white' }}>Final Net Amount</Label>
                              <h6 className="mb-0" style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>{currencyCode} {(parseFloat(form.Invoice_NetAmount) || 0).toFixed(2)}</h6>
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* Row 6: Payment Panel */}
                    <Card className="mb-3" style={{ border: '2px solid #556ee6', borderRadius: '8px', boxShadow: '0 4px 6px rgba(85,110,230,0.1)' }}>
                      <CardBody className="p-3">
                        <h6 className="mb-3" style={{ color: '#556ee6', fontWeight: 'bold', fontSize: '15px' }}>
                          <i className="fas fa-wallet me-2"></i> Payment & Settlement
                        </h6>

                        {form.Invoice_Type === "ca" && (
                          <Row className="mb-3">
                            <Col md={6}>
                              <div className="p-2 rounded border h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                <Label className="fw-bold mb-2" style={{ fontSize: '12px' }}>Payment Method <span className="text-danger">*</span></Label>
                                <div className="d-flex gap-3 flex-wrap">
                                  <div className="form-check">
                                    <Input
                                      className="form-check-input"
                                      type="radio"
                                      name="PaymentMethod"
                                      id="creditCard"
                                      value="cc"
                                      checked={form.PaymentMethod === "cc"}
                                      onChange={(e) => {
                                        const newValue = form.PaymentMethod === "cc" ? null : e.target.value;
                                        setForm({ ...form, PaymentMethod: newValue, AuthCode: newValue ? form.AuthCode : "" });
                                      }}
                                      disabled={isReadOnly || (isReturnMode && originalInvoicePaymentMethod !== "cc")}
                                    />
                                    <Label className="form-check-label small" for="creditCard">Credit Card</Label>
                                  </div>

                                  <div className="form-check">
                                    <Input
                                      className="form-check-input"
                                      type="radio"
                                      name="PaymentMethod"
                                      id="tabee"
                                      value="ta"
                                      checked={form.PaymentMethod === "ta"}
                                      onChange={(e) => {
                                        const newValue = form.PaymentMethod === "ta" ? null : e.target.value;
                                        if (newValue === "ta") {
                                          if (isReturnMode) {
                                            const netAmount = form.Invoice_NetAmount || 0;
                                            const roundedNetAmount = Math.round(netAmount * 100) / 100;
                                            setForm({
                                              ...form,
                                              PaymentMethod: newValue,
                                              AuthCode: newValue ? form.AuthCode : "",
                                              CashAmount: null,
                                              CashlessAmount: roundedNetAmount > 0 ? roundedNetAmount : null
                                            });
                                          } else {
                                            const amountPaid = form.AmountPaidByCustomer || form.Invoice_NetAmount || 0;
                                            const roundedAmountPaid = Math.round(amountPaid * 100) / 100;
                                            setForm({
                                              ...form,
                                              PaymentMethod: newValue,
                                              AuthCode: newValue ? form.AuthCode : "",
                                              CashAmount: null,
                                              CashlessAmount: roundedAmountPaid > 0 ? roundedAmountPaid : null
                                            });
                                          }
                                        } else {
                                          setForm({ ...form, PaymentMethod: newValue, AuthCode: newValue ? form.AuthCode : "" });
                                        }
                                      }}
                                      disabled={isReadOnly || (isReturnMode && originalInvoicePaymentMethod !== "ta")}
                                    />
                                    <Label className="form-check-label small" for="tabee">Tabby</Label>
                                  </div>

                                  <div className="form-check">
                                    <Input
                                      className="form-check-input"
                                      type="radio"
                                      name="PaymentMethod"
                                      id="bank"
                                      value="ba"
                                      checked={form.PaymentMethod === "ba"}
                                      onChange={(e) => {
                                        const newValue = form.PaymentMethod === "ba" ? null : e.target.value;
                                        setForm({ ...form, PaymentMethod: newValue, AuthCode: newValue ? form.AuthCode : "" });
                                      }}
                                      disabled={isReadOnly || (isReturnMode && originalInvoicePaymentMethod !== "ba")}
                                    />
                                    <Label className="form-check-label small" for="bank">Bank</Label>
                                  </div>
                                </div>
                              </div>
                            </Col>

                            {!isReturnMode && (
                              <Col md={6}>
                                <div className="p-2 rounded border h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                  <Label className="fw-bold mb-2" style={{ fontSize: '12px' }}>Redemptions</Label>
                                  <div className="d-flex gap-2">
                                    <div style={{ flex: 1 }}>
                                      <div className="d-flex justify-content-between align-items-center p-1 px-2 rounded border bg-white h-100" style={{ minHeight: '35px' }}>
                                        <span className="form-check-label small fw-bold text-primary">Loyalty</span>
                                        {form.PointsRedeemed > 0 ? (
                                          <div className="d-flex align-items-center">
                                            <span className="badge bg-primary" style={{ fontSize: '10px' }}>{currencyCode} {form.PointsAmount.toFixed(2)}</span>
                                            <Button color="link" size="sm" className="text-danger ms-1 p-0" onClick={() => {
                                              setForm(prev => ({ ...prev, PointsRedeemed: 0, PointsAmount: 0, LoyaltyAmount: 0 }));
                                            }}>
                                              <i className="fas fa-times-circle"></i>
                                            </Button>
                                          </div>
                                        ) : (
                                          <Button color="primary" size="sm" className="py-0 px-2" disabled={customerBranchLoyaltyBalance <= 0 || !activeLoyaltyConfig} onClick={() => setShowLoyaltyRedeemModal(true)} style={{ fontSize: '10px' }}>Redeem</Button>
                                        )}
                                      </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div className="d-flex justify-content-between align-items-center p-1 px-2 rounded border bg-white h-100" style={{ minHeight: '35px' }}>
                                        <span className="form-check-label small fw-bold text-danger">Gift Card</span>
                                        {form.GiftCardAmount > 0 ? (
                                          <div className="d-flex align-items-center">
                                            <span className="badge bg-danger" style={{ fontSize: '10px' }}>{currencyCode} {form.GiftCardAmount.toFixed(2)}</span>
                                            <Button color="link" size="sm" className="text-danger ms-1 p-0" onClick={() => {
                                              setForm(prev => ({ ...prev, GiftCardAmount: 0, RedeemedGiftCardCode: "" }));
                                            }}>
                                              <i className="fas fa-times-circle"></i>
                                            </Button>
                                          </div>
                                        ) : (
                                          <Button color="danger" size="sm" className="py-0 px-2" onClick={handleGiftCardRedeem} style={{ fontSize: '10px' }}>Redeem</Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            )}
                          </Row>
                        )}

                        {(customerAdvanceBalance || customerOutstandingCredit) && (
                          <div className="d-flex gap-2 mb-3">
                            {customerOutstandingCredit && (
                              <div className="flex-fill p-2 rounded border border-warning" style={{ backgroundColor: '#fffdf0' }}>
                                <div className="d-flex align-items-center gap-2">
                                  <i className="fas fa-file-invoice-dollar text-warning"></i>
                                  <div>
                                    <div className="text-muted" style={{ fontSize: '10px' }}>Outstanding Credit</div>
                                    <div className="fw-bold text-warning" style={{ fontSize: '12px' }}>
                                      {loadingCustomerInfo ? <ClipLoader size={12} color="#ffc107" /> : `${currencyCode} ${(customerOutstandingCredit.totalOutstanding || customerOutstandingCredit.TotalOutstanding || 0).toFixed(2)}`}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {customerAdvanceBalance && (
                              <div className="flex-fill p-2 rounded border border-success" style={{ backgroundColor: '#f6fff8' }}>
                                <div className="d-flex align-items-center gap-2">
                                  <i className="fas fa-wallet text-success"></i>
                                  <div>
                                    <div className="text-muted" style={{ fontSize: '10px' }}>Available Advance</div>
                                    <div className="fw-bold text-success" style={{ fontSize: '12px' }}>
                                      {loadingCustomerInfo ? <ClipLoader size={12} color="#28a745" /> : `${currencyCode} ${(customerAdvanceBalance.availableAdvance || customerAdvanceBalance.AvailableAdvance || 0).toFixed(2)}`}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <Row className="g-2">
                          <Col md={2}>
                            <FormGroup className="mb-0">
                              <Label className="small fw-bold mb-1">Tip Amount</Label>
                              <Input
                                id="TipAmount"
                                name="TipAmount"
                                type="number"
                                step="0.01"
                                value={formatNumericValue(form.TipAmount)}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const tipAmt = value === '' ? 0 : (parseFloat(value) || 0);
                                  setForm(prev => {
                                    const net = parseFloat(prev.Invoice_NetAmount) || 0;
                                    const loyalty = prev.LoyaltyAmount || 0;
                                    const giftCard = prev.GiftCardAmount || 0;
                                    const totalRequired = Math.max(0, Math.round((net + tipAmt - loyalty - giftCard) * 100) / 100);
                                    const isCashlessMethod = prev.PaymentMethod === 'cc' || prev.PaymentMethod === 'ba' || prev.PaymentMethod === 'on';
                                    const isCashMethod = prev.PaymentMethod === 'ca' || !prev.PaymentMethod;
                                    let newForm = { ...prev, TipAmount: tipAmt };
                                    if (isCashlessMethod) {
                                      const currentCash = parseFloat(prev.CashAmount) || 0;
                                      const newCashless = Math.max(0, Math.round((totalRequired - currentCash) * 100) / 100);
                                      newForm.CashlessAmount = newCashless === 0 ? null : newCashless;
                                    } else if (isCashMethod) {
                                      const currentCashless = parseFloat(prev.CashlessAmount) || 0;
                                      const newCash = Math.max(0, Math.round((totalRequired - currentCashless) * 100) / 100);
                                      newForm.CashAmount = newCash;
                                    } else if (prev.PaymentMethod === 'ta') {
                                      newForm.CashlessAmount = totalRequired;
                                      newForm.CashAmount = 0;
                                    }
                                    return newForm;
                                  });
                                }}
                                style={{ height: '35px', fontSize: '12px' }}
                                disabled={isReadOnly || isReturnMode}
                                placeholder="0.00"
                              />
                            </FormGroup>
                          </Col>
                          {form.PaymentMethod !== "ta" && (
                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="small fw-bold mb-1">Cash Amount</Label>
                                <Input
                                  id="CashAmount"
                                  name="CashAmount"
                                  type="number"
                                  step="0.01"
                                  value={formatNumericValue(form.CashAmount)}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const cashAmount = value === '' ? 0 : (parseFloat(value) || 0);
                                    if (isReturnMode) {
                                      const netAmount = form.Invoice_NetAmount || 0;
                                      const cashlessAmount = Math.max(0, Math.round((netAmount - cashAmount) * 100) / 100);
                                      setForm({ ...form, CashAmount: value === '' ? null : cashAmount, CashlessAmount: cashlessAmount === 0 ? null : cashlessAmount });
                                    } else {
                                      const netAmount = parseFloat(form.Invoice_NetAmount) || 0;
                                      const tipAmount = parseFloat(form.TipAmount) || 0;
                                      const loyaltyAmount = parseFloat(form.LoyaltyAmount) || 0;
                                      const giftCardAmount = parseFloat(form.GiftCardAmount) || 0;
                                      const totalToPay = netAmount + tipAmount - loyaltyAmount - giftCardAmount;
                                      const shouldAutoCalculate = form.PaymentMethod === "ca" || !form.PaymentMethod;
                                      const cashlessAmount = shouldAutoCalculate ? Math.max(0, Math.round((totalToPay - cashAmount) * 100) / 100) : (form.CashlessAmount || null);
                                      setForm({ ...form, CashAmount: value === '' ? null : cashAmount, CashlessAmount: shouldAutoCalculate ? (cashlessAmount === 0 ? null : cashlessAmount) : form.CashlessAmount, PaymentMethod: cashAmount > 0 && shouldAutoCalculate ? "ca" : form.PaymentMethod });
                                    }
                                  }}
                                  style={{ height: '35px', fontSize: '12px' }}
                                  placeholder="0.00"
                                  disabled={isReadOnly || isReturnMode}
                                />
                              </FormGroup>
                            </Col>
                          )}
                          {(isReturnMode ? (originalInvoicePaymentMethod === "cc" || originalInvoicePaymentMethod === "on" || originalInvoicePaymentMethod === "ta" || originalInvoicePaymentMethod === "ba") : (form.PaymentMethod === "cc" || form.PaymentMethod === "on" || form.PaymentMethod === "ta" || form.PaymentMethod === "ba")) && (
                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="small fw-bold mb-1">
                                  {(isReturnMode ? originalInvoicePaymentMethod : form.PaymentMethod) === "cc" ? "CC Amt"
                                    : (isReturnMode ? originalInvoicePaymentMethod : form.PaymentMethod) === "ta" ? "Tabby Amt"
                                      : (isReturnMode ? originalInvoicePaymentMethod : form.PaymentMethod) === "ba" ? "Bank Amt"
                                        : "Cashless Amt"}
                                </Label>
                                <Input
                                  id="CashlessAmount"
                                  name="CashlessAmount"
                                  type="number"
                                  step="0.01"
                                  value={formatNumericValue(form.PaymentMethod === "ta" ? (isReturnMode ? (form.Invoice_NetAmount ? Math.round((form.Invoice_NetAmount || 0) * 100) / 100 : form.CashlessAmount) : (form.Invoice_NetAmount ? Math.round((parseFloat(form.Invoice_NetAmount) + parseFloat(form.TipAmount || 0)) * 100) / 100 : form.CashlessAmount)) : form.CashlessAmount)}
                                  readOnly={isReadOnly || isReturnMode || form.PaymentMethod === "ta"}
                                  onChange={(isReturnMode && (originalInvoicePaymentMethod === "cc" || originalInvoicePaymentMethod === "on" || originalInvoicePaymentMethod === "ta" || originalInvoicePaymentMethod === "ba")) || (!isReturnMode && !isReadOnly && (form.PaymentMethod === "cc" || form.PaymentMethod === "on" || form.PaymentMethod === "ta" || form.PaymentMethod === "ba") && form.PaymentMethod !== "ta") ? (e) => {
                                    const value = e.target.value;
                                    const cashlessAmount = value === '' ? 0 : (parseFloat(value) || 0);
                                    if (isReturnMode) {
                                      const netAmount = form.Invoice_NetAmount || 0;
                                      const cashAmount = Math.max(0, Math.round((netAmount - cashlessAmount) * 100) / 100);
                                      setForm({ ...form, CashlessAmount: value === '' ? null : cashlessAmount, CashAmount: cashAmount === 0 ? null : cashAmount });
                                    } else {
                                      const netAmount = parseFloat(form.Invoice_NetAmount) || 0;
                                      const tipAmount = parseFloat(form.TipAmount) || 0;
                                      const loyaltyAmount = parseFloat(form.LoyaltyAmount) || 0;
                                      const giftCardAmount = parseFloat(form.GiftCardAmount) || 0;
                                      const totalRequired = netAmount + tipAmount - loyaltyAmount - giftCardAmount;
                                      const cashAmount = Math.max(0, Math.round((totalRequired - cashlessAmount) * 100) / 100);
                                      setForm({ ...form, CashlessAmount: value === '' ? null : cashlessAmount, CashAmount: cashAmount === 0 ? null : cashAmount });
                                    }
                                  } : null}
                                  style={{ height: '35px', fontSize: '12px' }}
                                  placeholder="0.00"
                                />
                              </FormGroup>
                            </Col>
                          )}
                          {!isReturnMode && form.PointsRedeemed > 0 && (
                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="small fw-bold mb-1">Loyalty Amt</Label>
                                <Input type="number" step="0.01" value={parseFloat(form.PointsAmount || 0).toFixed(2)} readOnly style={{ height: '35px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
                              </FormGroup>
                            </Col>
                          )}
                          {!isReturnMode && form.GiftCardAmount > 0 && (
                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="small fw-bold mb-1">Gift Card Amt</Label>
                                <Input type="number" step="0.01" value={parseFloat(form.GiftCardAmount || 0).toFixed(2)} readOnly style={{ height: '35px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
                              </FormGroup>
                            </Col>
                          )}
                          {(form.PaymentMethod === "cc" || form.PaymentMethod === "on" || form.PaymentMethod === "ta" || form.PaymentMethod === "ba") && (
                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="small fw-bold mb-1">Auth Code</Label>
                                <Input type="text" value={form.AuthCode} onChange={(e) => setForm({ ...form, AuthCode: e.target.value })} placeholder="Code" style={{ height: '35px', fontSize: '12px' }} disabled={isReadOnly} />
                              </FormGroup>
                            </Col>
                          )}
                        </Row>

                        <Row className="mt-3">
                          <Col md={6}>
                            <FormGroup className="mb-0">
                              <Label className="small fw-bold mb-1">Invoice Document</Label>
                              <div className="d-flex gap-1">
                                <input key={form.Invoice_Document ? form.Invoice_Document.name : 'file-input'} id="invoiceDocumentUpload" type="file" className="d-none" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setForm({ ...form, Invoice_Document: e.target.files[0] })} disabled={isReadOnly} />
                                <Button color="light" size="sm" className="border" onClick={() => document.getElementById('invoiceDocumentUpload').click()} disabled={isReadOnly} style={{ height: '35px', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                  <FileText size={14} className="me-1" /> Browse
                                </Button>
                                <Input type="text" value={form.Invoice_Document ? form.Invoice_Document.name : (form.Invoice_DocumentPath ? form.Invoice_DocumentPath.split("/").pop() : "")} readOnly placeholder="No file" style={{ height: '35px', fontSize: '11px' }} />
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md={6} className="d-flex align-items-end justify-content-end gap-2">
                            <Button type="button" color="secondary" outline onClick={handleCancel} style={{ height: '35px', fontSize: '12px', minWidth: '100px' }}>Clear</Button>
                            <Button type="submit" color="primary" disabled={saveLoading || !form.Invoice_CustomerId || !form.Invoice_BusinessId || isReadOnly} style={{ height: '35px', fontSize: '12px', minWidth: '150px' }}>
                              {saveLoading ? <ClipLoader size={14} color="#ffffff" className="me-2" /> : (isReadOnly ? "View Only" : (isEditing ? "Update Invoice" : "Save Invoice"))}
                            </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Form>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container >

      {/* Modal for Bulk Selection */}
      < Modal isOpen={form.showEntityModal} toggle={closeEntityModal} size="lg" >
        <ModalHeader toggle={closeEntityModal}>
          Select {(form.modalEntityType || '').charAt(0).toUpperCase() + (form.modalEntityType || '').slice(1)}s with Quantities
        </ModalHeader>
        <ModalBody style={{ fontSize: '0.875rem' }}>
          {/* Search Input */}
          <div className="mb-3">
            <span style={{ fontSize: '0.875rem' }}>Search {(form.modalEntityType || '').charAt(0).toUpperCase() + (form.modalEntityType || '').slice(1)}s:</span>
            <Input
              type="text"
              placeholder={`Search for ${form.modalEntityType}s...`}
              value={form.modalSearchTerm}
              onChange={(e) => setForm(prev => ({ ...prev, modalSearchTerm: e.target.value }))}
              className="form-control"
              style={{ fontSize: '0.875rem' }}
            />
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
            {form.modalEntityType === 'product' && (() => {
              // Get already added product IDs
              const addedProductIds = form.entityItems
                .filter(item => item.type === 'product')
                .map(item => item.entityId);

              // Filter out already added products from modalItems
              const availableProducts = modalItems.filter(product =>
                !addedProductIds.includes(product.value)
              );

              if (modalLoading && modalItems.length === 0) {
                return (
                  <div className="text-center p-4">
                    <ClipLoader size={20} color="#556ee6" />
                    <p className="mt-2 text-muted">Loading products...</p>
                  </div>
                );
              }

              return availableProducts.length > 0 ? (
                <>
                  {availableProducts.map(product => (
                    <div key={product.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                      <div className="d-flex align-items-center gap-2">
                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id={`product-${product.value}`}
                            checked={!!form.selectedModalItems[product.value]}
                            onChange={(e) => {
                              handleModalItemCheck(product.value, e.target.checked);
                              if (e.target.checked) {
                                setTimeout(() => {
                                  const qtyInput = document.getElementById(`qty-product-${product.value}`);
                                  if (qtyInput) {
                                    qtyInput.focus();
                                    qtyInput.select();
                                  }
                                }, 100);
                              }
                            }}
                          />
                          <span className="form-check-label">
                            {product.label}
                          </span>
                        </div>
                        {form.selectedModalItems[product.value] && (
                          <div className="ms-auto d-flex align-items-center gap-2">
                            <span style={{ fontSize: '0.875rem' }}>Quantity:</span>
                            <Input
                              id={`qty-product-${product.value}`}
                              type="number"
                              min="1"
                              value={form.selectedModalItems[product.value] || ''}
                              onChange={(e) => handleModalQuantityChange(product.value, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              style={{ width: '80px', fontSize: '0.875rem' }}
                              size="sm"
                              placeholder="Qty"
                            />
                            {/* Show stock button only if product has stock, otherwise show "no stock" text */}
                            {form.productStockStatus[product.value] === false ? (
                              <span style={{ fontSize: '0.875rem', color: '#dc3545', fontWeight: '500' }}>
                                No Stock
                              </span>
                            ) : form.productStockStatus[product.value] === true ? (
                              <Button
                                color="outline-primary"
                                size="sm"
                                onClick={() => handleOpenStockModal(product.value, product.label, form.selectedModalItems[product.value])}
                                title="Select Stock"
                                style={{ padding: '4px 8px' }}
                              >
                                <Package size={14} />
                              </Button>
                            ) : (
                              // Loading state - stock check in progress
                              <span style={{ fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic' }}>
                                Checking...
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {modalHasMore && (
                    <div className="text-center mt-3 mb-2">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={loadMoreModalItems}
                        disabled={modalLoading}
                        style={{ fontSize: '0.875rem' }}
                      >
                        {modalLoading ? (
                          <>
                            <ClipLoader size={14} color="#ffffff" className="me-2" />
                            Loading...
                          </>
                        ) : (
                          'Show 30 More'
                        )}
                      </Button>
                      <p className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                        Showing {availableProducts.length} of {modalTotalCount} products
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center p-4 text-muted">
                  <i className="fas fa-search fa-2x mb-2"></i>
                  <p>
                    {form.modalSearchTerm && form.modalSearchTerm.length >= 2
                      ? `No products found matching "${form.modalSearchTerm}"`
                      : 'No products available'}
                  </p>
                </div>
              );
            })()}

            {form.modalEntityType === 'service' && (() => {
              // Get already added service IDs
              const addedServiceIds = form.entityItems
                .filter(item => item.type === 'service')
                .map(item => item.entityId);

              // Filter out already added services from modalItems
              const availableServices = modalItems.filter(service =>
                !addedServiceIds.includes(service.value)
              );

              if (modalLoading && modalItems.length === 0) {
                return (
                  <div className="text-center p-4">
                    <ClipLoader size={20} color="#556ee6" />
                    <p className="mt-2 text-muted">Loading services...</p>
                  </div>
                );
              }

              return availableServices.length > 0 ? (
                <>
                  {availableServices.map(service => (
                    <div key={service.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                      <div className="d-flex align-items-center gap-2">
                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id={`service-${service.value}`}
                            checked={!!form.selectedModalItems[service.value]}
                            onChange={(e) => {
                              handleModalItemCheck(service.value, e.target.checked);
                              if (e.target.checked) {
                                setTimeout(() => {
                                  const qtyInput = document.getElementById(`qty-service-${service.value}`);
                                  if (qtyInput) {
                                    qtyInput.focus();
                                    qtyInput.select();
                                  }
                                }, 100);
                              }
                            }}
                          />
                          <span className="form-check-label">
                            {service.label}
                          </span>
                        </div>
                        {form.selectedModalItems[service.value] && (
                          <div className="ms-auto">
                            <span style={{ fontSize: '0.875rem' }}>Quantity:</span>
                            <Input
                              id={`qty-service-${service.value}`}
                              type="number"
                              min="1"
                              value={form.selectedModalItems[service.value] || ''}
                              onChange={(e) => handleModalQuantityChange(service.value, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              style={{ width: '80px', fontSize: '0.875rem' }}
                              size="sm"
                              placeholder="Qty"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {modalHasMore && (
                    <div className="text-center mt-3 mb-2">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={loadMoreModalItems}
                        disabled={modalLoading}
                        style={{ fontSize: '0.875rem' }}
                      >
                        {modalLoading ? (
                          <>
                            <ClipLoader size={14} color="#ffffff" className="me-2" />
                            Loading...
                          </>
                        ) : (
                          'Show 30 More'
                        )}
                      </Button>
                      <p className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                        Showing {availableServices.length} of {modalTotalCount} services
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center p-4 text-muted">
                  <i className="fas fa-search fa-2x mb-2"></i>
                  <p>
                    {form.modalSearchTerm && form.modalSearchTerm.length >= 2
                      ? `No services found matching "${form.modalSearchTerm}"`
                      : 'No services available'}
                  </p>
                </div>
              );
            })()}

            {form.modalEntityType === 'package' && (() => {
              // Get already added package IDs
              const addedPackageIds = form.entityItems
                .filter(item => item.type === 'package')
                .map(item => item.entityId);

              return packageOptions
                .filter(pkg =>
                  pkg.label.toLowerCase().includes(form.modalSearchTerm.toLowerCase()) &&
                  !addedPackageIds.includes(pkg.value)
                )
                .map(pkg => (
                  <div key={pkg.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                    <div className="d-flex align-items-center gap-2">
                      <div className="form-check">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          id={`package-${pkg.value}`}
                          checked={!!form.selectedModalItems[pkg.value]}
                          onChange={(e) => {
                            // For packages, set quantity to 1 (membership, no quantity needed)
                            handleModalItemCheck(pkg.value, e.target.checked);
                            if (e.target.checked) {
                              handleModalQuantityChange(pkg.value, '1');
                            }
                          }}
                        />
                        <span className="form-check-label">
                          {pkg.label}
                        </span>
                      </div>
                    </div>
                  </div>
                ));
            })()}

            {form.modalEntityType === 'packageSeries' && (() => {
              // Get already added package series IDs (check both regular items and installment items)
              const addedPackageSeriesIds = form.entityItems
                .filter(item => item.type === 'packageSeries')
                .map(item => item.entityId);

              return packageSeriesOptions
                .filter(series =>
                  series.label.toLowerCase().includes(form.modalSearchTerm.toLowerCase()) &&
                  !addedPackageSeriesIds.includes(series.value)
                )
                .map(series => (
                  <div key={series.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                    <div className="d-flex align-items-center gap-2">
                      <div className="form-check">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          id={`packageSeries-${series.value}`}
                          checked={!!form.selectedModalItems[series.value]}
                          onChange={(e) => {
                            // For package series, set quantity to 1 (membership, no quantity needed)
                            // The installment modal will open automatically when added
                            handleModalItemCheck(series.value, e.target.checked);
                            if (e.target.checked) {
                              handleModalQuantityChange(series.value, '1');
                            }
                          }}
                        />
                        <span className="form-check-label">
                          {series.label}
                        </span>
                      </div>
                    </div>
                  </div>
                ));
            })()}

            {form.modalEntityType === 'giftcard' && (() => {
              // Get already added gift card IDs
              const addedGiftCardIds = form.entityItems
                .filter(item => item.type === 'giftcard')
                .map(item => item.entityId);

              // Filter out already added gift cards from modalItems
              const availableGiftCards = modalItems.filter(gc =>
                !addedGiftCardIds.includes(gc.value) &&
                gc.label.toLowerCase().includes(form.modalSearchTerm.toLowerCase())
              );

              return availableGiftCards.length > 0 ? (
                <>
                  {availableGiftCards.map(gc => (
                    <div key={gc.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                      <div className="d-flex align-items-center gap-2">
                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id={`giftcard-${gc.value}`}
                            checked={!!form.selectedModalItems[gc.value]}
                            onChange={(e) => {
                              handleModalItemCheck(gc.value, e.target.checked);
                              if (e.target.checked) {
                                setTimeout(() => {
                                  const qtyInput = document.getElementById(`qty-giftcard-${gc.value}`);
                                  if (qtyInput) {
                                    qtyInput.focus();
                                    qtyInput.select();
                                  }
                                }, 100);
                              }
                            }}
                          />
                          <span className="form-check-label">
                            {gc.label}
                          </span>
                        </div>
                        {form.selectedModalItems[gc.value] && (
                          <div className="ms-auto">
                            <span style={{ fontSize: '0.875rem' }}>Quantity:</span>
                            <Input
                              id={`qty-giftcard-${gc.value}`}
                              type="number"
                              min="1"
                              value={form.selectedModalItems[gc.value] || ''}
                              onChange={(e) => handleModalQuantityChange(gc.value, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              style={{ width: '80px', fontSize: '0.875rem' }}
                              size="sm"
                              placeholder="Qty"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center p-4 text-muted">
                  <i className="fas fa-id-card-alt fa-2x mb-2"></i>
                  <p>
                    {form.modalSearchTerm
                      ? `No gift cards found matching "${form.modalSearchTerm}"`
                      : 'No gift cards available'}
                  </p>
                </div>
              );
            })()}
          </div>
        </ModalBody>
        <ModalFooter style={{ fontSize: '0.875rem' }}>
          <div className="d-flex justify-content-between w-100">
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              Selected: {Object.keys(form.selectedModalItems).filter(key => form.selectedModalItems[key] > 0).length} items
            </span>
            <div className="d-flex gap-2">
              <Button color="secondary" onClick={closeEntityModal} style={{ fontSize: '0.875rem' }}>
                Cancel
              </Button>
              <Button
                color="success"
                onClick={addSelectedModalItems}
                disabled={Object.entries(form.selectedModalItems).filter(([key, quantity]) => {
                  if (quantity === undefined || quantity === null || quantity === '') return false;
                  const qty = parseInt(quantity);
                  return !isNaN(qty) && qty > 0;
                }).length === 0}
                style={{ fontSize: '0.875rem' }}
              >
                Add Selected Items
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal >

      {/* Customer Membership Modal */}
      < Modal isOpen={form.showMembershipModal} toggle={closeMembershipModal} size="lg" >
        <ModalHeader toggle={closeMembershipModal}>
          Select Unbilled Services to Bill
        </ModalHeader>
        <ModalBody style={{ fontSize: '0.875rem' }}>
          {/* Section 1a: New Package Series Installments (for purchase) - Can be multiple series */}
          {form.currentPackageSeriesForInstallments && form.currentPackageSeriesForInstallments.length > 0 && (
            <div className="mb-4">
              {form.currentPackageSeriesForInstallments.map((seriesData, seriesIndex) => (
                <div key={seriesData.seriesId} className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                  <h6 className="text-success mb-3" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                    <i className="fas fa-plus-circle me-2"></i>New Package Series Installments - {seriesData.seriesName}
                  </h6>
                  <div className="mb-2" style={{ fontSize: '0.875rem' }}>
                    Series: {seriesData.seriesName} |
                    Select installments to pay during purchase
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {seriesData.installments && seriesData.installments.length > 0 ? (
                      seriesData.installments.map((installment) => {
                        const installmentKey = `new_${seriesData.seriesId}_${installment.installmentId}`;
                        const isSelected = form.newPackageSeriesInstallments[installmentKey]?.checked || false;
                        const amount = form.newPackageSeriesInstallments[installmentKey]?.amount ?? '';

                        // Get series entity for discount and tax calculation
                        const seriesEntity = seriesData.seriesEntity || packageSeries.find(s => s.seriesId === seriesData.seriesId);

                        // Calculate net amount with discount and tax
                        const grossAmount = installment.amount || installment.InstallmentAmount || 0;
                        const discountPercentage = seriesEntity?.discountPercentage || 0;
                        const taxIds = seriesEntity?.taxIds || [];
                        const netAmount = calculateNetAmount(grossAmount, discountPercentage, taxIds);

                        return (
                          <div key={installment.installmentId} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded" style={{ fontSize: '0.875rem', backgroundColor: '#fff' }}>
                            <div className="form-check">
                              <Input
                                className="form-check-input"
                                type="checkbox"
                                id={`new-installment-${seriesData.seriesId}-${installment.installmentId}`}
                                checked={isSelected}
                                onChange={(e) => {
                                  setForm(prev => ({
                                    ...prev,
                                    newPackageSeriesInstallments: {
                                      ...prev.newPackageSeriesInstallments,
                                      [installmentKey]: {
                                        ...prev.newPackageSeriesInstallments[installmentKey],
                                        checked: e.target.checked,
                                        validationError: ''
                                      }
                                    }
                                  }));
                                  if (e.target.checked) {
                                    setForm(prev => ({
                                      ...prev,
                                      newPackageSeriesInstallments: {
                                        ...prev.newPackageSeriesInstallments,
                                        [installmentKey]: {
                                          ...prev.newPackageSeriesInstallments[installmentKey],
                                          amount: installment.amount,
                                          checked: true
                                        }
                                      }
                                    }));
                                  }
                                }}
                              />
                              <span className="form-check-label">
                                {installment.installmentName}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                                Net: <strong>{netAmount.toFixed(2)}</strong>
                              </div>
                              {isSelected && (
                                <div className="d-flex align-items-center gap-1">
                                  <span className="mb-0" style={{ fontSize: '0.875rem' }}>Amount:</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    max={installment.amount}
                                    step="0.01"
                                    value={netAmount.toFixed(2)}
                                    readOnly
                                    disabled
                                    style={{
                                      width: '100px',
                                      fontSize: '0.875rem',
                                      backgroundColor: '#f8f9fa',
                                      cursor: 'not-allowed'
                                    }}
                                    size="sm"
                                    placeholder="Amount"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-muted text-center py-2" style={{ fontSize: '0.875rem' }}>
                        No installments available for this series
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Section 1: Membership Installments */}
          {form.customerMemberships.length > 0 && (
            <div className="mb-4">
              <h5 className="text-primary mb-3" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                <i className="fas fa-credit-card me-2"></i>Membership Installments
              </h5>
              <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
                {form.customerMemberships
                  .filter(membership => {
                    // Only show memberships that have at least one unpaid installment
                    if (!membership.installments || membership.installments.length === 0) {
                      return false;
                    }
                    // Check if there's at least one installment with remaining amount > 0
                    const hasUnpaidInstallments = membership.installments.some(installment => {
                      const billedAmount = installment.billedAmount || installment.paidAmount || 0;
                      const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
                      return remainingAmount > 0;
                    });
                    return hasUnpaidInstallments;
                  })
                  .map((membership, membershipIndex) => {
                    // Filter installments to only show unpaid ones
                    const unpaidInstallments = membership.installments.filter(installment => {
                      const billedAmount = installment.billedAmount || installment.paidAmount || 0;
                      const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
                      return remainingAmount > 0;
                    });

                    return (
                      <div key={membership.membershipId} className="mb-3">
                        <div className="border rounded" style={{ padding: '0.75rem' }}>
                          <h6 className="text-primary mb-2" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            <i className="fas fa-credit-card me-2"></i>
                            {isPackageType(membership.entity_Type) ? 'Package' : 'Package Series'} Membership
                          </h6>
                          <div className="mb-2" style={{ fontSize: '0.875rem' }}>
                            {isPackageType(membership.entity_Type) ? 'Package' : 'Series'}: {form.entityNames[membership.entity_Id] || `${isPackageType(membership.entity_Type) ? 'Package' : 'Series'} ${membership.entity_Id}`} |
                            Purchase Date: {new Date(membership.purchaseDate).toLocaleDateString('en-GB')}
                          </div>

                          {unpaidInstallments.length > 0 ? (
                            <div>
                              <h6 className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>Available Installments:</h6>
                              {unpaidInstallments.map((installment, installmentIndex) => {
                                // Calculate sequential installment number based on original sequence
                                const sequentialNumber = membership.minInstallmentId !== null
                                  ? installment.installmentId - membership.minInstallmentId + 1
                                  : installment.installmentId;
                                const installmentName = form.installmentNames[installment.installmentId] || `Installment ${sequentialNumber}`;
                                const isSelected = form.selectedMembershipInstallments[installment.installmentId]?.checked || false;
                                const amount = form.selectedMembershipInstallments[installment.installmentId]?.amount ?? '';

                                return (
                                  <div key={installment.installmentId} className="d-flex align-items-center justify-content-between mb-1 p-2 border rounded" style={{ fontSize: '0.875rem' }}>
                                    <div className="form-check">
                                      <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`installment-${installment.installmentId}`}
                                        checked={isSelected}
                                        onChange={(e) => {
                                          handleMembershipInstallmentCheck(installment.installmentId, e.target.checked);
                                          if (e.target.checked) {
                                            // Set default amount to remaining amount when checked
                                            const billedAmount = installment.billedAmount || installment.paidAmount || 0;
                                            const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
                                            handleMembershipInstallmentAmountChange(installment.installmentId, remainingAmount);
                                          }
                                        }}
                                      />
                                      <span className="form-check-label">
                                        {installmentName}
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                                        Amount: {installment.amount.toFixed(2)} |
                                        Billed: {((installment.billedAmount || installment.paidAmount) || 0).toFixed(2)} |
                                        Remaining: {(Math.round((installment.amount - (installment.billedAmount || installment.paidAmount || 0)) * 100) / 100).toFixed(2)}
                                      </div>
                                      {isSelected && (
                                        <div className="d-flex align-items-center gap-1">
                                          <span className="mb-0" style={{ fontSize: '0.875rem' }}>Amount:</span>
                                          <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={amount}
                                            readOnly
                                            disabled
                                            style={{
                                              width: '100px',
                                              fontSize: '0.875rem',
                                              backgroundColor: '#f8f9fa',
                                              cursor: 'not-allowed'
                                            }}
                                            size="sm"
                                            placeholder="Amount"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Section 2: Unbilled Services from Bookings */}
          {form.unbilledServices && form.unbilledServices.length > 0 && (
            <div className="mt-4 pt-4 border-top">
              <h6 className="text-info mb-3" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                <i className="fas fa-list-check me-2"></i>Unbilled Services from Bookings
              </h6>
              <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
                {form.unbilledServices.map((service) => {
                  // Handle both camelCase (from API) and PascalCase (if transformed)
                  const bookingResourceDetailId = service.bookingResourceDetail_Id || service.BookingResourceDetail_Id;
                  const serviceKey = `unbilled_${bookingResourceDetailId}`;
                  const isSelected = form.selectedUnbilledServices[serviceKey]?.checked || false;

                  // Get service/package names (handle both camelCase and PascalCase)
                  const serviceName = service.spaServiceName_En || service.SpaServiceName_En || null;
                  const packageName = service.packageName_En || service.PackageName_En || null;
                  const packageId = service.package_Id || service.Package_Id;
                  const serviceId = service.service_Id || service.Service_Id;

                  // Determine if this is a package or individual service
                  const isPackage = packageId !== null && packageId !== undefined;

                  // Get amounts (handle both camelCase and PascalCase)
                  const calculatedAmount = service.calculatedAmount || service.CalculatedAmount || 0;
                  const serviceNetAmount = service.serviceNetAmount || service.ServiceNetAmount || 0;
                  const packageAmount = service.packageAmount || service.PackageAmount || 0; // Use Amount (before tax) instead of NetAmount
                  const packageNetAmount = service.packageNetAmount || service.PackageNetAmount || 0;

                  // For packages, use PackageAmount (before tax); for services, use calculatedAmount or serviceNetAmount
                  const amount = form.selectedUnbilledServices[serviceKey]?.amount ?? (isPackage ? (packageAmount || calculatedAmount) : (calculatedAmount || serviceNetAmount || 0));

                  // Display name: For packages, show package name; for services, show service name
                  const displayName = isPackage
                    ? (packageName || `Package ${packageId}`)
                    : (serviceName || `Service ${serviceId}`);

                  // Get booking info
                  const bookingId = service.booking_Id || service.Booking_Id;
                  const bookingDate = service.booking_Date || service.Booking_Date;
                  const employeeName = service.employeeName_En || service.EmployeeName_En;
                  const roomName = service.roomName_En || service.RoomName_En;
                  const fromTime = service.from_Time || service.From_Time;
                  const toTime = service.to_Time || service.To_Time;
                  const daysSinceCompletion = service.daysSinceCompletion || service.DaysSinceCompletion;

                  return (
                    <div key={bookingResourceDetailId} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded" style={{ fontSize: '0.875rem', backgroundColor: '#fff' }}>
                      <div className="form-check" style={{ flex: '1' }}>
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          id={`unbilled-service-${bookingResourceDetailId}`}
                          checked={isSelected}
                          onChange={(e) => {
                            setForm(prev => ({
                              ...prev,
                              selectedUnbilledServices: {
                                ...prev.selectedUnbilledServices,
                                [serviceKey]: {
                                  ...prev.selectedUnbilledServices[serviceKey],
                                  checked: e.target.checked,
                                  validationError: ''
                                }
                              }
                            }));
                            if (e.target.checked) {
                              // Set default amount when checked
                              // We consistently use NET amount (after tax) as the default in the modal
                              const defaultAmount = isPackage
                                ? (packageNetAmount || calculatedAmount || 0)
                                : (serviceNetAmount || calculatedAmount || 0);
                              setForm(prev => ({
                                ...prev,
                                selectedUnbilledServices: {
                                  ...prev.selectedUnbilledServices,
                                  [serviceKey]: {
                                    ...prev.selectedUnbilledServices[serviceKey],
                                    checked: true,
                                    amount: defaultAmount
                                  }
                                }
                              }));
                            }

                          }}
                        />
                        <label className="form-check-label" htmlFor={`unbilled-service-${bookingResourceDetailId}`} style={{ cursor: 'pointer' }}>
                          <div>
                            <strong>{displayName}</strong>
                            {isPackage && (
                              <span className="badge bg-primary ms-2" style={{ fontSize: '0.75rem' }}>Package</span>
                            )}
                          </div>
                          <div className="text-muted small mt-1">
                            Booking: #{bookingId} |
                            Date: {bookingDate ? new Date(bookingDate).toLocaleDateString('en-GB') : 'N/A'}
                            {!isPackage && employeeName && ` | Employee: ${employeeName}`}
                            {!isPackage && roomName && ` | Room: ${roomName}`}
                            {!isPackage && fromTime && toTime && (
                              ` | Time: ${new Date(fromTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${new Date(toTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                            )}
                          </div>
                        </label>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="text-muted" style={{ fontSize: '0.875rem', textAlign: 'right' }}>
                          <div>Amount: {(isPackage ? (packageAmount || calculatedAmount || 0) : (serviceNetAmount || calculatedAmount || 0)).toFixed(2)}</div>
                          {daysSinceCompletion !== null && daysSinceCompletion !== undefined && (
                            <div className="small">Completed {daysSinceCompletion} day(s) ago</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 text-muted small" style={{ fontSize: '0.75rem' }}>
                <i className="mdi mdi-information-outline me-1"></i>
                These are completed services from bookings that have not been billed yet.
              </div>
            </div>
          )}

          {/* Show message if no items available */}
          {(!form.customerMemberships || form.customerMemberships.length === 0) &&
            (!form.unbilledServices || form.unbilledServices.length === 0) && (
              <div className="text-center text-muted py-4" style={{ fontSize: '0.875rem' }}>
                No billable items found for this customer.
              </div>
            )}
        </ModalBody>
        <ModalFooter style={{ fontSize: '0.875rem' }}>
          <div className="d-flex justify-content-between w-100">
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              Selected: {Object.values(form.selectedMembershipInstallments).filter(item => item.checked).length} existing installments
              {Object.values(form.newPackageSeriesInstallments).filter(item => item.checked).length > 0 &&
                `, ${Object.values(form.newPackageSeriesInstallments).filter(item => item.checked).length} new installments`
              }
              {Object.values(form.selectedCompletePayments).filter(item => item.checked).length > 0 &&
                `, ${Object.values(form.selectedCompletePayments).filter(item => item.checked).length} complete payment(s)`
              }
              {Object.values(form.selectedUnbilledServices).filter(item => item.checked).length > 0 &&
                `, ${Object.values(form.selectedUnbilledServices).filter(item => item.checked).length} unbilled service(s)`
              }
            </span>
            <div className="d-flex gap-2">
              <Button color="secondary" onClick={closeMembershipModal} style={{ fontSize: '0.875rem' }}>
                Cancel
              </Button>
              <Button
                color="success"
                onClick={addSelectedMembershipInstallments}
                disabled={
                  Object.values(form.selectedMembershipInstallments).filter(item => item.checked && item.amount > 0).length === 0 &&
                  Object.values(form.newPackageSeriesInstallments).filter(item => item.checked && item.amount > 0).length === 0 &&
                  Object.values(form.selectedCompletePayments).filter(item => item.checked && item.amount > 0).length === 0 &&
                  Object.values(form.selectedUnbilledServices).filter(item => item.checked && item.amount > 0).length === 0
                }
                style={{ fontSize: '0.875rem' }}
              >
                Add Selected
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal >

      {/* Quick Add Customer Modal */}
      < QuickAddCustomer
        isOpen={showQuickAddCustomer}
        toggle={() => setShowQuickAddCustomer(false)
        }
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Customer Profile Modal */}
      < CustomerProfileModal
        isOpen={showCustomerProfile}
        toggle={() => setShowCustomerProfile(false)}
        customerId={selectedCustomerOption?.value || null}
        customer={selectedCustomerOption ? { customer_Id: selectedCustomerOption.value } : null}
      />

      {/* Advanced Customer Search Modal */}
      <CustomerAdvancedSearchModal
        isOpen={showAdvancedSearchModal}
        toggle={() => setShowAdvancedSearchModal(false)}
        userBranchId={session.branchId}
        onCustomerSelected={(customer) => {
          // Set the selected customer in the form and dropdown
          setForm(prev => ({
            ...prev,
            Invoice_CustomerId: customer.customer_Id,
            Invoice_SourceId: customer.customer_LastSourceId || null
          }));
          setSelectedCustomerOption({
            value: customer.customer_Id,
            label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
            customer: customer
          });
          // Reset membership status and check for membership
          setCustomerHasMembership(false);
          setCustomerHasBillableItems(false);
          if (customer.customer_Id) {
            openMembershipModal(customer.customer_Id);
          }
        }}
      />

      {/* Stock Selection Modal */}
      <StockSelectionModal
        key={`stock-modal-${form.currentStockProduct?.id || 'none'}`}
        isOpen={form.showStockModal}
        toggle={() => setForm(prev => ({ ...prev, showStockModal: false, preloadedStockItems: null }))}
        productId={form.currentStockProduct?.id}
        productName={form.currentStockProduct?.name}
        requestedQuantity={form.currentStockProduct?.quantity}
        onStockSelected={handleStockSelected}
        selectedStockItems={form.selectedStockData[form.currentStockProduct?.id] || []}
        branchId={form.Invoice_BranchId}
        businessId={form.Invoice_BusinessId}
        preloadedStockItems={form.preloadedStockItems || null}
        temporaryFormUniqueId={temporaryFormUniqueId}
        flag={isReturnMode ? 8 : 7}
        createdBy={getUserSession().userId}
      />

      {/* Stock Details Modal */}
      <StockDetailsModal
        isOpen={form.showStockDetailsModal}
        toggle={() => {
          // Restore SweetAlert z-index when closing stock modal
          const sweetAlertContainers = document.querySelectorAll('.swal2-container');
          sweetAlertContainers.forEach(container => {
            container.style.zIndex = '1060';
          });
          setForm(prev => ({ ...prev, showStockDetailsModal: false }));
        }}
        productName={form.currentStockDetailsProduct}
        stockDetails={form.currentStockDetails}
      />

      {/* Loyalty Redeem Modal */}
      <Modal isOpen={showLoyaltyRedeemModal} toggle={() => setShowLoyaltyRedeemModal(false)} centered>
        <ModalHeader toggle={() => setShowLoyaltyRedeemModal(false)}>Redeem Loyalty Points</ModalHeader>
        <ModalBody>
          <div className="text-center mb-4">
            <h6 className="text-muted mb-1">Available Loyalty Balance</h6>
            <h3 className="text-success">{customerBranchLoyaltyBalance} Points</h3>
            {activeLoyaltyConfig && (
              <p className="text-muted small">
                Current Rate: {activeLoyaltyConfig.redeemPoints} pts = {currencyCode} {activeLoyaltyConfig.redeemAmount.toFixed(2)}
              </p>
            )}
          </div>

          <FormGroup>
            <Label for="pointsToRedeem">Points to Redeem</Label>
            <Input
              id="pointsToRedeem"
              type="number"
              value={form.PointsRedeemed || ""}
              placeholder="Enter points to redeem"
              onChange={(e) => {
                const pts = parseInt(e.target.value) || 0;
                if (pts < 0) return;

                // Allow entering more but show warning in logic or just cap it
                const cappedPts = Math.min(pts, customerBranchLoyaltyBalance);

                let amt = 0;
                if (activeLoyaltyConfig) {
                  amt = (cappedPts / activeLoyaltyConfig.redeemPoints) * activeLoyaltyConfig.redeemAmount;
                }

                setForm(prev => ({
                  ...prev,
                  PointsRedeemed: cappedPts,
                  PointsAmount: amt,
                  LoyaltyAmount: amt
                }));
              }}
            />
            <p className="text-muted small mt-1">
              You can redeem up to {customerBranchLoyaltyBalance} points.
            </p>
          </FormGroup>

          {activeLoyaltyConfig && form.PointsAmount > 0 && (
            <div className="alert alert-info border-0 mb-0">
              <div className="d-flex justify-content-between">
                <span>Equivalent Currency Amount:</span>
                <span className="fw-bold">{currencyCode} {form.PointsAmount.toFixed(2)}</span>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => {
            // Adjust payment amounts after loyalty redemption:
            // Deduct from CashAmount if it has a value, otherwise deduct from CashlessAmount
            setForm(prev => {
              const net = parseFloat(prev.Invoice_NetAmount) || 0;
              const tip = parseFloat(prev.TipAmount) || 0;
              const loyalty = prev.LoyaltyAmount || 0;
              const giftCard = prev.GiftCardAmount || 0;
              const cashAmount = parseFloat(prev.CashAmount) || 0;
              const cashlessAmount = parseFloat(prev.CashlessAmount) || 0;

              if (cashAmount > 0) {
                // Deduct loyalty from CashAmount
                const newCash = Math.max(0, Math.round((cashAmount - loyalty) * 100) / 100);
                return {
                  ...prev,
                  CashAmount: newCash
                };
              } else if (cashlessAmount > 0) {
                // No cash — deduct loyalty from CashlessAmount
                const newCashless = Math.max(0, Math.round((cashlessAmount - loyalty) * 100) / 100);
                return {
                  ...prev,
                  CashlessAmount: newCashless === 0 ? null : newCashless
                };
              } else {
                // Fallback: derive from net + tip - giftCard
                const newCash = Math.max(0, Math.round((net + tip - loyalty - giftCard) * 100) / 100);
                return {
                  ...prev,
                  CashAmount: newCash
                };
              }
            });
            setShowLoyaltyRedeemModal(false);
          }}>
            Confirm Redemption
          </Button>
          <Button color="secondary" onClick={() => setShowLoyaltyRedeemModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={showCustomerProfile}
        toggle={() => setShowCustomerProfile(false)}
        customerId={form.Invoice_CustomerId}
        customerName={selectedCustomerOption?.label}
      />

      {/* Invoice Print Modal */}
      {
        invoicePrintData && (
          <InvoicePrintModal
            isOpen={showInvoicePrintModal}
            toggle={handlePrintModalClose}
            invoiceData={invoicePrintData}
          />
        )
      }
    </div >
  );
}